import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './App.css';

// Components
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Separator } from './components/ui/separator';
import { toast, Toaster } from 'sonner';
import { CoverageSelector, computeRequiredCredits } from './components/CoverageSelector';
import { CoverageRulesSection } from './components/CoverageRulesSection';
import { RollUsageSection } from './components/RollUsageSection';
import { AppShell } from './components/AppShell';
import { ExecutiveDashboard } from './components/ExecutiveDashboard';
import { SalesDashboard } from './components/SalesDashboard';
import { MyClients } from './components/MyClients';
import { OwnershipHistory, SalesRepStatsStrip } from './components/OwnershipHistory';
import { OrderQueue } from './components/OrderQueue';
import { OrderBuilder } from './components/OrderBuilder';
import ActivatePage from './components/ActivatePage';
import { PortalAccessCard, SetupChecklist, PricingPanel, CustomerActivity } from './components/CommercialPanels';
import { ProductsInventory } from './components/ProductsInventory';
import { ProductCatalog } from './components/ProductCatalog';
import { DealerDashboard, DealerNewOrder, DealerOrders } from './components/DealerPortal';
import { WarrantyCenter } from './components/WarrantyCenter';
import { AdminWarrantyCenter } from './components/AdminWarrantyCenter';
import { SerialTraceability } from './components/SerialTraceability';
import { ProductionBarcodes } from './components/ProductionBarcodes';
import { SoftwareCenter } from './components/SoftwareCenter';
import { EmailCenter } from './components/EmailCenter';
import { PricingWorkspace } from './components/PricingWorkspace';

// Icons
import { 
  ShoppingCart, 
  Package, 
  Users, 
  User,
  FileText, 
  LogOut, 
  Plus, 
  Eye,
  Download,
  Settings,
  Shield,
  Star,
  Building,
  Phone,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  Hash,
  X,
  Edit,
  Trash,
  Globe,
  Check,
  Send,
  RotateCcw,
  Barcode,
  ScanLine,
  Factory,
  Boxes,
  Clock
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Language Context and Translations
const LanguageContext = createContext();

const translations = {
  en: {
    // Login
    login: 'Login',
    username: 'Username',
    password: 'Password',
    loginButton: 'Sign In',
    loggingIn: 'Signing in...',
    distributionSystem: 'Distribution Management System',
    enterUsername: 'Enter your username',
    enterPassword: 'Enter your password',
    welcomeMessage: 'Welcome to TC Protection Film!',
    
    // Navigation & Tabs
    dashboard: 'Dashboard',
    newOrder: 'New Order',
    products: 'Products',
    software: 'Software TC',
    swTabPending: 'Pending',
    swTabActive: 'Active',
    swTabExpiring: 'Coming for Renewal',
    swTabExpired: 'Expired',
    swTabRejected: 'Rejected',
    swTabAll: 'All',
    swNoAccounts: 'No accounts in this category',
    warranty: 'Warranties',
    users: 'Users',
    pricing: 'Pricing',
    ordersManagement: 'Orders Management',
    serialSearch: 'Serial Search',
    warrantyManagement: 'Warranty Management',
    
    // Dashboard - Admin
    adminPanel: 'TC Administration Panel',
    executiveDashboard: 'Executive Dashboard',
    pendingLabel: 'Pending',
    processingLabel: 'Processing',
    onTheWay: 'On the Way',
    delivered: 'Delivered',
    totalRevenue: 'Total Revenue',
    revenueFromOrders: 'Revenue generated from orders',
    totalOrdersLabel: 'Total Orders',
    sinceStart: 'Since the start of operations',
    recentOrdersAdmin: 'Recent Orders',
    last10Orders: 'Last 10 system orders',
    noRecentOrdersAdmin: 'No recent orders',
    clientSearch: 'Client Search',
    searchClientsDistributors: 'Search clients and distributors',
    searchByNameEmailCompany: 'Search by name, email, company...',
    searching: 'Searching...',
    clear: 'Clear',
    useSearchToFindClients: 'Use search to find clients',
    noClientsFound: 'No clients found',
    elite: 'Elite',
    center: 'Center',
    viewProfile: 'View Profile',
    quickActions: 'Quick Actions',
    mainAdminActions: 'Main administrative actions',
    manageOrders: 'Manage Orders',
    viewProducts: 'View Products',
    manageUsersAction: 'Manage Users',
    managePrices: 'Manage Prices',
    clientProfile: 'Client Profile',
    detailedClientInfo: 'Detailed client information and history',
    personalInfo: 'Personal Information',
    fullName: 'Full Name',
    commercialInfo: 'Commercial Information',
    company: 'Company',
    clientType: 'Client Type',
    address: 'Address',
    statistics: 'Statistics',
    totalOrdersClient: 'Total Orders',
    totalSpentClient: 'Total Spent',
    pendingOrdersClient: 'Pending Orders',
    clientSince: 'Client Since',
    recentOrdersClient: 'Recent Orders',
    allOrders: 'All Orders',
    noOrdersYet: 'No orders yet',
    items: 'items',
    close: 'Close',
    
    // Dashboard - Client
    welcome: 'Welcome',
    searchOrders: 'Search Orders',
    searchByOrderNumberNameCompany: 'Search by order number, name or company...',
    searchResults: 'Search results',
    noOrdersFoundCriteria: 'No orders found with those criteria.',
    totalOrdersCard: 'Total Orders',
    sinceRegistration: 'Since you registered',
    totalInvested: 'Total Invested',
    inTCProducts: 'In TC Protection Film products',
    pendingOrdersTitle: 'Pending Orders',
    noPendingOrders: 'No pending orders',
    allOrdersCompleted: 'All your orders are completed',
    completedOrdersTitle: 'Completed Orders',
    noCompletedOrders: 'No completed orders',
    ordersWillAppearHere: 'Your orders will appear here when delivered',
    invoice: 'Invoice',
    loadingDashboard: 'Loading dashboard...',
    errorLoadingDashboard: 'Error loading dashboard data',
    enterAtLeast2Chars: 'Enter at least 2 characters to search',
    errorSearchingOrders: 'Error searching orders',
    more: 'more',
    shippingAddress: 'Shipping Address',
    
    // Orders
    order: 'Order',
    orders: 'Orders',
    orderDate: 'Order Date',
    status: 'Status',
    total: 'Total',
    items: 'Items',
    pending: 'Pending',
    processing: 'Processing',
    shipped: 'Shipped',
    completed: 'Completed',
    cancelled: 'Cancelled',
    downloadInvoice: 'Invoice',
    downloadPacking: 'Packing',
    invoiceDownloaded: 'Invoice downloaded successfully',
    packingDownloaded: 'Packing slip downloaded successfully',
    errorDownloadingInvoice: 'Error downloading invoice',
    errorDownloadingPacking: 'Error downloading packing slip',
    
    // Payment Status
    paymentStatus: 'Payment Status',
    paymentPending: 'Pending Payment',
    paymentPaid: 'Paid',
    markAsPaid: 'Mark as Paid',
    markAsPending: 'Mark as Pending',
    paymentStatusUpdated: 'Payment status updated',
    allPayments: 'All Payments',
    paidOrders: 'Paid Orders',
    unpaidOrders: 'Unpaid Orders',
    
    // Discount Management
    applyDiscount: 'Apply Discount',
    editDiscount: 'Edit Discount',
    discountPercentage: 'Discount Percentage',
    enterDiscountPercentage: 'Enter discount percentage (0-100)',
    discountUpdated: 'Discount updated successfully',
    currentDiscount: 'Current Discount',
    newTotal: 'New Total',
    saveDiscount: 'Save Discount',
    yourDiscountApplied: 'Your discount is automatically applied',
    
    // Products
    product: 'Product',
    price: 'Price',
    quantity: 'Quantity',
    stockAvailable: 'Stock Available',
    units: 'units',
    ppfProducts: 'Paint Protection Film (PPF)',
    windowTint: 'Window Tint',
    addToOrder: 'Add to Order',
    noProducts: 'No products available',
    productCatalog: 'Product Catalog',
    managePPFWindowTint: 'Manage your PPF and Window Tint inventory',
    manageCategories: 'Manage Categories',
    addProduct: 'Add Product',
    addNewProduct: 'Add New Product',
    completeNewProductInfo: 'Complete the new product information for the catalog',
    productName: 'Product Name',
    description: 'Description',
    detailedDescription: 'Detailed product description',
    category: 'Category',
    size: 'Size',
    finish: 'Finish',
    priceUSD: 'Price (USD)',
    stockQuantity: 'Stock Quantity',
    editProduct: 'Edit Product',
    modifyProductInfo: 'Modify product information',
    basePriceUSD: 'Base Price (USD)',
    updateProduct: 'Update Product',
    startAddingProducts: 'Start adding products to your catalog',
    addFirstProduct: 'Add First Product',
    productsLabel: 'products',
    discount: 'discount',
    loadingProducts: 'Loading products...',
    errorLoadingProducts: 'Error loading products',
    productAddedSuccess: 'Product added successfully',
    productUpdatedSuccess: 'Product updated successfully',
    productDeletedSuccess: 'Product deleted successfully',
    confirmDeleteProduct: 'Are you sure you want to delete this product?',
    cannotDeleteProductOrders: 'Cannot delete product. It is referenced in orders.',
    errorDeletingProduct: 'Error deleting product',
    deleteProduct: 'Delete Product',
    
    // Custom Items
    customItems: 'Custom Items',
    customItemsDescription: 'Add special items not in catalog. These items will NOT have tier discount applied.',
    itemName: 'Item Name',
    customItemNamePlaceholder: 'e.g., Special PPF Kit',
    customItemNotesPlaceholder: 'e.g., Special order for customer',
    noDiscountApplied: 'No discount applied',
    catalogProducts: 'Catalog Products',
    tierDiscount: 'Tier Discount',
    add: 'Add',
    optional: 'Optional',
    
    // Categories
    productCategoryManagement: 'Product Category Management',
    addEditDeleteCategories: 'Add, edit or delete product categories',
    addNewCategory: 'Add New Category',
    categoryCode: 'Category Code',
    displayName: 'Display Name',
    createCategory: 'Create Category',
    existingCategories: 'Existing Categories',
    code: 'Code',
    categoryCreatedSuccess: 'Category created successfully',
    categoryDeletedSuccess: 'Category deleted successfully',
    confirmDeleteCategory: 'Are you sure you want to delete category',
    
    // New Order
    createNewOrder: 'Create New Order',
    createOrderForClient: 'Create Order for Client',
    selectClientAndProducts: 'Select a client and add products to their order',
    selectClient: 'Select Client',
    selectClientOption: 'Select a client...',
    selectClientRequired: 'Please select a client to create the order',
    noClientsAvailable: 'No clients available. Create client accounts first in the Users section.',
    addOrder: 'Add Order',
    selectProductsCompleteDetails: 'Select products and complete the details',
    productSelection: 'Product Selection',
    selectProductOption: 'Select product',
    stock: 'Stock',
    subtotalLabel: 'Subtotal',
    remove: 'Remove',
    
    // Stock Return Dialog
    returnToStock: 'Return to Stock?',
    returnToStockDescription: 'Do you want to return this item back to inventory?',
    yesReturnToStock: 'Yes, Return to Stock',
    noKeepRemoved: 'No, Remove Only',
    
    addProductButton: 'Add Product',
    shippingInfo: 'Shipping Information',
    savedAddresses: 'Saved Addresses',
    selectSavedAddress: 'Select saved address...',
    addNewAddress: 'Add New Address',
    fullShippingAddress: 'Full Shipping Address',
    includeNameAddressCityStateZip: 'Include name, address, city, state, zip code',
    specialInstructions: 'Special Instructions',
    anySpecialInstructions: 'Any special instructions for handling or delivery (optional)',
    orderSummary: 'Order Summary',
    totalLabel: 'Total:',
    createOrderButton: 'Create Order',
    addNewAddressTitle: 'Add New Address',
    saveAddressForFuture: 'Save this address for future use in your orders',
    addressName: 'Address Name',
    addressNamePlaceholder: 'Ex: Main Shop, Warehouse, Home',
    fullAddressLabel: 'Full Address',
    city: 'City',
    state: 'State',
    zipCode: 'Zip Code',
    setAsDefault: 'Set as default address',
    saveAddress: 'Save Address',
    orderCreatedSuccess: 'Order created successfully!',
    addressSavedSuccess: 'Address saved successfully',
    errorCreatingOrder: 'Error creating order',
    errorSavingAddress: 'Error saving address',
    errorLoadingProducts: 'Error loading products',
    loadingProductsOrder: 'Loading products...',
    
    // Edit Order Items
    editOrderItems: 'Edit Order Items',
    editItemsDescription: 'Modify quantities, add or remove products from this order',
    currentItems: 'Current Items',
    addMoreProducts: 'Add More Products',
    selectProductToAdd: 'Select product to add...',
    addToOrderBtn: 'Add',
    newTotal: 'New Total',
    originalTotal: 'Original Total',
    saveChanges: 'Save Changes',
    itemsUpdatedSuccess: 'Order items updated successfully',
    minOneItem: 'Order must have at least one item',
    perUnit: 'per unit',
    allProductsAdded: 'All available products have been added to the order',
    
    // Orders Management (Admin)
    ordersManagementTitle: 'Orders Management',
    manageAllOrders: 'Manage all system orders',
    totalLabel2: 'Total',
    searchOrdersAdmin: 'Search Orders',
    searchByOrderClientCompany: 'Search by order number, client or company...',
    manage: 'Manage',
    newOrders: 'New',
    inProcess: 'In Process',
    completedTab: 'Completed',
    noOrdersInCategory: 'No orders in this category',
    orderNumber: 'Order #',
    statusLabel: 'Status',
    editOrder: 'Edit',
    editItems: 'Items',
    deleteOrder: 'Delete',
    manageOrderNumber: 'Manage Order #',
    updateStatusAddSerials: 'Update status and add serial numbers',
    orderInfo: 'Order Information',
    client: 'Client',
    companyLabel: 'Company',
    created: 'Created',
    orderStatus: 'Order Status',
    trackingNumber: 'Tracking Number',
    optionalTracking: 'Optional',
    serialNumbers: 'Serial Numbers',
    addSerial: 'Add Serial',
    serialNumberLabel: 'Serial Number',
    selectProductSerial: 'Select product...',
    notes: 'Notes',
    adminNotes: 'Administrative Notes',
    internalOrderNotes: 'Internal notes about the order...',
    updateOrder: 'Update Order',
    orderStatusUpdated: 'Order status updated successfully',
    orderDeleted: 'Order deleted successfully',
    confirmDeleteOrder: 'Are you sure you want to delete order #',
    actionCannotBeUndone: '? This action cannot be undone.',
    errorUpdatingOrder: 'Error updating order',
    errorDeletingOrder: 'Error deleting order',
    loadingOrders: 'Loading orders...',
    
    // Users Management
    userManagement: 'User Management',
    manageClientDistributorAccounts: 'Manage client and distributor accounts',
    createUserButton: 'Create User',
    createNewUser: 'Create New User',
    completeInfoCreateAccount: 'Complete the information to create a new user account',
    usernameLabel: 'Username',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    fullNameLabel: 'Full Name',
    companyNameLabel: 'Company Name',
    roleLabel: 'Role',
    installationCenter: 'Installation Center',
    elitePartner: 'Elite TC Partner',
    tcAdmin: 'TC Administrator',
    salesRepRole: 'Sales Representative',
    customPricingBtn: 'Custom Pricing',
    softwareRenewalsTitle: 'Software Subscriptions',
    softwareRenewalsSubtitle: 'Renewal status of all software accounts — flag expired and expiring soon.',
    manageSoftwareAccounts: 'Manage Accounts',
    emailCenterTab: 'Email Center',
    productionBarcodesTab: 'Production Barcodes',
    quickComposerTitle: 'Compose a Message',
    quickComposerSubtitle: 'Type your subject and message, choose who to send it to, and hit send. That\'s it.',
    quickSendTo: 'Send to',
    quickSubject: 'Subject',
    quickSubjectPlaceholder: 'e.g. New product launch this month',
    quickMessage: 'Message',
    quickMessagePlaceholder: 'Write your message here...',
    quickMessageHint: 'Plain text is fine — we\'ll wrap it in your branded TC template automatically. Leave a blank line between paragraphs.',
    quickSendButton: 'Send Email',
    quickSending: 'Sending...',
    quickRecipientCount: 'This will be sent to {n} recipient(s).',
    quickCustomEmailsPlaceholder: 'Enter emails separated by commas',
    quickSubjectRequired: 'Please enter a subject',
    quickMessageRequired: 'Please type your message',
    quickEmailsRequired: 'Please enter at least one email address',
    quickSentOk: 'Sent to {n} recipient(s) ✓',
    quickSentPartial: 'Sent {sent}, {failed} failed',
    quickSendFailed: 'Sending failed',
    showAdvancedTemplates: '⚙ Advanced: manage email templates',
    hideAdvancedTemplates: '▲ Hide advanced templates',
    audAllDealers: 'All Dealers (Installation Centers + Elite Partners)',
    audInstallationCenters: 'Installation Centers only',
    audElitePartners: 'Elite Partners only',
    audSalesReps: 'Sales Representatives',
    audEveryone: 'Everyone (Dealers + Partners + Sales Reps)',
    audAdmins: 'Admins',
    audCustom: 'Specific email addresses',
    emailCenterTitle: 'Email Center',
    emailCenterSubtitle: 'Manage branded email templates, send marketing and system announcements to your partners.',
    newTemplateBtn: 'New Template',
    viewEmailLog: 'Email Log',
    subjectLabel: 'Subject',
    nameLabel: 'Name',
    categoryLabel: 'Category',
    descriptionLabel: 'Description',
    htmlBodyLabel: 'HTML Body',
    templateVarsHelper: 'Use {{variable_name}} placeholders. First-name, full-name, recipient-email are auto-filled per recipient.',
    editBtn: 'Edit',
    previewBtn: 'Preview',
    broadcastBtn: 'Broadcast',
    resetBtn: 'Reset',
    deleteBtn: 'Delete',
    saveBtn: 'Save',
    createBtn: 'Create',
    editTemplateTitle: 'Edit Template',
    editTemplateSubtitle: 'Edit subject and HTML body. All emails auto-wrap in the TC gold + black shell.',
    createTemplateTitle: 'Create Custom Template',
    templateIdLabel: 'Template ID (slug)',
    previewTitle: 'Email Preview',
    previewVarsLabel: 'Variables',
    rerenderBtn: 'Re-render',
    sendTestToLabel: 'Send test to',
    sendTestBtn: 'Send Test',
    testEmailRequired: 'Enter a test recipient email',
    testEmailSent: 'Test email sent to {email}',
    testEmailFailed: 'Test email failed to send',
    broadcastTitle: 'Send Broadcast',
    broadcastSubtitle: 'Send this template to a filtered audience. Every recipient gets their own copy with first_name auto-filled.',
    audienceLabel: 'Audience',
    audAllAdmins: 'All Admins',
    audAllDealers: 'All Dealers (Installation + Elite)',
    audInstallationCenters: 'Installation Centers only',
    audElitePartners: 'Elite Partners only',
    audSalesReps: 'Sales Reps only',
    audAllPartners: 'All Partners (Dealers + Sales Reps)',
    audCustom: 'Custom emails…',
    customEmailsLabel: 'Custom recipient emails (comma-separated)',
    customEmailsHelper: 'Enter emails below (comma-separated)',
    recipients: 'recipients',
    subjectOverrideLabel: 'Subject Override (leave blank to use template subject)',
    sendBroadcastBtn: 'Send',
    sending: 'Sending...',
    broadcastSuccess: 'Broadcast sent to {n} recipients',
    broadcastPartial: 'Partial: {sent} sent, {failed} failed',
    broadcastFailed: 'Broadcast failed',
    templateSavedSuccess: 'Template saved',
    templateCreatedSuccess: 'Template created',
    templateDeletedSuccess: 'Template deleted',
    templateResetSuccess: 'Template reset to defaults',
    errorSavingTemplate: 'Failed to save template',
    errorLoadingTemplates: 'Failed to load templates',
    resetTemplateConfirm: 'Reset this system template back to its factory defaults? Your changes will be lost.',
    deleteTemplateConfirm: 'Delete this template? This action cannot be undone.',
    emailLogTitle: 'Recent Email Activity',
    noEmailLogYet: 'No emails have been sent yet.',
    sendRemindersNow: 'Send Reminders Now',
    sendingReminders: 'Sending...',
    remindersLastSent: 'Last email sent',
    remindersSentSuccess: 'Digest sent to {n} admin(s)',
    remindersPartial: 'Partial send: {sent} succeeded, {failed} failed',
    remindersSkipped: 'Reminder already sent today',
    remindersFailed: 'Failed to send renewal reminders',
    renewalExpired: 'Expired',
    renewalExpiringSoon: 'Expiring Soon',
    renewalActive: 'Active',
    renewalTotal: 'Total',
    renewalExpiredHeader: '🚨 Expired Accounts',
    renewalExpiringHeader: '⏰ Expiring in 30 Days',
    renewalAllHealthy: 'All software accounts are healthy — nothing expires in the next 30 days.',
    daysAgo: 'days ago',
    daysLeft: 'days left',
    createSoftwareAccountBtn: 'Create Software Account',
    createSoftwareAccountTitle: 'Create Software Account',
    createSoftwareAccountSubtitle: 'Create a software account directly for a client, bypassing the request/approval workflow.',
    businessNameLabel: 'Business Name',
    contactPersonLabel: 'Contact Person',
    planTypeLabel: 'Plan Type',
    softwareUsernameLabel: 'Software Username',
    softwarePasswordLabel: 'Software Password',
    subscriptionStartLabel: 'Subscription Start',
    subscriptionEndLabel: 'Subscription End',
    subscriptionEndRequired: 'Subscription end date is required',
    createAccountBtn: 'Create Account',
    softwareAccountCreatedSuccess: 'Software account created successfully',
    errorCreatingSoftwareAccount: 'Error creating software account',
    shopTypeLabel: 'Shop Type',
    adminNotesLabel: 'Admin Notes',
    customPricingTitle: 'Custom Pricing',
    customPricingSubtitle: 'Override the tier discount with a custom % or set specific per-product prices for this account.',
    customDiscountLabel: 'Custom Discount % (overrides tier)',
    customDiscountHelper: 'When set, this replaces the standard tier discount for all products. Leave empty to fall back to tier discount.',
    customPerProductLabel: 'Per-Product Custom Prices (optional)',
    customPerProductHelper: 'Set a fixed price for specific products. Custom prices always win over percentage discount.',
    listPriceLabel: 'List Price',
    customPriceLabel: 'Custom Price',
    leaveEmptyForTier: '— use tier —',
    willUseTierDiscount: 'Will use tier discount',
    invalidDiscountRange: 'Discount must be between 0 and 100',
    invalidCustomPrice: 'Custom prices must be non-negative numbers',
    pricingUpdatedSuccess: 'Custom pricing updated successfully',
    pricingClearedSuccess: 'Custom pricing cleared. Account will use tier discount.',
    errorUpdatingPricing: 'Error updating custom pricing',
    errorLoadingPricing: 'Error loading pricing data',
    clearAllPricing: 'Clear All Custom Pricing',
    clearAllPricingConfirm: 'This will remove the custom discount % AND all per-product prices for this account. Continue?',
    savePricingBtn: 'Save Pricing',
    salesRepDashboardTitle: 'Sales Rep Dashboard',
    salesRepDashboardSubtitle: 'Manage your clients and orders — grow your book of business.',
    myClients: 'My Clients',
    myOrders: 'My Orders',
    manageMyClients: 'Manage My Clients',
    assignSalesRepLabel: 'Assign Sales Rep (Optional)',
    noSalesRepAssigned: '— No sales rep assigned —',
    noSalesRepClientsYet: 'You have not created any clients yet. Add one from the "My Clients" tab first.',
    pendingOrdersLabel: 'Pending Orders',
    totalRevenueLabel: 'Total Revenue',
    recentOrders: 'Recent Orders',
    phoneLabel: 'Phone',
    addressLabel: 'Address',
    userList: 'User List',
    registeredUsers: 'Users registered in the system',
    user: 'User',
    activeStatus: 'Active',
    inactiveStatus: 'Inactive',
    deactivate: 'Deactivate',
    activate: 'Activate',
    noUsersRegistered: 'No users registered',
    startCreatingAccounts: 'Start creating accounts for your clients',
    createFirstUser: 'Create First User',
    userCreatedSuccess: 'User created successfully',
    userDeletedSuccess: 'User deleted successfully',
    userStatusUpdated: 'User status updated',
    confirmDeleteUser: 'Are you sure you want to delete user',
    errorCreatingUser: 'Error creating user',
    errorDeletingUser: 'Error deleting user',
    errorUpdatingUser: 'Error updating user',
    userUpdatedSuccess: 'User updated successfully',
    editUser: 'Edit User',
    editUserInfo: 'Edit user account information',
    updateUser: 'Update User',
    newPasswordOptional: 'New Password (leave blank to keep current)',
    loadingUsers: 'Loading users...',
    
    // Warranty - Client
    warrantyManagementClient: 'Warranty Management',
    requestWarrantiesCheckHistory: 'Request warranties and check your registration history',
    requestWarranty: 'Request Warranty',
    myWarranties: 'My Warranties',
    searchWarranties: 'Search Warranties',
    searchInMyWarranties: 'Search in My Warranties',
    searchBySerialClientVINVehicle: 'Search by serial, client, VIN or vehicle...',
    myWarrantyRequests: 'My Warranty Requests',
    warrantyHistoryStatus: 'History and status of your warranty requests',
    noWarrantiesRequested: 'You have no warranties requested',
    requestWarrantiesToProvide: 'Request warranties to provide peace of mind to your clients',
    requestFirstWarranty: 'Request First Warranty',
    requestWarrantyTitle: 'Request Warranty',
    completeInstallationInfo: 'Complete the installation information to request warranty coverage',
    serialNumberRequired: 'Warranty Number *',
    verifyingSerial: 'Verifying serial number...',
    validSerialNumber: 'Valid serial number',
    productLabel: 'Product',
    orderLabel: 'Order',
    finalClientName: 'Final Client Name *',
    vehicleOwnerName: 'Full name of vehicle owner',
    vehicleMakeRequired: 'Vehicle Make *',
    modelRequired: 'Model *',
    yearRequired: 'Year *',
    vinRequired: 'VIN Number *',
    vinCharacters: '17 VIN characters',
    vehicleMileageRequired: 'Vehicle Mileage *',
    vehicleMileagePlaceholder: 'Current odometer reading',
    mileageLabel: 'Mileage',
    installationDateRequired: 'Installation Date *',
    installationAreaRequired: 'Installation Area *',
    selectArea: 'Select area...',
    additionalNotesOptional: 'Additional Notes',
    additionalInstallationInfo: 'Additional information about the installation...',
    sendRequest: 'Send Request',
    mustEnterValidSerial: 'You must enter a valid serial number',
    warrantyRequestSentSuccess: 'Warranty request sent successfully. It will be reviewed by our team.',
    errorSendingWarrantyRequest: 'Error sending warranty request',
    loadingWarranties: 'Loading warranties...',
    errorLoadingWarranties: 'Error loading warranties',
    
    // Warranty Card
    clientLabel: 'Client',
    vinLabel: 'VIN',
    serialLabel: 'Warranty No.',
    installedLabel: 'Installed',
    areaLabel: 'Area',
    requestedLabel: 'Requested',
    notesLabel: 'Notes',
    adminCommentsLabel: 'Admin comments',
    warrantyValidUntil: 'Warranty valid until',
    underReview: 'Under Review',
    downloadCertificate: 'Download Certificate',
    certificateDownloaded: 'Certificate downloaded successfully',
    errorDownloadingCertificate: 'Error downloading certificate',
    rejectionReason: 'Rejection Reason',
    enterSerialFromProduct: 'Enter the warranty number from your certificate',
    downloading: 'Downloading...',
    
    // Warranty Admin
    warrantyManagementAdmin: 'Warranty Management',
    manageDistributorWarrantyRequests: 'Manage distributor warranty requests',
    pendingTab: 'Pending',
    approvedTab: 'Approved',
    rejectedTab: 'Rejected',
    allTab: 'All',
    searchWarrantiesAdmin: 'Search Warranties',
    searchBySerialVINClientVehicleDistributor: 'Search by serial, VIN, client, vehicle or distributor...',
    noPendingWarranties: 'No pending warranties',
    allRequestsProcessed: 'All requests have been processed',
    noApprovedWarranties: 'No approved warranties',
    noRejectedWarranties: 'No rejected warranties',
    noWarrantiesWithStatus: 'No warranties found with this status',
    distributorLabel: 'Distributor',
    installationLabel: 'Installation',
    applicantNotes: 'Applicant notes',
    approveWarranty: 'Approve Warranty',
    warrantyActivatedClientNotified: 'The warranty will be activated and the client will be notified.',
    rejectWarranty: 'Reject Warranty',
    warrantyRejectedProvideReason: 'The warranty will be rejected. Please provide a reason.',
    warrantySummary: 'Warranty Summary',
    commentsOptional: 'Comments (Optional)',
    addCommentsOrInstructions: 'Add comments or additional instructions...',
    rejectionReasonRequired: 'Rejection Reason *',
    explainRejectionReason: 'Explain the reason for rejecting this warranty...',
    warrantyValidityNote: 'Note: The warranty will be valid for 5 years from today.',
    warrantyDurationMonthsLabel: 'Warranty Duration (months) *',
    warrantyDurationHelper: 'Set how long this warranty will remain valid from the approval date. Minimum 5 months.',
    monthsWord: 'months',
    invalidWarrantyDuration: 'Please enter a valid duration of at least 5 months.',
    confirmApprovalBtn: 'Confirm Approval',
    confirmRejectionBtn: 'Confirm Rejection',
    processingText: 'Processing...',
    editWarrantyTitle: 'Edit Warranty',
    modifyWarrantyDetails: 'Modify warranty details',
    statusField: 'Status',
    clientNameField: 'Client Name',
    vinField: 'VIN',
    makeField: 'Make',
    modelField: 'Model',
    yearField: 'Year',
    installationDateField: 'Installation Date',
    installationAreaField: 'Installation Area',
    warrantyStart: 'Warranty Start',
    warrantyEnd: 'Warranty End',
    clientNotesField: 'Client Notes',
    adminNotesField: 'Admin Notes',
    saving: 'Saving...',
    warrantyApprovedSuccess: 'Warranty approved successfully',
    warrantyRejectedNotification: 'Warranty rejected',
    warrantyUpdatedSuccess: 'Warranty updated successfully',
    errorProcessingRequest: 'Error processing the request',
    errorUpdatingWarranty: 'Error updating warranty',
    loadingWarrantiesAdmin: 'Loading warranties...',
    confirmDeleteWarranty: 'Are you sure you want to delete this warranty? This action cannot be undone.',
    warrantyDeletedSuccess: 'Warranty deleted successfully',
    errorDeletingWarranty: 'Error deleting warranty',
    confirmDeleteSoftwareRequest: 'Are you sure you want to delete this software access request? This action cannot be undone.',
    softwareRequestDeletedSuccess: 'Software request deleted successfully',
    errorDeletingSoftwareRequest: 'Error deleting software request',
    approveBtn: 'Approve',
    rejectBtn: 'Reject',
    
    // Software - Client
    softwareTCCutPro: 'TC CutPro Software',
    preciseCuttingPatterns: 'Precise cutting patterns for any vehicle',
    mostAdvancedSoftware: 'The most advanced software in the industry with thousands of cutting patterns for PPF and Window Tint. Optimize your time, reduce waste and guarantee perfect fits.',
    patternsCount: '15,000+ Patterns',
    completeDatabase: 'Complete database with patterns for vehicles from 1980 to 2024. Monthly updates included.',
    laserPrecisionTitle: 'Laser Precision',
    laserPrecisionDesc: 'Patterns developed with 3D laser technology for perfect fits. Reduce waste by up to 40%.',
    easyToUseTitle: 'Easy to Use',
    easyToUseDesc: 'Intuitive interface with search by year, make and model. Direct integration with cutting plotters.',
    readyToRevolutionize: 'Ready to revolutionize your business?',
    joinShops: 'Join more than 2,500 shops that already trust TC CutPro Software.',
    requestAccessBtn: 'Request Access',
    shopType: 'Shop Type',
    autoDetailing: 'Auto Detailing',
    tintShop: 'Tint Shop',
    bodyShop: 'Body Shop',
    dealership: 'Dealership',
    mobileService: 'Mobile Service',
    other: 'Other',
    monthlyVolume: 'Monthly Volume (vehicles)',
    vehicles1to10: '1-10 vehicles',
    vehicles11to25: '11-25 vehicles',
    vehicles26to50: '26-50 vehicles',
    vehicles51to100: '51-100 vehicles',
    vehiclesOver100: 'More than 100 vehicles',
    currentSoftware: 'What software do you currently use? (Optional)',
    currentSoftwarePlaceholder: 'Ex: None, AccuCut, other',
    additionalInfo: 'Additional Information',
    sendRequest: 'Send Request',
    yourRequestStatus: 'Your Request Status',
    requestBeingReviewed: 'Your request is being reviewed by our team. We will notify you when it is processed.',
    accessApproved: 'Your access has been approved! Use the following credentials:',
    planLabel: 'Plan',
    validUntilLabel: 'Valid until',
    noLimitDate: 'No limit date',
    userLabel: 'User',
    passwordField: 'Password',
    adminMessage: 'Admin message',
    requestRejected: 'Your request was rejected.',
    reasonLabel: 'Reason',
    requestAccessToCutPro: 'Request Access to TC CutPro',
    completeInfoWeWillContact: 'Complete this information and our team will contact you within 24 hours',
    businessNameField: 'Business Name',
    contactPersonField: 'Contact Person',
    shopTypeField: 'Shop Type',
    selectOption: 'Select...',
    dealershipOption: 'Dealership',
    mobileServiceOption: 'Mobile Service',
    otherOption: 'Other',
    monthlyVolumeField: 'Monthly Volume (vehicles)',
    vehiclesRange1: '1-10 vehicles',
    vehiclesRange2: '11-25 vehicles',
    vehiclesRange3: '26-50 vehicles',
    vehiclesRange4: '51-100 vehicles',
    vehiclesRange5: 'More than 100 vehicles',
    whatSoftwareCurrently: 'What software do you currently use? (Optional)',
    nonePlaceholder: 'Ex: None, AccuCut, other',
    additionalInfoField: 'Additional Information',
    tellUsYourNeeds: 'Tell us about your specific needs',
    sendRequestBtn: 'Send Request',
    requestSentSuccess: 'Request sent successfully! It will be reviewed by our team.',
    errorSendingRequest: 'Error sending request',
    
    // Software - Admin
    softwareManagementAdmin: 'TC CutPro Software Management',
    manageSoftwareAccessRequests: 'Manage software access requests',
    activeTab: 'Active',
    searchSubscriptions: 'Search Subscriptions',
    searchByNameEmailCompanyUser: 'Search by name, email, company or user...',
    results: 'Results',
    noResultsFound: 'No results found.',
    noRequestsCategory: 'No requests',
    approveAccessBtn: 'Approve Access',
    rejectBtn: 'Reject',
    approveAccessTitle: 'Approve Access',
    configureSubscriptionDetails: 'Configure subscription details',
    rejectRequestTitle: 'Reject Request',
    provideRejectionReason: 'Provide a reason for rejection',
    requestSummary: 'Request Summary',
    contactLabel: 'Contact',
    typeLabel: 'Type',
    volumeLabel: 'Volume',
    planField: 'Plan',
    basicPlan: 'Basic - $299/month',
    professionalPlan: 'Professional - $599/month',
    enterprisePlan: 'Enterprise - Custom',
    userRequired: 'User *',
    userPlaceholder: 'cutpro_user',
    passwordRequired: 'Password *',
    softwarePassword: 'Software password',
    subscriptionStart: 'Subscription Start',
    subscriptionEndField: 'Subscription End',
    notesOptional: 'Notes (Optional)',
    additionalNotesPlaceholder: 'Additional notes...',
    rejectReasonRequired: 'Rejection Reason *',
    explainReasonPlaceholder: 'Explain the reason...',
    approveBtn: 'Approve',
    rejectConfirmBtn: 'Reject',
    editSubscription: 'Edit Subscription',
    updateSubscriptionDetails: 'Update subscription details',
    startLabel: 'Start',
    endLabel: 'End',
    accessApprovedSuccess: 'Access approved successfully',
    requestRejectedNotification: 'Request rejected',
    subscriptionUpdatedSuccess: 'Subscription updated successfully',
    errorProcessingRequestSoftware: 'Error processing request',
    errorUpdatingSoftware: 'Error updating',
    loadingSoftware: 'Loading...',
    errorLoadingRequests: 'Error loading requests',
    
    // Serial Search
    serialAndWarrantySearch: 'Serial and Warranty Search',
    trackSerialNumbersManageWarranties: 'Track serial numbers and manage registered warranties',
    serials: 'Serials',
    registeredWarranties: 'Registered Warranties',
    searchSerialNumber: 'Search Serial Number',
    searchSerialWarranties: 'Search',
    enterSerialNumber: 'Enter serial number (ex: TC-PPF-001234)',
    rollNumberRequired: 'Roll Number / Tube Serial *',
    rollNumberShort: 'Roll No.',
    enterRollNumberHint: 'Physical serial printed on the film roll/tube (ex: TKL99R2504P2378)',
    mustEnterRollNumber: 'Please enter the roll number / tube serial',
    searchBySerialVINClientVehicleAdmin: 'Search by serial, VIN, client, vehicle...',
    serialNumberResults: 'Serial number results',
    noRecordWithSerial: 'No record found with that serial number.',
    warrantyResults: 'Warranty results',
    noWarrantiesFoundCriteria: 'No warranties found with those criteria.',
    recentSerialNumbers: 'Recent Serial Numbers',
    last50SerialNumbers: 'Last 50 serial numbers assigned',
    noSerialNumbers: 'No serial numbers',
    serialNumbersAppearHere: 'Serial numbers will appear here when assigned to orders',
    orderColumn: 'Order',
    clientColumn: 'Client',
    statusColumn: 'Status',
    dateColumn: 'Date',
    recentRegisteredWarranties: 'Recent Registered Warranties',
    last50Warranties: 'Last 50 warranties registered by clients',
    noRegisteredWarranties: 'No registered warranties',
    warrantiesAppearHere: 'Warranties will appear here when clients register them',
    vehicleColumn: 'Vehicle',
    serialColumn: 'Serial',
    installationColumn: 'Installation',
    registeredColumn: 'Registered',
    assigned: 'Assigned',
    activeWarranty: 'Active',
    loadingSerialNumbers: 'Loading serial numbers...',
    errorLoadingSerialNumbers: 'Error loading serial numbers',
    
    // Pricing
    pricingManagement: 'Pricing Management',
    configureDiscountsByAccountType: 'Configure discounts by account type',
    newTier: 'New Tier',
    createPricingTier: 'Create Pricing Tier',
    defineNewAccountTypeDiscount: 'Define a new account type with its corresponding discount',
    roleCode: 'Role Code',
    roleCodePlaceholder: 'Ex: premium_dealer',
    displayNamePricing: 'Display Name',
    displayNamePlaceholder: 'Ex: Premium Distributor',
    discountPercentage: 'Discount Percentage (%)',
    createTier: 'Create Tier',
    configuredPricingTiers: 'Configured Pricing Tiers',
    discountsAppliedAutomatically: 'Discounts applied automatically based on account type',
    accountType: 'Account Type',
    discountColumn: 'Discount',
    examplePrice: 'Example (Price $100)',
    actionsColumn: 'Actions',
    noPricingTiersConfigured: 'No pricing tiers configured',
    createPricingTiersForAccounts: 'Create pricing tiers for different account types',
    createFirstTier: 'Create First Tier',
    pricingTierCreatedSuccess: 'Pricing tier created successfully',
    pricingTierUpdatedSuccess: 'Pricing tier updated successfully',
    pricingTierDeletedSuccess: 'Pricing tier deleted successfully',
    confirmDeleteTier: 'Are you sure you want to delete tier',
    usersWillSeeNoDiscount: '? Users with this role will see prices without discount.',
    errorCreatingTier: 'Error creating pricing tier',
    errorUpdatingTier: 'Error updating pricing tier',
    errorDeletingTier: 'Error deleting pricing tier',
    loadingPricingTiers: 'Loading pricing tiers...',
    
    // Common
    search: 'Search',
    filter: 'Filter',
    all: 'All',
    cancel: 'Cancel',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    confirm: 'Confirm',
    loading: 'Loading...',
    noResults: 'No results found',
    success: 'Success',
    error: 'Error',
    required: 'Required',
    optional: 'Optional',
    actions: 'Actions',
    details: 'Details',
    date: 'Date',
    logout: 'Logout',
    minCharactersSearch: 'Enter at least 2 characters to search',
    minCharactersSearch3: 'Enter at least 3 characters to search',
    
    // Installation Areas
    fullFront: 'Full Front',
    partialFront: 'Partial Front',
    fullBody: 'Full Body',
    interior: 'Interior',
    windshield: 'Windshield',
    changeFinish: 'Change Finish',
    fullWrap: 'Full Wrap',
    protectionKit: 'Protection Kit',
    
    // Shop Types
    autoDetailing: 'Auto Detailing',
    tintShop: 'Tint Shop',
    bodyShop: 'Body Shop',
    dealership: 'Dealership',
    mobileService: 'Mobile Service',
    other: 'Other',
    
    // Volume Options
    vehicles: 'vehicles',
    vehiclesPerMonth: 'vehicles/month',
    moreThan: 'More than',
    
    // Roles
    tcAdministrator: 'TC Administrator',
    eliteTCPartner: 'Elite TC Partner',
    installationCenterRole: 'Installation Center',
    admin: 'Admin',
    
    // Misc
    errorLoadingDashboard: 'Error loading dashboard data',
    errorLoadingCategories: 'Error loading categories',
    errorCreatingCategory: 'Error creating category'
  },
  es: {
    // Login
    login: 'Iniciar Sesión',
    username: 'Usuario',
    password: 'Contraseña',
    loginButton: 'Ingresar',
    loggingIn: 'Iniciando sesión...',
    distributionSystem: 'Sistema de Gestión de Distribución',
    enterUsername: 'Ingresa tu usuario',
    enterPassword: 'Ingresa tu contraseña',
    welcomeMessage: '¡Bienvenido a TC Protection Film!',
    
    // Navigation & Tabs
    dashboard: 'Panel',
    newOrder: 'Nueva Orden',
    products: 'Productos',
    software: 'Software TC',
    swTabPending: 'Pendientes',
    swTabActive: 'Activas',
    swTabExpiring: 'Por Vencer',
    swTabExpired: 'Vencidas',
    swTabRejected: 'Rechazadas',
    swTabAll: 'Todas',
    swNoAccounts: 'No hay cuentas en esta categoría',
    warranty: 'Garantías',
    users: 'Usuarios',
    pricing: 'Precios',
    ordersManagement: 'Gestión Órdenes',
    serialSearch: 'Búsqueda Series',
    warrantyManagement: 'Gestión Garantías',
    
    // Dashboard - Admin
    adminPanel: 'Panel de Administración TC',
    executiveDashboard: 'Dashboard Ejecutivo',
    pendingLabel: 'Pendientes',
    processingLabel: 'Procesando',
    onTheWay: 'En Camino',
    delivered: 'Entregadas',
    totalRevenue: 'Ingresos Totales',
    revenueFromOrders: 'Revenue generado por órdenes',
    totalOrdersLabel: 'Órdenes Totales',
    sinceStart: 'Desde el inicio de operaciones',
    recentOrdersAdmin: 'Órdenes Recientes',
    last10Orders: 'Últimas 10 órdenes del sistema',
    noRecentOrdersAdmin: 'No hay órdenes recientes',
    clientSearch: 'Búsqueda de Clientes',
    searchClientsDistributors: 'Buscar clientes y distribuidores',
    searchByNameEmailCompany: 'Buscar por nombre, email, empresa...',
    searching: 'Buscando...',
    clear: 'Limpiar',
    useSearchToFindClients: 'Usa la búsqueda para encontrar clientes',
    noClientsFound: 'No se encontraron clientes',
    elite: 'Elite',
    center: 'Centro',
    viewProfile: 'Ver Perfil',
    quickActions: 'Acciones Rápidas',
    mainAdminActions: 'Gestiones administrativas principales',
    manageOrders: 'Gestionar Órdenes',
    viewProducts: 'Ver Productos',
    manageUsersAction: 'Gestionar Usuarios',
    managePrices: 'Gestionar Precios',
    clientProfile: 'Perfil del Cliente',
    detailedClientInfo: 'Información detallada del cliente y su historial',
    personalInfo: 'Información Personal',
    fullName: 'Nombre Completo',
    commercialInfo: 'Información Comercial',
    company: 'Empresa',
    clientType: 'Tipo de Cliente',
    address: 'Dirección',
    statistics: 'Estadísticas',
    totalOrdersClient: 'Órdenes Totales',
    totalSpentClient: 'Total Gastado',
    pendingOrdersClient: 'Órdenes Pendientes',
    clientSince: 'Cliente Desde',
    recentOrdersClient: 'Órdenes Recientes',
    allOrders: 'Todas las Órdenes',
    noOrdersYet: 'Sin órdenes aún',
    items: 'artículos',
    close: 'Cerrar',
    
    // Dashboard - Client
    welcome: 'Bienvenido',
    searchOrders: 'Buscar Órdenes',
    searchByOrderNumberNameCompany: 'Buscar por número de orden, nombre o empresa...',
    searchResults: 'Resultados de búsqueda',
    noOrdersFoundCriteria: 'No se encontraron órdenes con esos criterios.',
    totalOrdersCard: 'Órdenes Totales',
    sinceRegistration: 'Desde que te registraste',
    totalInvested: 'Total Invertido',
    inTCProducts: 'En productos TC Protection Film',
    pendingOrdersTitle: 'Órdenes Pendientes',
    noPendingOrders: 'No hay órdenes pendientes',
    allOrdersCompleted: 'Todas tus órdenes están completadas',
    completedOrdersTitle: 'Órdenes Completadas',
    noCompletedOrders: 'No hay órdenes completadas',
    ordersWillAppearHere: 'Tus órdenes aparecerán aquí cuando sean entregadas',
    invoice: 'Factura',
    loadingDashboard: 'Cargando dashboard...',
    errorLoadingDashboard: 'Error al cargar datos del dashboard',
    enterAtLeast2Chars: 'Ingresa al menos 2 caracteres para buscar',
    errorSearchingOrders: 'Error al buscar órdenes',
    more: 'más',
    shippingAddress: 'Dirección de Envío',
    
    // Orders
    order: 'Orden',
    orders: 'Órdenes',
    orderDate: 'Fecha de Orden',
    status: 'Estado',
    total: 'Total',
    items: 'Artículos',
    pending: 'Pendiente',
    processing: 'Procesando',
    shipped: 'Enviado',
    completed: 'Completado',
    cancelled: 'Cancelado',
    downloadInvoice: 'Factura',
    downloadPacking: 'Empaque',
    invoiceDownloaded: 'Invoice descargado exitosamente',
    packingDownloaded: 'Packing slip descargado exitosamente',
    errorDownloadingInvoice: 'Error al descargar invoice',
    errorDownloadingPacking: 'Error al descargar packing slip',
    
    // Payment Status
    paymentStatus: 'Estado de Pago',
    paymentPending: 'Pago Pendiente',
    paymentPaid: 'Pagado',
    markAsPaid: 'Marcar como Pagado',
    markAsPending: 'Marcar como Pendiente',
    paymentStatusUpdated: 'Estado de pago actualizado',
    allPayments: 'Todos los Pagos',
    paidOrders: 'Órdenes Pagadas',
    unpaidOrders: 'Órdenes Sin Pagar',
    
    // Discount Management
    applyDiscount: 'Aplicar Descuento',
    editDiscount: 'Editar Descuento',
    discountPercentage: 'Porcentaje de Descuento',
    enterDiscountPercentage: 'Ingresa el porcentaje de descuento (0-100)',
    discountUpdated: 'Descuento actualizado exitosamente',
    currentDiscount: 'Descuento Actual',
    newTotal: 'Nuevo Total',
    saveDiscount: 'Guardar Descuento',
    yourDiscountApplied: 'Tu descuento se aplica automáticamente',
    
    // Products
    product: 'Producto',
    price: 'Precio',
    quantity: 'Cantidad',
    stockAvailable: 'Stock Disponible',
    units: 'unidades',
    ppfProducts: 'Película de Protección de Pintura (PPF)',
    windowTint: 'Polarizado de Ventanas',
    addToOrder: 'Agregar a Orden',
    noProducts: 'No hay productos disponibles',
    productCatalog: 'Catálogo de Productos',
    managePPFWindowTint: 'Gestiona tu inventario de PPF y Window Tint',
    manageCategories: 'Gestionar Categorías',
    addProduct: 'Agregar Producto',
    addNewProduct: 'Agregar Nuevo Producto',
    completeNewProductInfo: 'Completa la información del nuevo producto para el catálogo',
    productName: 'Nombre del Producto',
    description: 'Descripción',
    detailedDescription: 'Descripción detallada del producto',
    category: 'Categoría',
    size: 'Tamaño',
    finish: 'Acabado',
    priceUSD: 'Precio (USD)',
    stockQuantity: 'Cantidad en Stock',
    editProduct: 'Editar Producto',
    modifyProductInfo: 'Modifica la información del producto',
    basePriceUSD: 'Precio Base (USD)',
    updateProduct: 'Actualizar Producto',
    startAddingProducts: 'Comienza agregando productos a tu catálogo',
    addFirstProduct: 'Agregar Primer Producto',
    productsLabel: 'productos',
    discount: 'descuento',
    loadingProducts: 'Cargando productos...',
    errorLoadingProducts: 'Error al cargar productos',
    productAddedSuccess: 'Producto agregado exitosamente',
    productUpdatedSuccess: 'Producto actualizado exitosamente',
    productDeletedSuccess: 'Producto eliminado exitosamente',
    confirmDeleteProduct: '¿Está seguro que desea eliminar este producto?',
    cannotDeleteProductOrders: 'No se puede eliminar el producto. Está referenciado en órdenes.',
    errorDeletingProduct: 'Error al eliminar producto',
    deleteProduct: 'Eliminar Producto',
    
    // Custom Items
    customItems: 'Artículos Especiales',
    customItemsDescription: 'Agregar artículos especiales que no están en el catálogo. Estos artículos NO tendrán descuento de nivel aplicado.',
    itemName: 'Nombre del Artículo',
    customItemNamePlaceholder: 'Ej: Kit PPF Especial',
    customItemNotesPlaceholder: 'Ej: Pedido especial para cliente',
    noDiscountApplied: 'Sin descuento aplicado',
    catalogProducts: 'Productos del Catálogo',
    tierDiscount: 'Descuento de Nivel',
    add: 'Agregar',
    optional: 'Opcional',
    
    // Categories
    productCategoryManagement: 'Gestionar Categorías de Productos',
    addEditDeleteCategories: 'Agregar, editar o eliminar categorías de productos',
    addNewCategory: 'Agregar Nueva Categoría',
    categoryCode: 'Código de Categoría',
    displayName: 'Nombre para Mostrar',
    createCategory: 'Crear Categoría',
    existingCategories: 'Categorías Existentes',
    code: 'Código',
    categoryCreatedSuccess: 'Categoría creada exitosamente',
    categoryDeletedSuccess: 'Categoría eliminada exitosamente',
    confirmDeleteCategory: '¿Está seguro que desea eliminar la categoría',
    
    // New Order
    createNewOrder: 'Crear Nueva Orden',
    createOrderForClient: 'Crear Orden para Cliente',
    selectClientAndProducts: 'Selecciona un cliente y agrega productos a su orden',
    selectClient: 'Seleccionar Cliente',
    selectClientOption: 'Seleccionar cliente...',
    selectClientRequired: 'Por favor selecciona un cliente para crear la orden',
    noClientsAvailable: 'No hay clientes disponibles. Crea cuentas de cliente primero en la sección de Usuarios.',
    addOrder: 'Agregar Orden',
    selectProductsCompleteDetails: 'Selecciona los productos y completa los detalles',
    productSelection: 'Selección de Productos',
    selectProductOption: 'Seleccionar producto',
    stock: 'Stock',
    subtotalLabel: 'Subtotal',
    remove: 'Eliminar',
    
    // Stock Return Dialog
    returnToStock: '¿Devolver al Inventario?',
    returnToStockDescription: '¿Desea devolver este artículo al inventario?',
    yesReturnToStock: 'Sí, Devolver al Stock',
    noKeepRemoved: 'No, Solo Eliminar',
    
    addProductButton: 'Agregar Producto',
    shippingInfo: 'Información de Envío',
    savedAddresses: 'Direcciones Guardadas',
    selectSavedAddress: 'Seleccionar dirección guardada...',
    addNewAddress: 'Agregar Nueva Dirección',
    fullShippingAddress: 'Dirección de Envío Completa',
    includeNameAddressCityStateZip: 'Incluye nombre, dirección, ciudad, estado, código postal',
    specialInstructions: 'Instrucciones Especiales',
    anySpecialInstructions: 'Cualquier instrucción especial para el manejo o entrega de la orden (opcional)',
    orderSummary: 'Resumen de la Orden',
    totalLabel: 'Total:',
    createOrderButton: 'Crear Orden',
    addNewAddressTitle: 'Agregar Nueva Dirección',
    saveAddressForFuture: 'Guarda esta dirección para uso futuro en tus órdenes',
    addressName: 'Nombre de la Dirección',
    addressNamePlaceholder: 'Ej: Taller Principal, Almacén, Casa',
    fullAddressLabel: 'Dirección Completa',
    city: 'Ciudad',
    state: 'Estado',
    zipCode: 'Código Postal',
    setAsDefault: 'Establecer como dirección predeterminada',
    saveAddress: 'Guardar Dirección',
    orderCreatedSuccess: '¡Orden creada exitosamente!',
    addressSavedSuccess: 'Dirección guardada exitosamente',
    errorCreatingOrder: 'Error al crear orden',
    errorSavingAddress: 'Error al guardar dirección',
    errorLoadingProducts: 'Error al cargar productos',
    loadingProductsOrder: 'Cargando productos...',
    
    // Edit Order Items
    editOrderItems: 'Editar Artículos de la Orden',
    editItemsDescription: 'Modificar cantidades, agregar o eliminar productos de esta orden',
    currentItems: 'Artículos Actuales',
    addMoreProducts: 'Agregar Más Productos',
    selectProductToAdd: 'Seleccionar producto a agregar...',
    addToOrderBtn: 'Agregar',
    newTotal: 'Nuevo Total',
    originalTotal: 'Total Original',
    saveChanges: 'Guardar Cambios',
    itemsUpdatedSuccess: 'Artículos de la orden actualizados exitosamente',
    minOneItem: 'La orden debe tener al menos un artículo',
    perUnit: 'por unidad',
    allProductsAdded: 'Todos los productos disponibles han sido agregados a la orden',
    
    // Orders Management (Admin)
    ordersManagementTitle: 'Gestión de Órdenes',
    manageAllOrders: 'Administra todas las órdenes del sistema',
    totalLabel2: 'Total',
    searchOrdersAdmin: 'Buscar Órdenes',
    searchByOrderClientCompany: 'Buscar por número de orden, cliente o empresa...',
    manage: 'Gestionar',
    newOrders: 'Nuevas',
    inProcess: 'En Proceso',
    completedTab: 'Completadas',
    noOrdersInCategory: 'No hay órdenes en esta categoría',
    orderNumber: 'Orden #',
    statusLabel: 'Estado',
    editOrder: 'Editar',
    editItems: 'Artículos',
    deleteOrder: 'Eliminar',
    manageOrderNumber: 'Gestionar Orden #',
    updateStatusAddSerials: 'Actualizar estado y agregar números de serie',
    orderInfo: 'Información de la Orden',
    client: 'Cliente',
    companyLabel: 'Empresa',
    created: 'Creada',
    orderStatus: 'Estado de la Orden',
    trackingNumber: 'Número de Seguimiento',
    optionalTracking: 'Opcional',
    serialNumbers: 'Números de Serie',
    addSerial: 'Agregar Serie',
    serialNumberLabel: 'Número de Serie',
    selectProductSerial: 'Seleccionar producto...',
    notes: 'Notas',
    adminNotes: 'Notas Administrativas',
    internalOrderNotes: 'Notas internas sobre la orden...',
    updateOrder: 'Actualizar Orden',
    orderStatusUpdated: 'Estado de orden actualizado exitosamente',
    orderDeleted: 'Orden eliminada exitosamente',
    confirmDeleteOrder: '¿Está seguro que desea eliminar la orden #',
    actionCannotBeUndone: '? Esta acción no se puede deshacer.',
    errorUpdatingOrder: 'Error al actualizar orden',
    errorDeletingOrder: 'Error al eliminar orden',
    loadingOrders: 'Cargando órdenes...',
    
    // Users Management
    userManagement: 'Gestión de Usuarios',
    manageClientDistributorAccounts: 'Administra cuentas de clientes y distribuidores',
    createUserButton: 'Crear Usuario',
    createNewUser: 'Crear Nuevo Usuario',
    completeInfoCreateAccount: 'Completa la información para crear una nueva cuenta de usuario',
    usernameLabel: 'Nombre de Usuario',
    emailLabel: 'Email',
    passwordLabel: 'Contraseña',
    fullNameLabel: 'Nombre Completo',
    companyNameLabel: 'Nombre de la Empresa',
    roleLabel: 'Rol',
    installationCenter: 'Installation Center',
    elitePartner: 'Elite TC Partner',
    tcAdmin: 'TC Administrator',
    salesRepRole: 'Representante de Ventas',
    customPricingBtn: 'Precios Personalizados',
    softwareRenewalsTitle: 'Suscripciones de Software',
    softwareRenewalsSubtitle: 'Estado de renovación de todas las cuentas — marca las vencidas y por vencer.',
    manageSoftwareAccounts: 'Administrar Cuentas',
    emailCenterTab: 'Centro Email',
    productionBarcodesTab: 'Códigos de Producción',
    quickComposerTitle: 'Redactar un Mensaje',
    quickComposerSubtitle: 'Escribe el asunto y el mensaje, elige a quién enviarlo y presiona enviar. Así de simple.',
    quickSendTo: 'Enviar a',
    quickSubject: 'Asunto',
    quickSubjectPlaceholder: 'ej. Lanzamiento de nuevo producto este mes',
    quickMessage: 'Mensaje',
    quickMessagePlaceholder: 'Escribe tu mensaje aquí...',
    quickMessageHint: 'Texto simple está bien — lo colocaremos automáticamente en tu plantilla TC con tu marca. Deja una línea en blanco entre párrafos.',
    quickSendButton: 'Enviar Email',
    quickSending: 'Enviando...',
    quickRecipientCount: 'Se enviará a {n} destinatario(s).',
    quickCustomEmailsPlaceholder: 'Ingresa correos separados por comas',
    quickSubjectRequired: 'Por favor ingresa un asunto',
    quickMessageRequired: 'Por favor escribe tu mensaje',
    quickEmailsRequired: 'Por favor ingresa al menos un correo',
    quickSentOk: 'Enviado a {n} destinatario(s) ✓',
    quickSentPartial: 'Enviados {sent}, {failed} fallaron',
    quickSendFailed: 'Error al enviar',
    showAdvancedTemplates: '⚙ Avanzado: administrar plantillas de email',
    hideAdvancedTemplates: '▲ Ocultar plantillas avanzadas',
    audAllDealers: 'Todos los Distribuidores (Centros + Elite Partners)',
    audInstallationCenters: 'Solo Centros de Instalación',
    audElitePartners: 'Solo Elite Partners',
    audSalesReps: 'Representantes de Ventas',
    audEveryone: 'Todos (Distribuidores + Partners + Ventas)',
    audAdmins: 'Administradores',
    audCustom: 'Correos específicos',
    emailCenterTitle: 'Centro de Emails',
    emailCenterSubtitle: 'Administra plantillas de email con marca TC, envía anuncios y comunicados a tus socios.',
    newTemplateBtn: 'Nueva Plantilla',
    viewEmailLog: 'Historial',
    subjectLabel: 'Asunto',
    nameLabel: 'Nombre',
    categoryLabel: 'Categoría',
    descriptionLabel: 'Descripción',
    htmlBodyLabel: 'Cuerpo HTML',
    templateVarsHelper: 'Usa marcadores {{nombre_variable}}. first_name, full_name y recipient_email se completan automáticamente por destinatario.',
    editBtn: 'Editar',
    previewBtn: 'Vista Previa',
    broadcastBtn: 'Enviar',
    resetBtn: 'Restablecer',
    deleteBtn: 'Eliminar',
    saveBtn: 'Guardar',
    createBtn: 'Crear',
    editTemplateTitle: 'Editar Plantilla',
    editTemplateSubtitle: 'Edita el asunto y el cuerpo HTML. Todos los emails se envuelven automáticamente en la marca TC gold + black.',
    createTemplateTitle: 'Crear Plantilla Personalizada',
    templateIdLabel: 'ID de Plantilla (slug)',
    previewTitle: 'Vista Previa del Email',
    previewVarsLabel: 'Variables',
    rerenderBtn: 'Volver a Renderizar',
    sendTestToLabel: 'Enviar prueba a',
    sendTestBtn: 'Enviar Prueba',
    testEmailRequired: 'Ingresa un email de prueba',
    testEmailSent: 'Email de prueba enviado a {email}',
    testEmailFailed: 'Falló el envío de prueba',
    broadcastTitle: 'Enviar Difusión',
    broadcastSubtitle: 'Envía esta plantilla a una audiencia filtrada. Cada destinatario recibe su propia copia con el nombre auto-completado.',
    audienceLabel: 'Audiencia',
    audAllAdmins: 'Todos los Admins',
    audAllDealers: 'Todos los Distribuidores (Instalación + Elite)',
    audInstallationCenters: 'Solo Centros de Instalación',
    audElitePartners: 'Solo Socios Elite',
    audSalesReps: 'Solo Representantes',
    audAllPartners: 'Todos los Socios (Distribuidores + Reps)',
    audCustom: 'Emails personalizados…',
    customEmailsLabel: 'Emails personalizados (separados por comas)',
    customEmailsHelper: 'Ingresa los emails abajo (separados por comas)',
    recipients: 'destinatarios',
    subjectOverrideLabel: 'Sobrescribir Asunto (deja en blanco para usar el asunto de la plantilla)',
    sendBroadcastBtn: 'Enviar',
    sending: 'Enviando...',
    broadcastSuccess: 'Difusión enviada a {n} destinatarios',
    broadcastPartial: 'Parcial: {sent} enviados, {failed} fallidos',
    broadcastFailed: 'Falló la difusión',
    templateSavedSuccess: 'Plantilla guardada',
    templateCreatedSuccess: 'Plantilla creada',
    templateDeletedSuccess: 'Plantilla eliminada',
    templateResetSuccess: 'Plantilla restablecida',
    errorSavingTemplate: 'Error al guardar',
    errorLoadingTemplates: 'Error al cargar plantillas',
    resetTemplateConfirm: '¿Restablecer esta plantilla del sistema a los valores predeterminados? Tus cambios se perderán.',
    deleteTemplateConfirm: '¿Eliminar esta plantilla? Esta acción no se puede deshacer.',
    emailLogTitle: 'Actividad Reciente de Emails',
    noEmailLogYet: 'Aún no se han enviado emails.',
    sendRemindersNow: 'Enviar Recordatorios',
    sendingReminders: 'Enviando...',
    remindersLastSent: 'Último email enviado',
    remindersSentSuccess: 'Resumen enviado a {n} administrador(es)',
    remindersPartial: 'Envío parcial: {sent} exitosos, {failed} fallidos',
    remindersSkipped: 'Los recordatorios ya se enviaron hoy',
    remindersFailed: 'Error al enviar los recordatorios de renovación',
    renewalExpired: 'Vencidas',
    renewalExpiringSoon: 'Por Vencer',
    renewalActive: 'Activas',
    renewalTotal: 'Total',
    renewalExpiredHeader: '🚨 Cuentas Vencidas',
    renewalExpiringHeader: '⏰ Vencen en 30 Días',
    renewalAllHealthy: 'Todas las cuentas de software están al día — ninguna vence en los próximos 30 días.',
    daysAgo: 'días atrás',
    daysLeft: 'días restantes',
    createSoftwareAccountBtn: 'Crear Cuenta de Software',
    createSoftwareAccountTitle: 'Crear Cuenta de Software',
    createSoftwareAccountSubtitle: 'Crea una cuenta de software directamente para un cliente, saltando el flujo de solicitud/aprobación.',
    businessNameLabel: 'Nombre del Negocio',
    contactPersonLabel: 'Persona de Contacto',
    planTypeLabel: 'Tipo de Plan',
    softwareUsernameLabel: 'Usuario del Software',
    softwarePasswordLabel: 'Contraseña del Software',
    subscriptionStartLabel: 'Inicio de Suscripción',
    subscriptionEndLabel: 'Fin de Suscripción',
    subscriptionEndRequired: 'La fecha de fin de suscripción es requerida',
    createAccountBtn: 'Crear Cuenta',
    softwareAccountCreatedSuccess: 'Cuenta de software creada exitosamente',
    errorCreatingSoftwareAccount: 'Error al crear cuenta de software',
    shopTypeLabel: 'Tipo de Negocio',
    adminNotesLabel: 'Notas del Administrador',
    customPricingTitle: 'Precios Personalizados',
    customPricingSubtitle: 'Sobreescribe el descuento por nivel con un % personalizado o define precios específicos por producto para esta cuenta.',
    customDiscountLabel: 'Descuento Personalizado % (sobreescribe nivel)',
    customDiscountHelper: 'Cuando se establece, reemplaza el descuento estándar del nivel para todos los productos. Déjalo vacío para usar el descuento del nivel.',
    customPerProductLabel: 'Precios Personalizados por Producto (opcional)',
    customPerProductHelper: 'Define un precio fijo para productos específicos. Los precios personalizados siempre prevalecen sobre el descuento por porcentaje.',
    listPriceLabel: 'Precio Lista',
    customPriceLabel: 'Precio Personalizado',
    leaveEmptyForTier: '— usar nivel —',
    willUseTierDiscount: 'Se usará el descuento del nivel',
    invalidDiscountRange: 'El descuento debe estar entre 0 y 100',
    invalidCustomPrice: 'Los precios personalizados deben ser números no negativos',
    pricingUpdatedSuccess: 'Precios personalizados actualizados exitosamente',
    pricingClearedSuccess: 'Precios personalizados eliminados. La cuenta usará el descuento del nivel.',
    errorUpdatingPricing: 'Error al actualizar precios personalizados',
    errorLoadingPricing: 'Error al cargar precios',
    clearAllPricing: 'Eliminar Todos los Precios Personalizados',
    clearAllPricingConfirm: 'Se eliminará el descuento personalizado % Y todos los precios por producto de esta cuenta. ¿Continuar?',
    savePricingBtn: 'Guardar Precios',
    salesRepDashboardTitle: 'Panel del Representante de Ventas',
    salesRepDashboardSubtitle: 'Administra tus clientes y órdenes — haz crecer tu cartera.',
    myClients: 'Mis Clientes',
    myOrders: 'Mis Órdenes',
    manageMyClients: 'Administrar Mis Clientes',
    assignSalesRepLabel: 'Asignar Representante de Ventas (Opcional)',
    noSalesRepAssigned: '— Sin representante asignado —',
    noSalesRepClientsYet: 'Aún no has creado clientes. Agrega uno desde la pestaña "Mis Clientes".',
    pendingOrdersLabel: 'Órdenes Pendientes',
    totalRevenueLabel: 'Ingresos Totales',
    recentOrders: 'Órdenes Recientes',
    phoneLabel: 'Teléfono',
    addressLabel: 'Dirección',
    userList: 'Lista de Usuarios',
    registeredUsers: 'Usuarios registrados en el sistema',
    user: 'Usuario',
    activeStatus: 'Activo',
    inactiveStatus: 'Inactivo',
    deactivate: 'Desactivar',
    activate: 'Activar',
    noUsersRegistered: 'No hay usuarios registrados',
    startCreatingAccounts: 'Comienza creando cuentas para tus clientes',
    createFirstUser: 'Crear Primer Usuario',
    userCreatedSuccess: 'Usuario creado exitosamente',
    userDeletedSuccess: 'Usuario eliminado exitosamente',
    userStatusUpdated: 'Estado del usuario actualizado',
    confirmDeleteUser: '¿Está seguro que desea eliminar al usuario',
    errorCreatingUser: 'Error al crear usuario',
    errorDeletingUser: 'Error al eliminar usuario',
    errorUpdatingUser: 'Error al actualizar usuario',
    userUpdatedSuccess: 'Usuario actualizado exitosamente',
    editUser: 'Editar Usuario',
    editUserInfo: 'Editar información de la cuenta del usuario',
    updateUser: 'Actualizar Usuario',
    newPasswordOptional: 'Nueva Contraseña (dejar en blanco para mantener la actual)',
    loadingUsers: 'Cargando usuarios...',
    
    // Warranty - Client
    warrantyManagementClient: 'Gestión de Garantías',
    requestWarrantiesCheckHistory: 'Solicita garantías y consulta el historial de tus registros',
    requestWarranty: 'Solicitar Garantía',
    myWarranties: 'Mis Garantías',
    searchWarranties: 'Buscar Garantías',
    searchInMyWarranties: 'Buscar en Mis Garantías',
    searchBySerialClientVINVehicle: 'Buscar por serie, cliente, VIN o vehículo...',
    myWarrantyRequests: 'Mis Solicitudes de Garantía',
    warrantyHistoryStatus: 'Historial y estado de tus solicitudes de garantía',
    noWarrantiesRequested: 'No tienes garantías solicitadas',
    requestWarrantiesToProvide: 'Solicita garantías para brindar tranquilidad a tus clientes',
    requestFirstWarranty: 'Solicitar Primera Garantía',
    requestWarrantyTitle: 'Solicitar Garantía',
    completeInstallationInfo: 'Complete la información de instalación para solicitar cobertura de garantía',
    serialNumberRequired: 'Número de Garantía *',
    verifyingSerial: 'Verificando número de serie...',
    validSerialNumber: 'Número de serie válido',
    productLabel: 'Producto',
    orderLabel: 'Orden',
    finalClientName: 'Nombre del Cliente Final *',
    vehicleOwnerName: 'Nombre completo del propietario del vehículo',
    vehicleMakeRequired: 'Marca del Vehículo *',
    modelRequired: 'Modelo *',
    yearRequired: 'Año *',
    vinRequired: 'Número VIN *',
    vinCharacters: '17 caracteres del VIN',
    vehicleMileageRequired: 'Millaje del Vehículo *',
    vehicleMileagePlaceholder: 'Lectura actual del odómetro',
    mileageLabel: 'Millaje',
    installationDateRequired: 'Fecha de Instalación *',
    installationAreaRequired: 'Área de Instalación *',
    selectArea: 'Seleccionar área...',
    additionalNotesOptional: 'Notas Adicionales',
    additionalInstallationInfo: 'Información adicional sobre la instalación...',
    sendRequest: 'Enviar Solicitud',
    mustEnterValidSerial: 'Debe ingresar un número de serie válido',
    warrantyRequestSentSuccess: 'Solicitud de garantía enviada exitosamente. Será revisada por nuestro equipo.',
    errorSendingWarrantyRequest: 'Error al enviar solicitud de garantía',
    loadingWarranties: 'Cargando garantías...',
    errorLoadingWarranties: 'Error al cargar garantías',
    
    // Warranty Card
    clientLabel: 'Cliente',
    vinLabel: 'VIN',
    serialLabel: 'No. de Garantía',
    installedLabel: 'Instalado',
    areaLabel: 'Área',
    requestedLabel: 'Solicitado',
    notesLabel: 'Notas',
    adminCommentsLabel: 'Comentarios del administrador',
    warrantyValidUntil: 'Garantía válida hasta',
    underReview: 'En Revisión',
    downloadCertificate: 'Descargar Certificado',
    certificateDownloaded: 'Certificado descargado exitosamente',
    errorDownloadingCertificate: 'Error al descargar certificado',
    rejectionReason: 'Razón del Rechazo',
    enterSerialFromProduct: 'Ingrese el número de garantía de su certificado',
    downloading: 'Descargando...',
    
    // Warranty Admin
    warrantyManagementAdmin: 'Gestión de Garantías',
    manageDistributorWarrantyRequests: 'Administra las solicitudes de garantía de los distribuidores',
    pendingTab: 'Pendientes',
    approvedTab: 'Aprobadas',
    rejectedTab: 'Rechazadas',
    allTab: 'Todas',
    searchWarrantiesAdmin: 'Buscar Garantías',
    searchBySerialVINClientVehicleDistributor: 'Buscar por serie, VIN, cliente, vehículo o distribuidor...',
    noPendingWarranties: 'No hay garantías pendientes',
    allRequestsProcessed: 'Todas las solicitudes han sido procesadas',
    noApprovedWarranties: 'No hay garantías aprobadas',
    noRejectedWarranties: 'No hay garantías rechazadas',
    noWarrantiesWithStatus: 'No se encontraron garantías con este estado',
    distributorLabel: 'Distribuidor',
    installationLabel: 'Instalación',
    applicantNotes: 'Notas del solicitante',
    approveWarranty: 'Aprobar Garantía',
    warrantyActivatedClientNotified: 'La garantía será activada y el cliente será notificado.',
    rejectWarranty: 'Rechazar Garantía',
    warrantyRejectedProvideReason: 'La garantía será rechazada. Por favor proporcione un motivo.',
    warrantySummary: 'Resumen de Garantía',
    commentsOptional: 'Comentarios (Opcional)',
    addCommentsOrInstructions: 'Añada comentarios o instrucciones adicionales...',
    rejectionReasonRequired: 'Motivo del Rechazo *',
    explainRejectionReason: 'Explique el motivo del rechazo de esta garantía...',
    warrantyValidityNote: 'Nota: La garantía tendrá una validez de 5 años a partir de hoy.',
    warrantyDurationMonthsLabel: 'Duración de la Garantía (meses) *',
    warrantyDurationHelper: 'Define cuánto tiempo será válida esta garantía desde la fecha de aprobación. Mínimo 5 meses.',
    monthsWord: 'meses',
    invalidWarrantyDuration: 'Ingrese una duración válida de al menos 5 meses.',
    confirmApprovalBtn: 'Confirmar Aprobación',
    confirmRejectionBtn: 'Confirmar Rechazo',
    processingText: 'Procesando...',
    editWarrantyTitle: 'Editar Garantía',
    modifyWarrantyDetails: 'Modifique los detalles de la garantía',
    statusField: 'Estado',
    clientNameField: 'Nombre del Cliente',
    vinField: 'VIN',
    makeField: 'Marca',
    modelField: 'Modelo',
    yearField: 'Año',
    installationDateField: 'Fecha de Instalación',
    installationAreaField: 'Área de Instalación',
    warrantyStart: 'Inicio de Garantía',
    warrantyEnd: 'Fin de Garantía',
    clientNotesField: 'Notas del Cliente',
    adminNotesField: 'Notas del Administrador',
    saving: 'Guardando...',
    warrantyApprovedSuccess: 'Garantía aprobada exitosamente',
    warrantyRejectedNotification: 'Garantía rechazada',
    warrantyUpdatedSuccess: 'Garantía actualizada exitosamente',
    errorProcessingRequest: 'Error al procesar la solicitud',
    errorUpdatingWarranty: 'Error al actualizar garantía',
    loadingWarrantiesAdmin: 'Cargando garantías...',
    confirmDeleteWarranty: '¿Estás seguro de que deseas eliminar esta garantía? Esta acción no se puede deshacer.',
    warrantyDeletedSuccess: 'Garantía eliminada exitosamente',
    errorDeletingWarranty: 'Error al eliminar garantía',
    confirmDeleteSoftwareRequest: '¿Estás seguro de que deseas eliminar esta solicitud de acceso al software? Esta acción no se puede deshacer.',
    softwareRequestDeletedSuccess: 'Solicitud de software eliminada exitosamente',
    errorDeletingSoftwareRequest: 'Error al eliminar solicitud de software',
    approveBtn: 'Aprobar',
    rejectBtn: 'Rechazar',
    
    // Software - Client
    softwareTCCutPro: 'TC CutPro Software',
    preciseCuttingPatterns: 'Patrones de corte precisos para cualquier vehículo',
    mostAdvancedSoftware: 'El software más avanzado de la industria con miles de patrones de corte para PPF y Window Tint. Optimiza tu tiempo, reduce desperdicios y garantiza ajustes perfectos.',
    patternsCount: '15,000+ Patrones',
    completeDatabase: 'Base de datos completa con patrones para vehículos desde 1980 hasta 2024. Actualizaciones mensuales incluidas.',
    laserPrecisionTitle: 'Precisión Láser',
    laserPrecisionDesc: 'Patrones desarrollados con tecnología láser 3D para ajustes perfectos. Reduce desperdicios hasta 40%.',
    easyToUseTitle: 'Fácil de Usar',
    easyToUseDesc: 'Interface intuitiva con búsqueda por año, marca y modelo. Integración directa con plotters de corte.',
    readyToRevolutionize: '¿Listo para revolucionar tu negocio?',
    joinShops: 'Únete a más de 2,500 talleres que ya confían en TC CutPro Software.',
    requestAccessBtn: 'Solicitar Acceso',
    shopType: 'Tipo de Taller',
    autoDetailing: 'Auto Detailing',
    tintShop: 'Tint Shop',
    bodyShop: 'Body Shop',
    dealership: 'Concesionaria',
    mobileService: 'Servicio Móvil',
    other: 'Otro',
    monthlyVolume: 'Volumen Mensual (vehículos)',
    vehicles1to10: '1-10 vehículos',
    vehicles11to25: '11-25 vehículos',
    vehicles26to50: '26-50 vehículos',
    vehicles51to100: '51-100 vehículos',
    vehiclesOver100: 'Más de 100 vehículos',
    currentSoftware: '¿Qué software usas actualmente? (Opcional)',
    currentSoftwarePlaceholder: 'Ej: Ninguno, AccuCut, otro',
    additionalInfo: 'Información Adicional',
    sendRequest: 'Enviar Solicitud',
    yourRequestStatus: 'Estado de tu Solicitud',
    requestBeingReviewed: 'Tu solicitud está siendo revisada por nuestro equipo. Te notificaremos cuando sea procesada.',
    accessApproved: '¡Tu acceso ha sido aprobado! Usa las siguientes credenciales:',
    planLabel: 'Plan',
    validUntilLabel: 'Válido hasta',
    noLimitDate: 'Sin fecha límite',
    userLabel: 'Usuario',
    passwordField: 'Contraseña',
    adminMessage: 'Mensaje del administrador',
    requestRejected: 'Tu solicitud fue rechazada.',
    reasonLabel: 'Motivo',
    requestAccessToCutPro: 'Solicitar Acceso a TC CutPro',
    completeInfoWeWillContact: 'Completa esta información y nuestro equipo te contactará en 24 horas',
    businessNameField: 'Nombre del Negocio',
    contactPersonField: 'Persona de Contacto',
    shopTypeField: 'Tipo de Taller',
    selectOption: 'Seleccionar...',
    dealershipOption: 'Concesionaria',
    mobileServiceOption: 'Servicio Móvil',
    otherOption: 'Otro',
    monthlyVolumeField: 'Volumen Mensual (vehículos)',
    vehiclesRange1: '1-10 vehículos',
    vehiclesRange2: '11-25 vehículos',
    vehiclesRange3: '26-50 vehículos',
    vehiclesRange4: '51-100 vehículos',
    vehiclesRange5: 'Más de 100 vehículos',
    whatSoftwareCurrently: '¿Qué software usas actualmente? (Opcional)',
    nonePlaceholder: 'Ej: Ninguno, AccuCut, otro',
    additionalInfoField: 'Información Adicional',
    tellUsYourNeeds: 'Cuéntanos sobre tus necesidades específicas',
    sendRequestBtn: 'Enviar Solicitud',
    requestSentSuccess: '¡Solicitud enviada exitosamente! Será revisada por nuestro equipo.',
    errorSendingRequest: 'Error al enviar solicitud',
    
    // Software - Admin
    softwareManagementAdmin: 'Gestión de Software TC CutPro',
    manageSoftwareAccessRequests: 'Administra las solicitudes de acceso al software',
    activeTab: 'Activas',
    searchSubscriptions: 'Buscar Suscripciones',
    searchByNameEmailCompanyUser: 'Buscar por nombre, email, empresa o usuario...',
    results: 'Resultados',
    noResultsFound: 'No se encontraron resultados.',
    noRequestsCategory: 'No hay solicitudes',
    approveAccessBtn: 'Aprobar Acceso',
    rejectBtn: 'Rechazar',
    approveAccessTitle: 'Aprobar Acceso',
    configureSubscriptionDetails: 'Configure los detalles de la suscripción',
    rejectRequestTitle: 'Rechazar Solicitud',
    provideRejectionReason: 'Proporcione un motivo para el rechazo',
    requestSummary: 'Resumen de Solicitud',
    contactLabel: 'Contacto',
    typeLabel: 'Tipo',
    volumeLabel: 'Volumen',
    planField: 'Plan',
    basicPlan: 'Básico - $299/mes',
    professionalPlan: 'Professional - $599/mes',
    enterprisePlan: 'Enterprise - Personalizado',
    userRequired: 'Usuario *',
    userPlaceholder: 'usuario_cutpro',
    passwordRequired: 'Contraseña *',
    softwarePassword: 'Contraseña para el software',
    subscriptionStart: 'Inicio Suscripción',
    subscriptionEndField: 'Fin Suscripción',
    notesOptional: 'Notas (Opcional)',
    additionalNotesPlaceholder: 'Notas adicionales...',
    rejectReasonRequired: 'Motivo del Rechazo *',
    explainReasonPlaceholder: 'Explique el motivo...',
    approveBtn: 'Aprobar',
    rejectConfirmBtn: 'Rechazar',
    editSubscription: 'Editar Suscripción',
    updateSubscriptionDetails: 'Actualice los detalles de la suscripción',
    startLabel: 'Inicio',
    endLabel: 'Fin',
    accessApprovedSuccess: 'Acceso aprobado exitosamente',
    requestRejectedNotification: 'Solicitud rechazada',
    subscriptionUpdatedSuccess: 'Suscripción actualizada exitosamente',
    errorProcessingRequestSoftware: 'Error al procesar solicitud',
    errorUpdatingSoftware: 'Error al actualizar',
    loadingSoftware: 'Cargando...',
    errorLoadingRequests: 'Error al cargar solicitudes',
    
    // Serial Search
    serialAndWarrantySearch: 'Búsqueda de Series y Garantías',
    trackSerialNumbersManageWarranties: 'Rastrea números de serie y gestiona garantías registradas',
    serials: 'Series',
    registeredWarranties: 'Garantías Registradas',
    searchSerialNumber: 'Buscar Número de Serie',
    searchSerialWarranties: 'Buscar',
    enterSerialNumber: 'Ingresa número de serie (ej: TC-PPF-001234)',
    rollNumberRequired: 'Número de Rollo / Serie del Tubo *',
    rollNumberShort: 'No. Rollo',
    enterRollNumberHint: 'Serie física impresa en el rollo/tubo de la película (ej: TKL99R2504P2378)',
    mustEnterRollNumber: 'Por favor ingresa el número de rollo / serie del tubo',
    searchBySerialVINClientVehicleAdmin: 'Buscar por serie, VIN, cliente, vehículo...',
    serialNumberResults: 'Resultados de números de serie',
    noRecordWithSerial: 'No se encontró ningún registro con ese número de serie.',
    warrantyResults: 'Resultados de garantías',
    noWarrantiesFoundCriteria: 'No se encontraron garantías con esos criterios.',
    recentSerialNumbers: 'Números de Serie Recientes',
    last50SerialNumbers: 'Últimos 50 números de serie asignados',
    noSerialNumbers: 'No hay números de serie',
    serialNumbersAppearHere: 'Los números de serie aparecerán aquí cuando se asignen a órdenes',
    orderColumn: 'Orden',
    clientColumn: 'Cliente',
    statusColumn: 'Estado',
    dateColumn: 'Fecha',
    recentRegisteredWarranties: 'Garantías Registradas Recientes',
    last50Warranties: 'Últimas 50 garantías registradas por clientes',
    noRegisteredWarranties: 'No hay garantías registradas',
    warrantiesAppearHere: 'Las garantías aparecerán aquí cuando los clientes las registren',
    vehicleColumn: 'Vehículo',
    serialColumn: 'Serie',
    installationColumn: 'Instalación',
    registeredColumn: 'Registrado',
    assigned: 'Asignado',
    activeWarranty: 'Activa',
    loadingSerialNumbers: 'Cargando números de serie...',
    errorLoadingSerialNumbers: 'Error al cargar números de serie',
    
    // Pricing
    pricingManagement: 'Gestión de Precios',
    configureDiscountsByAccountType: 'Configura descuentos por tipo de cuenta',
    newTier: 'Nuevo Nivel',
    createPricingTier: 'Crear Nivel de Precios',
    defineNewAccountTypeDiscount: 'Define un nuevo tipo de cuenta con su descuento correspondiente',
    roleCode: 'Código del Rol',
    roleCodePlaceholder: 'Ej: premium_dealer',
    displayNamePricing: 'Nombre para Mostrar',
    displayNamePlaceholder: 'Ej: Distribuidor Premium',
    discountPercentage: 'Porcentaje de Descuento (%)',
    createTier: 'Crear Nivel',
    configuredPricingTiers: 'Niveles de Precios Configurados',
    discountsAppliedAutomatically: 'Descuentos aplicados automáticamente según el tipo de cuenta',
    accountType: 'Tipo de Cuenta',
    discountColumn: 'Descuento',
    examplePrice: 'Ejemplo (Precio $100)',
    actionsColumn: 'Acciones',
    noPricingTiersConfigured: 'No hay niveles de precios configurados',
    createPricingTiersForAccounts: 'Crea niveles de precios para diferentes tipos de cuenta',
    createFirstTier: 'Crear Primer Nivel',
    pricingTierCreatedSuccess: 'Nivel de precios creado exitosamente',
    pricingTierUpdatedSuccess: 'Nivel de precios actualizado exitosamente',
    pricingTierDeletedSuccess: 'Nivel de precios eliminado exitosamente',
    confirmDeleteTier: '¿Está seguro que desea eliminar el nivel',
    usersWillSeeNoDiscount: '? Los usuarios con este rol verán precios sin descuento.',
    errorCreatingTier: 'Error al crear nivel de precios',
    errorUpdatingTier: 'Error al actualizar nivel de precios',
    errorDeletingTier: 'Error al eliminar nivel de precios',
    loadingPricingTiers: 'Cargando niveles de precios...',
    
    // Common
    search: 'Buscar',
    filter: 'Filtrar',
    all: 'Todos',
    cancel: 'Cancelar',
    save: 'Guardar',
    edit: 'Editar',
    delete: 'Eliminar',
    confirm: 'Confirmar',
    loading: 'Cargando...',
    noResults: 'No se encontraron resultados',
    success: 'Éxito',
    error: 'Error',
    required: 'Requerido',
    optional: 'Opcional',
    actions: 'Acciones',
    details: 'Detalles',
    date: 'Fecha',
    logout: 'Cerrar Sesión',
    minCharactersSearch: 'Ingresa al menos 2 caracteres para buscar',
    minCharactersSearch3: 'Ingresa al menos 3 caracteres para buscar',
    
    // Installation Areas
    fullFront: 'Frente Completo',
    partialFront: 'Frente Parcial',
    fullBody: 'Carrocería Completa',
    interior: 'Interior',
    windshield: 'Parabrisas',
    changeFinish: 'Cambio de Acabado',
    fullWrap: 'Envoltura Completa',
    protectionKit: 'Kit de Protección',
    
    // Shop Types
    autoDetailing: 'Auto Detailing',
    tintShop: 'Tint Shop',
    bodyShop: 'Body Shop',
    dealership: 'Concesionaria',
    mobileService: 'Servicio Móvil',
    other: 'Otro',
    
    // Volume Options
    vehicles: 'vehículos',
    vehiclesPerMonth: 'vehículos/mes',
    moreThan: 'Más de',
    
    // Roles
    tcAdministrator: 'TC Administrator',
    eliteTCPartner: 'Elite TC Partner',
    installationCenterRole: 'Installation Center',
    admin: 'Admin',
    
    // Misc
    errorLoadingDashboard: 'Error al cargar datos del dashboard',
    errorLoadingCategories: 'Error al cargar categorías',
    errorCreatingCategory: 'Error al crear categoría'
  }
};

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('tc_language') || 'es';
  });
  
  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'es' : 'en';
    setLanguage(newLang);
    localStorage.setItem('tc_language', newLang);
  };
  
  const t = (key) => {
    return translations[language][key] || key;
  };
  
  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Language Toggle Component
const LanguageToggle = () => {
  const { language, toggleLanguage } = useLanguage();
  
  return (
    <button
      onClick={toggleLanguage}
      className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800 hover:bg-slate-700 border border-amber-500/30 transition-all"
      data-testid="language-toggle"
    >
      <Globe className="w-4 h-4 text-amber-400" />
      <span className="text-sm font-medium text-white">{language.toUpperCase()}</span>
    </button>
  );
};

// Auth Context
const AuthContext = React.createContext();

const IDLE_LIMIT_MS = 60 * 60 * 1000;   // 60 minutes of inactivity -> logout
const IDLE_WARN_MS = 60 * 1000;         // warn 60s before logout
const ACTIVITY_KEY = 'tc_last_activity';

// Shows a "still there?" warning before auto-logout; resets on any activity (synced across tabs).
const InactivityGuard = ({ onTimeout }) => {
  const [warnLeft, setWarnLeft] = useState(0);
  const lastRef = React.useRef(Date.now());

  const bump = () => {
    const now = Date.now();
    lastRef.current = now;
    try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch (e) {}
    setWarnLeft(0);
  };

  useEffect(() => {
    bump();
    const evs = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    let throttle = 0;
    const onAct = () => { const n = Date.now(); if (n - throttle > 1000) { throttle = n; bump(); } };
    evs.forEach((e) => window.addEventListener(e, onAct, { passive: true }));
    const onStorage = (e) => { if (e.key === ACTIVITY_KEY && e.newValue) { lastRef.current = Math.max(lastRef.current, parseInt(e.newValue, 10)); setWarnLeft(0); } };
    window.addEventListener('storage', onStorage);
    const iv = setInterval(() => {
      const idle = Date.now() - lastRef.current;
      if (idle >= IDLE_LIMIT_MS) { clearInterval(iv); onTimeout(); return; }
      if (idle >= IDLE_LIMIT_MS - IDLE_WARN_MS) setWarnLeft(Math.ceil((IDLE_LIMIT_MS - idle) / 1000));
    }, 1000);
    return () => { evs.forEach((e) => window.removeEventListener(e, onAct)); window.removeEventListener('storage', onStorage); clearInterval(iv); };
    // eslint-disable-next-line
  }, []);

  if (warnLeft <= 0) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="inactivity-warning">
      <div className="bg-[#14171B] border border-[#C6A15B]/30 rounded-2xl p-6 max-w-sm w-full mx-4 text-center shadow-2xl">
        <Clock className="w-10 h-10 text-[#C6A15B] mx-auto mb-3" />
        <h3 className="text-white font-semibold text-lg">Still there?</h3>
        <p className="text-slate-400 text-sm mt-2">For your security you'll be signed out in <span className="text-[#C6A15B] font-semibold" data-testid="inactivity-countdown">{warnLeft}</span> second{warnLeft === 1 ? '' : 's'}.</p>
        <div className="flex gap-3 mt-5">
          <button onClick={onTimeout} className="flex-1 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200 text-sm" data-testid="inactivity-logout-btn">Log out</button>
          <button onClick={bump} className="flex-1 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium text-sm" data-testid="inactivity-stay-btn">Stay logged in</button>
        </div>
      </div>
    </div>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserInfo();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [token]);

  // Keep the logged-in user's role/pricing/permissions fresh without requiring a re-login,
  // and force logout if the account was disabled. Refreshes every 2 min + on tab focus.
  useEffect(() => {
    if (!token) return;
    const refresh = () => fetchUserInfo(true);
    const iv = setInterval(refresh, 120000);
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line
  }, [token]);

  const fetchUserInfo = async (silent = false) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      const status = error?.response?.status;
      // On initial load logout on any failure; on silent background refresh only on auth failures
      // (so a transient network blip doesn't sign people out).
      if (!silent || status === 401 || status === 403) {
        if (!silent) console.error('Auth error:', error);
        logout();
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        username,
        password
      });
      
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
      {user && <InactivityGuard onTimeout={logout} />}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Login Component
const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const result = await login(username, password);
    
    if (result.success) {
      toast.success(t('welcomeMessage'));
      navigate('/dashboard');
    } else {
      toast.error(result.message);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-pattern opacity-20"></div>
      
      {/* Language Toggle - Top Right */}
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>
      
      <Card className="w-full max-w-md bg-black/40 backdrop-blur-xl border-amber-500/20 shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 w-28 h-16 flex items-center justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_warranty-approval/artifacts/1iugy51y_Gold.png" 
              alt="TC Protection Film Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-white">TC Protection Film</CardTitle>
          <CardDescription className="text-amber-400/80">
            {t('distributionSystem')}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-gray-300">{t('username')}</Label>
              <Input
                id="username"
                type="text"
                placeholder={language === 'en' ? 'Enter your username' : 'Ingresa tu usuario'}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="bg-white/10 border-gray-600 text-white placeholder-gray-400 focus:border-amber-500"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">{t('password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={language === 'en' ? 'Enter your password' : 'Ingresa tu contraseña'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-white/10 border-gray-600 text-white placeholder-gray-400 focus:border-amber-500"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold"
              disabled={isLoading}
            >
              {isLoading ? t('loggingIn') : t('loginButton')}
            </Button>
          </form>
          

        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard Header Component
const DashboardHeader = ({ user, onLogout }) => {
  const { t } = useLanguage();
  
  const getRoleBadge = (role) => {
    const roleMap = {
      'tc_admin': { label: 'TC Administrator', color: 'bg-red-600', icon: <Shield className="w-3 h-3" /> },
      'elite_partner': { label: 'Elite TC Partner', color: 'bg-purple-600', icon: <Star className="w-3 h-3" /> },
      'installation_center': { label: 'Installation Center', color: 'bg-blue-600', icon: <Building className="w-3 h-3" /> },
      'sales_rep': { label: 'Sales Rep', color: 'bg-emerald-600', icon: <Users className="w-3 h-3" /> }
    };
    
    const roleInfo = roleMap[role] || { label: role, color: 'bg-gray-600', icon: null };
    
    return (
      <Badge className={`${roleInfo.color} text-white gap-1`}>
        {roleInfo.icon}
        {roleInfo.label}
      </Badge>
    );
  };

  return (
    <div className="bg-black/80 backdrop-blur-xl border-b border-amber-500/20 px-3 sm:px-6 py-3 sm:py-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div className="flex items-center space-x-3 sm:space-x-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_warranty-approval/artifacts/1iugy51y_Gold.png" 
            alt="TC Logo" 
            className="w-10 h-6 sm:w-12 sm:h-7 object-contain"
          />
          <div>
            <h1 className="text-base sm:text-xl font-bold text-white">TC Protection Film</h1>
            <p className="text-xs sm:text-sm text-amber-400">{t('distributionSystem')}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto justify-between sm:justify-end">
          <LanguageToggle />
          <div className="text-right hidden xs:block sm:block">
            <p className="text-white font-medium text-sm sm:text-base truncate max-w-[120px] sm:max-w-none">{user?.full_name}</p>
            <p className="text-xs text-gray-400 truncate max-w-[120px] sm:max-w-none">{user?.company_name}</p>
          </div>
          <div className="hidden md:block">{getRoleBadge(user?.role)}</div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onLogout}
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 text-xs sm:text-sm px-2 sm:px-3"
          >
            <LogOut className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('logout')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

// Products Component
const ProductsSection = ({ user }) => {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    display_name: '',
    description: '',
    icon: ''
  });
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    category: 'ppf',
    size: '',
    finish: '',
    price: '',
    stock_quantity: ''
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/products`);
      // Handle both paginated and non-paginated responses
      const productsData = response.data.products || response.data;
      setProducts(productsData);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error al cargar categorías');
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/api/categories`, newCategory);
      toast.success('Categoría creada exitosamente');
      setNewCategory({
        name: '',
        display_name: '',
        description: '',
        icon: ''
      });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear categoría');
    }
  };

  const deleteCategory = async (categoryName, displayName) => {
    if (window.confirm(t('confirmDeleteCategory') + ` "${displayName}"?`)) {
      try {
        await axios.delete(`${API_BASE_URL}/api/categories/${categoryName}`);
        toast.success(t('categoryDeletedSuccess'));
        fetchCategories();
      } catch (error) {
        toast.error(error.response?.data?.detail || t('errorCreatingCategory'));
      }
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/api/products`, {
        ...newProduct,
        price: parseFloat(newProduct.price),
        stock_quantity: parseInt(newProduct.stock_quantity)
      });
      
      toast.success('Producto agregado exitosamente');
      setShowAddProduct(false);
      setNewProduct({
        name: '',
        description: '',
        category: 'ppf',
        size: '',
        finish: '',
        price: '',
        stock_quantity: ''
      });
      fetchProducts();
    } catch (error) {
      toast.error('Error al agregar producto');
    }
  };

  const startEditProduct = (product) => {
    setEditingProduct({
      ...product,
      price: product.original_price || product.price, // Use original price for editing
      stock_quantity: product.stock_quantity.toString()
    });
    setShowEditProduct(true);
  };

  const handleEditProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_BASE_URL}/api/products/${editingProduct.product_id}`, {
        ...editingProduct,
        price: parseFloat(editingProduct.price),
        stock_quantity: parseInt(editingProduct.stock_quantity)
      });
      
      toast.success(t('productUpdatedSuccess'));
      setShowEditProduct(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const deleteProduct = async (productId, productName) => {
    if (window.confirm(t('confirmDeleteProduct') + ` "${productName}"`)) {
      try {
        await axios.delete(`${API_BASE_URL}/api/products/${productId}`);
        toast.success(t('productDeletedSuccess'));
        fetchProducts();
      } catch (error) {
        const errorMsg = error.response?.data?.detail || t('errorDeletingProduct');
        toast.error(errorMsg);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="tc-spinner"></div>
        <span className="ml-4 text-primary">{t('loadingProducts')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-primary mb-2">
            {t('productCatalog')}
          </h2>
          <p className="text-secondary">{t('managePPFWindowTint')}</p>
        </div>
        {user?.role === 'tc_admin' && (
          <div className="flex gap-3">
            <Button 
              onClick={() => setShowCategoryManager(true)}
              className="tc-button-secondary hover-lift"
            >
              <Settings className="w-4 h-4 mr-2" />
              {t('manageCategories')}
            </Button>
            <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
              <DialogTrigger asChild>
                <Button className="tc-button-primary hover-lift">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('addProduct')}
                </Button>
              </DialogTrigger>
              <DialogContent className="tc-dialog-content">
                <div className="tc-dialog-header">
                  <DialogTitle className="tc-dialog-title">{t('addNewProduct')}</DialogTitle>
                  <DialogDescription className="tc-dialog-description">
                    {t('completeNewProductInfo')}
                  </DialogDescription>
                </div>
              <div className="tc-dialog-body">
                <form onSubmit={handleAddProduct} className="tc-form-grid space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label className="tc-label">{t('productName')}</label>
                      <input
                        className="tc-input tc-focus-ring"
                        value={newProduct.name}
                        onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                        placeholder="Ej: 60&quot;x50&apos; TC PPF Premium"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="tc-label">{t('description')}</label>
                      <textarea
                        className="tc-textarea tc-focus-ring"
                        value={newProduct.description}
                        onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                        placeholder={t('detailedDescription')}
                        rows={3}
                        required
                      />
                    </div>
                  </div>

                  <div className="tc-form-grid tc-form-grid-2 gap-4">
                    <div>
                      <label className="tc-label">{t('category')}</label>
                      <select 
                        className="tc-select tc-focus-ring"
                        value={newProduct.category} 
                        onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                      >
                        {categories.map((category) => (
                          <option key={category.name} value={category.name}>
                            {category.display_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="tc-label">{t('size')}</label>
                      <input
                        className="tc-input tc-focus-ring"
                        value={newProduct.size}
                        onChange={(e) => setNewProduct({...newProduct, size: e.target.value})}
                        placeholder='Ej: 60"x50&apos;'
                        required
                      />
                    </div>
                  </div>

                  <div className="tc-form-grid tc-form-grid-2 gap-4">
                    <div>
                      <label className="tc-label">{t('finish')}</label>
                      <input
                        className="tc-input tc-focus-ring"
                        value={newProduct.finish}
                        onChange={(e) => setNewProduct({...newProduct, finish: e.target.value})}
                        placeholder="Ej: matte, gloss, satin"
                      />
                    </div>
                    
                    <div>
                      <label className="tc-label">{t('priceUSD')}</label>
                      <input
                        className="tc-input tc-focus-ring"
                        type="number"
                        step="0.01"
                        value={newProduct.price}
                        onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                        placeholder="299.99"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="tc-label">{t('stockQuantity')}</label>
                    <input
                      className="tc-input tc-focus-ring"
                      type="number"
                      value={newProduct.stock_quantity}
                      onChange={(e) => setNewProduct({...newProduct, stock_quantity: e.target.value})}
                      placeholder="100"
                      required
                    />
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button 
                      type="button" 
                      onClick={() => setShowAddProduct(false)}
                      className="tc-button-secondary flex-1"
                    >
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="tc-button-primary flex-1">
                      {t('addProduct')}
                    </Button>
                  </div>
                </form>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        )}
        
        {/* Edit Product Dialog */}
        {user?.role === 'tc_admin' && (
          <Dialog open={showEditProduct} onOpenChange={setShowEditProduct}>
            <DialogContent className="tc-dialog-content">
              <div className="tc-dialog-header">
                <DialogTitle className="tc-dialog-title">{t('editProduct')}</DialogTitle>
                <DialogDescription className="tc-dialog-description">
                  {t('modifyProductInfo')}
                </DialogDescription>
              </div>
              <div className="tc-dialog-body">
                <form onSubmit={handleEditProduct} className="tc-form-grid space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label className="tc-label">{t('productName')}</label>
                      <input
                        className="tc-input tc-focus-ring"
                        value={editingProduct?.name || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                        placeholder="Ej: 60&quot;x50&apos; TC PPF Premium"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="tc-label">Descripción</label>
                      <textarea
                        className="tc-textarea tc-focus-ring"
                        value={editingProduct?.description || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                        placeholder="Descripción detallada del producto"
                        rows={3}
                        required
                      />
                    </div>
                  </div>

                  <div className="tc-form-grid tc-form-grid-2 gap-4">
                    <div>
                      <label className="tc-label">Categoría</label>
                      <select 
                        className="tc-select tc-focus-ring"
                        value={editingProduct?.category || 'ppf'} 
                        onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})}
                      >
                        {categories.map((category) => (
                          <option key={category.name} value={category.name}>
                            {category.display_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="tc-label">Tamaño</label>
                      <input
                        className="tc-input tc-focus-ring"
                        value={editingProduct?.size || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, size: e.target.value})}
                        placeholder='Ej: 60"x50&apos;'
                        required
                      />
                    </div>
                  </div>

                  <div className="tc-form-grid tc-form-grid-2 gap-4">
                    <div>
                      <label className="tc-label">Acabado</label>
                      <input
                        className="tc-input tc-focus-ring"
                        value={editingProduct?.finish || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, finish: e.target.value})}
                        placeholder="Ej: matte, gloss, satin"
                      />
                    </div>
                    
                    <div>
                      <label className="tc-label">Precio Base (USD)</label>
                      <input
                        className="tc-input tc-focus-ring"
                        type="number"
                        step="0.01"
                        value={editingProduct?.price || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, price: e.target.value})}
                        placeholder="299.99"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="tc-label">Cantidad en Stock</label>
                    <input
                      className="tc-input tc-focus-ring"
                      type="number"
                      value={editingProduct?.stock_quantity || ''}
                      onChange={(e) => setEditingProduct({...editingProduct, stock_quantity: e.target.value})}
                      placeholder="100"
                      required
                    />
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button 
                      type="button" 
                      onClick={() => setShowEditProduct(false)}
                      className="tc-button-secondary flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button type="submit" className="tc-button-primary flex-1">
                      Actualizar Producto
                    </Button>
                  </div>
                </form>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Products organized by category - Dynamic rendering */}
      <div className="space-y-8">
        {categories.map((category) => {
          const categoryProducts = products.filter(p => p.category === category.name);
          if (categoryProducts.length === 0) return null;
          
          // Determine icon and badge color based on category
          const getCategoryIcon = () => {
            switch (category.icon) {
              case 'shield': return <Shield className="w-4 h-4 text-black font-bold" />;
              case 'eye': return <Eye className="w-4 h-4 text-white" />;
              case 'star': return <Star className="w-4 h-4 text-black" />;
              case 'settings': return <Settings className="w-4 h-4 text-white" />;
              default: return <Package className="w-4 h-4 text-white" />;
            }
          };
          
          const getIconBgColor = () => {
            switch (category.name) {
              case 'ppf': return 'bg-gradient-to-br from-amber-500 to-amber-600';
              case 'window_tint': return 'bg-gradient-to-br from-blue-500 to-blue-600';
              default: return 'bg-gradient-to-br from-purple-500 to-purple-600';
            }
          };
          
          const getBadgeClass = () => {
            switch (category.name) {
              case 'ppf': return 'tc-badge tc-badge-gold';
              case 'window_tint': return 'tc-badge tc-badge-info';
              default: return 'tc-badge tc-badge-gold';
            }
          };
          
          const getBadgeText = () => {
            if (category.name === 'ppf') return 'PPF';
            if (category.name === 'window_tint') return 'TINT';
            return category.name.toUpperCase().substring(0, 6);
          };
          
          return (
            <div key={category.name} className="space-y-6">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 ${getIconBgColor()} rounded-full flex items-center justify-center`}>
                  {getCategoryIcon()}
                </div>
                <h3 className="text-2xl font-bold text-primary">{category.display_name}</h3>
                <Badge className={getBadgeClass()}>
                  {categoryProducts.length} {t('productsLabel')}
                </Badge>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryProducts.map((product) => (
                  <Card key={product.product_id} className="tc-card hover-lift fade-in">
                    <CardHeader className="tc-card-header">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-primary text-lg font-semibold mb-2">
                            {product.name}
                          </CardTitle>
                          <CardDescription className="text-secondary text-sm">
                            {product.description}
                          </CardDescription>
                        </div>
                        <div className="ml-4">
                          <Badge className={getBadgeClass()}>
                            {getBadgeText()}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="tc-card-content">
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted">{t('size')}:</span>
                            <p className="text-primary font-medium">{product.size}</p>
                          </div>
                          {product.finish && (
                            <div>
                              <span className="text-muted">{t('finish')}:</span>
                              <p className="text-primary font-medium capitalize">{product.finish}</p>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex justify-between items-center">
                          {(user?.role === 'elite_partner' || user?.role === 'tc_admin') && (
                            <div>
                              <span className="text-muted text-sm">{t('stockAvailable')}:</span>
                              <p className={`font-bold text-lg ${
                                product.stock_quantity > 20 ? 'text-green-400' : 
                                product.stock_quantity > 5 ? 'text-yellow-400' : 'text-red-400'
                              }`}>
                                {product.stock_quantity} {t('units')}
                              </p>
                            </div>
                          )}
                          
                          <div className={`${(user?.role === 'elite_partner' || user?.role === 'tc_admin') ? 'text-right' : ''}`}>
                            {product.discount_percentage > 0 && (
                              <div className="text-sm text-muted line-through">
                                ${product.original_price?.toFixed(2)}
                              </div>
                            )}
                            <div className="text-3xl font-bold text-gold">
                              ${product.price.toFixed(2)}
                            </div>
                            {product.discount_percentage > 0 && (
                              <div className="text-xs text-green-400">
                                {product.discount_percentage}% {t('discount')}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {user?.role === 'tc_admin' && (
                          <div className="pt-4 border-t border-amber-500/20 space-y-2">
                            <Button 
                              onClick={() => startEditProduct(product)}
                              className="tc-button-secondary w-full"
                              size="sm"
                            >
                              <Settings className="w-4 h-4 mr-2" />
                              {t('editProduct')}
                            </Button>
                            <Button 
                              onClick={() => deleteProduct(product.product_id, product.name)}
                              variant="destructive"
                              className="w-full bg-red-600 hover:bg-red-700"
                              size="sm"
                            >
                              <Trash className="w-4 h-4 mr-2" />
                              {t('deleteProduct')}
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {products.length === 0 && (
        <Card className="tc-card">
          <CardContent className="tc-card-content text-center py-12">
            <Package className="w-16 h-16 text-muted mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-primary mb-2">{t('noProducts')}</h3>
            <p className="text-secondary mb-6">{t('startAddingProducts')}</p>
            {user?.role === 'tc_admin' && (
              <Button 
                onClick={() => setShowAddProduct(true)}
                className="tc-button-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('addFirstProduct')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Category Management Dialog */}
      {user?.role === 'tc_admin' && (
        <Dialog open={showCategoryManager} onOpenChange={setShowCategoryManager}>
          <DialogContent className="tc-dialog-content max-w-2xl">
            <div className="tc-dialog-header">
              <DialogTitle className="tc-dialog-title">Gestionar Categorías de Productos</DialogTitle>
              <DialogDescription className="tc-dialog-description">
                Agregar, editar o eliminar categorías de productos
              </DialogDescription>
            </div>
            <div className="tc-dialog-body">
              <div className="space-y-6">
                {/* Add Category Form */}
                <div className="border-b border-amber-500/20 pb-6">
                  <h4 className="text-lg font-semibold text-primary mb-4">Agregar Nueva Categoría</h4>
                  <form onSubmit={handleCreateCategory} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="tc-label">Código de Categoría</label>
                        <input
                          className="tc-input tc-focus-ring"
                          value={newCategory.name}
                          onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                          placeholder="ej: ceramic_coating"
                          required
                        />
                      </div>
                      <div>
                        <label className="tc-label">Nombre para Mostrar</label>
                        <input
                          className="tc-input tc-focus-ring"
                          value={newCategory.display_name}
                          onChange={(e) => setNewCategory({...newCategory, display_name: e.target.value})}
                          placeholder="ej: Ceramic Coating"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="tc-label">Descripción</label>
                      <textarea
                        className="tc-textarea tc-focus-ring"
                        value={newCategory.description}
                        onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                        placeholder="Descripción de la categoría..."
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="tc-label">Icono (opcional)</label>
                      <select 
                        className="tc-select tc-focus-ring"
                        value={newCategory.icon} 
                        onChange={(e) => setNewCategory({...newCategory, icon: e.target.value})}
                      >
                        <option value="">Seleccionar icono...</option>
                        <option value="shield">Shield (PPF)</option>
                        <option value="eye">Eye (Window Tint)</option>
                        <option value="star">Star (Premium)</option>
                        <option value="package">Package (General)</option>
                        <option value="settings">Settings (Tools)</option>
                      </select>
                    </div>
                    <Button type="submit" className="tc-button-primary">
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Categoría
                    </Button>
                  </form>
                </div>

                {/* Existing Categories */}
                <div>
                  <h4 className="text-lg font-semibold text-primary mb-4">Categorías Existentes</h4>
                  <div className="space-y-3">
                    {categories.map((category) => (
                      <div key={category.name} className="flex items-center justify-between p-4 bg-tc-gold/5 rounded-lg border border-amber-500/20">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-gold to-amber-600 rounded-full flex items-center justify-center">
                            {category.icon === 'shield' && <Shield className="w-5 h-5 text-black" />}
                            {category.icon === 'eye' && <Eye className="w-5 h-5 text-black" />}
                            {category.icon === 'star' && <Star className="w-5 h-5 text-black" />}
                            {category.icon === 'package' && <Package className="w-5 h-5 text-black" />}
                            {category.icon === 'settings' && <Settings className="w-5 h-5 text-black" />}
                            {!category.icon && <Package className="w-5 h-5 text-black" />}
                          </div>
                          <div>
                            <h5 className="text-primary font-semibold">{category.display_name}</h5>
                            <p className="text-muted text-sm">Código: {category.name}</p>
                            {category.description && (
                              <p className="text-secondary text-sm">{category.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="tc-badge tc-badge-info text-xs">
                            {products.filter(p => p.category === category.name).length} {t('productsLabel')}
                          </Badge>
                          {!['ppf', 'window_tint'].includes(category.name) && (
                            <Button
                              onClick={() => deleteCategory(category.name, category.display_name)}
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              variant="ghost"
                            >
                              <Trash className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button 
                    onClick={() => setShowCategoryManager(false)}
                    className="tc-button-secondary"
                  >
                    Cerrar
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Client Dashboard Component
const ClientDashboard = ({ user }) => {
  const { t } = useLanguage();
  const [dashboardData, setDashboardData] = useState({
    totalOrders: 0,
    totalSpent: 0,
    pendingOrders: [],
    completedOrders: []
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchClientDashboardData();
  }, []);

  const fetchClientDashboardData = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/orders`);
      // Handle both paginated and non-paginated responses
      const orders = response.data.orders || response.data;

      const totalSpent = orders.reduce((sum, order) => sum + order.total_amount, 0);
      const pendingOrders = orders.filter(order => 
        ['pending', 'processing', 'shipped'].includes(order.status)
      );
      const completedOrders = orders.filter(order => 
        ['delivered', 'completed'].includes(order.status)
      );

      setDashboardData({
        totalOrders: orders.length,
        totalSpent,
        pendingOrders,
        completedOrders
      });
    } catch (error) {
      toast.error(t('errorLoadingDashboard'));
    } finally {
      setLoading(false);
    }
  };

  const handleOrderSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || searchQuery.length < 2) {
      toast.error(t('enterAtLeast2Chars'));
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/orders/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      toast.error(t('errorSearchingOrders'));
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const downloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/orders/${orderId}/invoice`);
      const pdfBase64 = response.data.invoice_pdf;
      downloadPDF(pdfBase64, `invoice-${orderId.substring(0, 8)}.pdf`);
      toast.success('Invoice descargado exitosamente');
    } catch (error) {
      toast.error('Error al descargar invoice');
    }
  };

  const downloadPDF = (pdfBase64, filename) => {
    const byteCharacters = atob(pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: t("pending"), class: 'tc-badge-warning' },
      'processing': { label: t("processing"), class: 'tc-badge-info' },
      'shipped': { label: t("shipped"), class: 'tc-badge-info' },
      'delivered': { label: t("delivered"), class: 'tc-badge-success' },
      'completed': { label: t("completed"), class: 'tc-badge-success' }
    };
    
    const statusInfo = statusMap[status] || { label: status, class: 'tc-badge-warning' };
    return <Badge className={`tc-badge ${statusInfo.class}`}>{statusInfo.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="tc-spinner"></div>
        <span className="ml-4 text-primary">{t('loadingDashboard')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="text-center pb-6">
        <h1 className="text-4xl font-bold text-primary mb-2">
          {t('welcome')}, {user?.full_name}
        </h1>
        <p className="text-secondary text-lg">{user?.company_name}</p>
        <div className="w-24 h-1 bg-gradient-to-r from-transparent via-tc-gold to-transparent mx-auto mt-4"></div>
      </div>

      {/* Order Search */}
      <Card className="tc-card">
        <CardContent className="tc-card-content">
          <h3 className="text-lg font-semibold text-primary mb-4">{t('searchOrders')}</h3>
          <form onSubmit={handleOrderSearch} className="flex gap-4 items-end">
            <div className="flex-1">
              <input
                className="tc-input tc-focus-ring"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('searchByOrderNumberNameCompany')}
              />
            </div>
            <Button 
              type="submit" 
              disabled={searching}
              className="tc-button-primary"
            >
              {searching ? t('searching') : t('search')}
            </Button>
            {searchResults !== null && (
              <Button 
                type="button" 
                onClick={clearSearch}
                className="tc-button-secondary"
              >
                {t('clear')}
              </Button>
            )}
          </form>
          
          {searchResults !== null && (
            <div className="mt-6">
              <h4 className="text-md font-medium text-primary mb-3">
                {t('searchResults')} ({searchResults.length})
              </h4>
              {searchResults.length === 0 ? (
                <p className="text-secondary">{t('noOrdersFoundCriteria')}</p>
              ) : (
                <div className="space-y-3">
                  {searchResults.map((order) => (
                    <div key={order.order_id} className="flex items-center justify-between p-3 bg-tc-gold/5 rounded-lg border border-amber-500/20">
                      <div className="flex items-center gap-3">
                        <Hash className="w-4 h-4 text-gold" />
                        <div>
                          <p className="text-primary font-medium">#{order.order_id.substring(0, 8)}</p>
                          <p className="text-muted text-sm">{new Date(order.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(order.status)}
                        <p className="text-gold font-semibold">${order.total_amount.toFixed(2)}</p>
                        <Button
                          onClick={() => downloadInvoice(order.order_id)}
                          size="sm"
                          className="tc-button-secondary"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          {t('invoice')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid md:grid-cols-2 gap-8">
        <Card className="tc-card hover-lift">
          <CardContent className="tc-card-content text-center py-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-tc-gold to-tc-gold-dark rounded-full flex items-center justify-center">
                <ShoppingCart className="w-8 h-8 text-black" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-primary mb-2">{dashboardData.totalOrders}</h3>
            <p className="text-secondary font-medium">{t('totalOrdersCard')}</p>
            <p className="text-muted text-sm mt-1">{t('sinceRegistration')}</p>
          </CardContent>
        </Card>

        <Card className="tc-card hover-lift">
          <CardContent className="tc-card-content text-center py-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                <DollarSign className="w-8 h-8 text-white" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-primary mb-2">${dashboardData.totalSpent.toFixed(2)}</h3>
            <p className="text-secondary font-medium">{t('totalInvested')}</p>
            <p className="text-muted text-sm mt-1">{t('inTCProducts')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Orders Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-primary">{t('pendingOrdersTitle')}</h2>
          <Badge className="tc-badge tc-badge-warning">{dashboardData.pendingOrders.length}</Badge>
        </div>

        {dashboardData.pendingOrders.length === 0 ? (
          <Card className="tc-card">
            <CardContent className="tc-card-content text-center py-8">
              <Package className="w-12 h-12 text-muted mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-primary mb-2">{t('noPendingOrders')}</h3>
              <p className="text-secondary">{t('allOrdersCompleted')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {dashboardData.pendingOrders.map((order) => (
              <Card key={order.order_id} className="tc-card hover-lift">
                <CardContent className="tc-card-content">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Hash className="w-5 h-5 text-gold" />
                      <h3 className="text-lg font-semibold text-primary">
                        #{order.order_id.substring(0, 8)}
                      </h3>
                      {getStatusBadge(order.status)}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gold">${order.total_amount.toFixed(2)}</p>
                      <p className="text-muted text-sm">{new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-secondary font-medium mb-2">{t('products')} ({order.items.length})</p>
                      <div className="space-y-1">
                        {order.items.slice(0, 2).map((item, idx) => (
                          <p key={idx} className="text-muted text-sm">
                            {item.quantity}x {item.product_name}
                          </p>
                        ))}
                        {order.items.length > 2 && (
                          <p className="text-muted text-sm">+{order.items.length - 2} {t('more')}...</p>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-secondary font-medium mb-2">{t('shippingAddress')}</p>
                      <p className="text-muted text-sm">
                        {order.shipping_address.length > 50 
                          ? order.shipping_address.substring(0, 50) + '...'
                          : order.shipping_address
                        }
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button 
                      onClick={() => downloadInvoice(order.order_id)}
                      className="tc-button-secondary"
                      size="sm"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {t('downloadInvoice')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Completed Orders Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-primary">{t('completedOrdersTitle')}</h2>
          <Badge className="tc-badge tc-badge-success">{dashboardData.completedOrders.length}</Badge>
        </div>

        {dashboardData.completedOrders.length === 0 ? (
          <Card className="tc-card">
            <CardContent className="tc-card-content text-center py-8">
              <FileText className="w-12 h-12 text-muted mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-primary mb-2">{t('noCompletedOrders')}</h3>
              <p className="text-secondary">{t('ordersWillAppearHere')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {dashboardData.completedOrders.map((order) => (
              <Card key={order.order_id} className="tc-card hover-lift">
                <CardContent className="tc-card-content">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Hash className="w-4 h-4 text-gold" />
                      <h4 className="font-semibold text-primary">#{order.order_id.substring(0, 8)}</h4>
                    </div>
                    {getStatusBadge(order.status)}
                  </div>
                  
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-muted text-sm">{order.items.length} {t('products')}</p>
                      <p className="text-muted text-xs">{new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gold">${order.total_amount.toFixed(2)}</p>
                      <Button 
                        onClick={() => downloadInvoice(order.order_id)}
                        className="tc-button-secondary mt-2"
                        size="sm"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        {t('invoice')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = ({ user, setActiveTab }) => {
  const { t } = useLanguage();
  const [dashboardData, setDashboardData] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    processingOrders: 0,
    shippedOrders: 0,
    deliveredOrders: 0,
    recentOrders: [],
    topCustomers: []
  });
  const [loading, setLoading] = useState(true);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState(null);
  const [clientSearching, setClientSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientProfile, setShowClientProfile] = useState(false);
  const [renewalData, setRenewalData] = useState(null);
  const [renewalExpanded, setRenewalExpanded] = useState({ expiring_soon: true, expired: true, active: false });
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderStatus, setReminderStatus] = useState(null);

  useEffect(() => {
    fetchAdminDashboardData();
    fetchRenewalStatus();
    fetchReminderStatus();
  }, []);

  const fetchRenewalStatus = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/software-accounts/renewal-status`);
      setRenewalData(res.data);
    } catch (error) {
      console.error('Error loading renewal status', error);
    }
  };

  const fetchReminderStatus = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/software-accounts/reminder-status`);
      setReminderStatus(res.data);
    } catch (error) {
      console.error('Error loading reminder status', error);
    }
  };

  const sendRenewalReminders = async () => {
    if (reminderSending) return;
    setReminderSending(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/admin/software-accounts/send-renewal-reminders?force=true`);
      const { sent_count, failed_count, status } = res.data;
      if (status === 'sent') {
        toast.success(t('remindersSentSuccess').replace('{n}', sent_count));
      } else if (status === 'partial') {
        toast.warning(t('remindersPartial').replace('{sent}', sent_count).replace('{failed}', failed_count));
      } else if (status === 'skipped') {
        toast.info(res.data.reason || t('remindersSkipped'));
      } else {
        toast.error(t('remindersFailed'));
      }
      fetchReminderStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('remindersFailed'));
    } finally {
      setReminderSending(false);
    }
  };

  const fetchAdminDashboardData = async () => {
    try {
      const ordersResponse = await axios.get(`${API_BASE_URL}/api/orders`);
      const usersResponse = await axios.get(`${API_BASE_URL}/api/users`);
      
      // Handle both paginated and non-paginated responses
      const orders = ordersResponse.data.orders || ordersResponse.data;
      const users = usersResponse.data.users || usersResponse.data;

      // Calculate statistics
      const totalRevenue = orders.reduce((sum, order) => sum + order.total_amount, 0);
      
      const statusCounts = orders.reduce((acc, order) => {
        acc[order.status] = (acc[order.status] || 0) + 1;
        return acc;
      }, {});

      // Get recent orders (last 10)
      const recentOrders = orders
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 10);

      // Calculate top customers by total spent
      const customerTotals = orders.reduce((acc, order) => {
        const key = order.customer_company || order.customer_name;
        acc[key] = (acc[key] || 0) + order.total_amount;
        return acc;
      }, {});

      const topCustomers = Object.entries(customerTotals)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([name, total]) => ({ name, total }));

      setDashboardData({
        totalOrders: orders.length,
        totalRevenue,
        pendingOrders: statusCounts.pending || 0,
        processingOrders: statusCounts.processing || 0,
        shippedOrders: statusCounts.shipped || 0,
        deliveredOrders: (statusCounts.delivered || 0) + (statusCounts.completed || 0),
        recentOrders,
        topCustomers
      });
    } catch (error) {
      toast.error(t('errorLoadingDashboard'));
    } finally {
      setLoading(false);
    }
  };
  const handleClientSearch = async (e) => {
    e.preventDefault();
    if (!clientSearchQuery.trim() || clientSearchQuery.length < 2) {
      toast.error(t('minCharactersSearch'));
      return;
    }

    setClientSearching(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clients/search?q=${encodeURIComponent(clientSearchQuery)}`);
      setClientSearchResults(response.data);
    } catch (error) {
      toast.error(t('error'));
      setClientSearchResults([]);
    } finally {
      setClientSearching(false);
    }
  };

  const clearClientSearch = () => {
    setClientSearchQuery('');
    setClientSearchResults(null);
  };

  const viewClientProfile = async (client) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clients/${client.user_id}/profile`);
      // Map the response to the expected format
      const profileData = {
        ...response.data.client,
        stats: {
          total_orders: response.data.statistics?.total_orders || 0,
          total_spent: response.data.statistics?.total_spent || 0,
          pending_orders: response.data.statistics?.status_breakdown?.pending || 0
        },
        orders: response.data.orders || [],
        recent_orders: response.data.recent_orders || []
      };
      setSelectedClient(profileData);
      setShowClientProfile(true);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const downloadPackingSlip = async (orderId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/orders/${orderId}/packing-slip`);
      const pdfBase64 = response.data.packing_slip_pdf;
      downloadPDF(pdfBase64, `packing-slip-${orderId.substring(0, 8)}.pdf`);
      toast.success(t('packingDownloaded'));
    } catch (error) {
      toast.error(t('errorDownloadingPacking'));
    }
  };

  const downloadPDF = (pdfBase64, filename) => {
    const byteCharacters = atob(pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: t('pending'), class: 'tc-badge-warning' },
      'processing': { label: t('processing'), class: 'tc-badge-info' },
      'shipped': { label: t('shipped'), class: 'tc-badge-info' },
      'delivered': { label: t('delivered'), class: 'tc-badge-success' },
      'completed': { label: t('completed'), class: 'tc-badge-success' }
    };
    
    const statusInfo = statusMap[status] || { label: status, class: 'tc-badge-warning' };
    return <Badge className={`tc-badge ${statusInfo.class}`}>{statusInfo.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="tc-spinner"></div>
        <span className="ml-4 text-primary">{t('loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Admin Header */}
      <div className="text-center pb-6">
        <h1 className="text-4xl font-bold text-primary mb-2">
          {t('adminPanel')}
        </h1>
        <p className="text-secondary text-lg">{t('executiveDashboard')}</p>
        <div className="w-24 h-1 bg-gradient-to-r from-transparent via-tc-gold to-transparent mx-auto mt-4"></div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <Card className="tc-card hover-lift">
          <CardContent className="tc-card-content text-center py-6">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <Package className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-primary mb-1">{dashboardData.pendingOrders}</h3>
            <p className="text-secondary text-sm font-medium">{t('pendingLabel')}</p>
          </CardContent>
        </Card>

        <Card className="tc-card hover-lift">
          <CardContent className="tc-card-content text-center py-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-primary mb-1">{dashboardData.processingOrders}</h3>
            <p className="text-secondary text-sm font-medium">{t('processingLabel')}</p>
          </CardContent>
        </Card>

        <Card className="tc-card hover-lift">
          <CardContent className="tc-card-content text-center py-6">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <ShoppingCart className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-primary mb-1">{dashboardData.shippedOrders}</h3>
            <p className="text-secondary text-sm font-medium">{t('onTheWay')}</p>
          </CardContent>
        </Card>

        <Card className="tc-card hover-lift">
          <CardContent className="tc-card-content text-center py-6">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-primary mb-1">{dashboardData.deliveredOrders}</h3>
            <p className="text-secondary text-sm font-medium">{t('delivered')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue and Orders Summary */}
      <div className="grid md:grid-cols-2 gap-8">
        <Card className="tc-card hover-lift">
          <CardContent className="tc-card-content text-center py-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-tc-gold to-tc-gold-dark rounded-full flex items-center justify-center">
                <DollarSign className="w-8 h-8 text-black" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-primary mb-2">${dashboardData.totalRevenue.toFixed(2)}</h3>
            <p className="text-secondary font-medium">{t('totalRevenue')}</p>
            <p className="text-muted text-sm mt-1">{t('revenueFromOrders')}</p>
          </CardContent>
        </Card>

        <Card className="tc-card hover-lift">
          <CardContent className="tc-card-content text-center py-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                <ShoppingCart className="w-8 h-8 text-white" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-primary mb-2">{dashboardData.totalOrders}</h3>
            <p className="text-secondary font-medium">{t('totalOrdersLabel')}</p>
            <p className="text-muted text-sm mt-1">{t('sinceStart')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Public Certificate Verification URL — for embedding on Shopify / website */}
      <Card className="tc-card" data-testid="public-verify-url-card">
        <CardContent className="tc-card-content">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-5 h-5 text-tc-gold" />
                <h3 className="text-lg font-semibold text-primary">Public Certificate Verification URL</h3>
              </div>
              <p className="text-secondary text-sm">Share this link on your Shopify site or anywhere so customers can verify their TC installation with a serial number.</p>
            </div>
            <div className="flex-1 flex items-center gap-2 bg-black/60 border border-amber-500/40 rounded-lg px-3 py-2 min-w-0">
              <code className="text-amber-300 text-sm truncate flex-1 font-mono" data-testid="public-verify-url">
                {(process.env.REACT_APP_PUBLIC_URL || window.location.origin) + '/verify'}
              </code>
              <Button size="sm" onClick={() => {
                const url = (process.env.REACT_APP_PUBLIC_URL || window.location.origin) + '/verify';
                navigator.clipboard.writeText(url).then(() => toast.success('URL copied to clipboard'));
              }} className="tc-button-primary text-xs shrink-0" data-testid="copy-verify-url-btn">
                Copy
              </Button>
              <Button size="sm" variant="outline" onClick={() => window.open((process.env.REACT_APP_PUBLIC_URL || window.location.origin) + '/verify', '_blank')} className="tc-button-secondary text-xs shrink-0" data-testid="open-verify-url-btn">
                Open
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Software Renewal Widget */}
      {renewalData && (
        <Card className="tc-card" data-testid="renewal-widget">
          <div className="tc-card-header flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
                <Settings className="w-5 h-5 text-emerald-400" />
                {t('softwareRenewalsTitle')}
              </h3>
              <p className="text-secondary text-sm">{t('softwareRenewalsSubtitle')}</p>
              {reminderStatus?.last_sent_at && (
                <p className="text-muted text-xs mt-1" data-testid="reminder-last-sent">
                  {t('remindersLastSent')}: {new Date(reminderStatus.last_sent_at).toLocaleString()}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={sendRenewalReminders}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={reminderSending}
                data-testid="send-reminders-now-btn"
              >
                {reminderSending ? t('sendingReminders') : (
                  <>
                    <span className="mr-1">✉️</span>
                    {t('sendRemindersNow')}
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setActiveTab('software')}
                className="tc-button-secondary"
                data-testid="renewal-widget-manage-btn"
              >
                {t('manageSoftwareAccounts')}
              </Button>
            </div>
          </div>
          <CardContent className="tc-card-content">
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="p-4 bg-red-900/20 border border-red-500/40 rounded-lg text-center" data-testid="renewal-stat-expired">
                <div className="text-3xl font-bold text-red-400">{renewalData.counts.expired}</div>
                <div className="text-xs text-red-300 uppercase mt-1">{t('renewalExpired')}</div>
              </div>
              <div className="p-4 bg-amber-900/20 border border-amber-500/40 rounded-lg text-center" data-testid="renewal-stat-expiring">
                <div className="text-3xl font-bold text-amber-400">{renewalData.counts.expiring_soon}</div>
                <div className="text-xs text-amber-300 uppercase mt-1">{t('renewalExpiringSoon')}</div>
              </div>
              <div className="p-4 bg-emerald-900/20 border border-emerald-500/40 rounded-lg text-center" data-testid="renewal-stat-active">
                <div className="text-3xl font-bold text-emerald-400">{renewalData.counts.active}</div>
                <div className="text-xs text-emerald-300 uppercase mt-1">{t('renewalActive')}</div>
              </div>
              <div className="p-4 bg-slate-800/60 border border-slate-600 rounded-lg text-center" data-testid="renewal-stat-total">
                <div className="text-3xl font-bold text-slate-200">{renewalData.counts.total}</div>
                <div className="text-xs text-slate-400 uppercase mt-1">{t('renewalTotal')}</div>
              </div>
            </div>

            {/* Expired section */}
            {renewalData.expired.length > 0 && (
              <div className="mb-4">
                <button
                  onClick={() => setRenewalExpanded({...renewalExpanded, expired: !renewalExpanded.expired})}
                  className="w-full flex items-center justify-between p-2 text-red-400 font-semibold hover:bg-red-500/10 rounded"
                >
                  <span>{t('renewalExpiredHeader')} ({renewalData.expired.length})</span>
                  <span>{renewalExpanded.expired ? '−' : '+'}</span>
                </button>
                {renewalExpanded.expired && (
                  <div className="space-y-2 mt-2" data-testid="renewal-list-expired">
                    {renewalData.expired.slice(0, 10).map(a => (
                      <div key={a.request_id} className="flex items-center justify-between p-3 bg-red-900/10 border border-red-500/20 rounded">
                        <div>
                          <p className="text-primary font-medium">{a.business_name}</p>
                          <p className="text-xs text-red-300">{a.contact_person} • {a.plan_type} • @{a.software_username}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-red-400 font-bold text-sm">{Math.abs(a.days_until_expiry)} {t('daysAgo')}</div>
                          <div className="text-xs text-muted">{a.subscription_end ? new Date(a.subscription_end).toLocaleDateString() : '-'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Expiring soon section */}
            {renewalData.expiring_soon.length > 0 && (
              <div className="mb-4">
                <button
                  onClick={() => setRenewalExpanded({...renewalExpanded, expiring_soon: !renewalExpanded.expiring_soon})}
                  className="w-full flex items-center justify-between p-2 text-amber-400 font-semibold hover:bg-amber-500/10 rounded"
                >
                  <span>{t('renewalExpiringHeader')} ({renewalData.expiring_soon.length})</span>
                  <span>{renewalExpanded.expiring_soon ? '−' : '+'}</span>
                </button>
                {renewalExpanded.expiring_soon && (
                  <div className="space-y-2 mt-2" data-testid="renewal-list-expiring">
                    {renewalData.expiring_soon.map(a => (
                      <div key={a.request_id} className="flex items-center justify-between p-3 bg-amber-900/10 border border-amber-500/20 rounded">
                        <div>
                          <p className="text-primary font-medium">{a.business_name}</p>
                          <p className="text-xs text-amber-300">{a.contact_person} • {a.plan_type} • @{a.software_username}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-amber-400 font-bold text-sm">{a.days_until_expiry} {t('daysLeft')}</div>
                          <div className="text-xs text-muted">{a.subscription_end ? new Date(a.subscription_end).toLocaleDateString() : '-'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {renewalData.expired.length === 0 && renewalData.expiring_soon.length === 0 && (
              <p className="text-emerald-400 text-center py-4">{t('renewalAllHealthy')}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Orders and Top Customers */}
      <div className="grid md:grid-cols-2 gap-8">
        {/* Recent Orders */}
        <Card className="tc-card">
          <div className="tc-card-header">
            <h3 className="text-xl font-semibold text-primary">{t('recentOrdersAdmin')}</h3>
            <p className="text-secondary text-sm">{t('last10Orders')}</p>
          </div>
          <CardContent className="tc-card-content p-0">
            <div className="space-y-1">
              {dashboardData.recentOrders.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-muted mx-auto mb-4" />
                  <p className="text-secondary">{t('noRecentOrdersAdmin')}</p>
                </div>
              ) : (
                dashboardData.recentOrders.map((order) => (
                  <div key={order.order_id} className="flex items-center justify-between p-4 hover:bg-tc-gold/5 transition-colors">
                    <div className="flex items-center gap-3">
                      <Hash className="w-4 h-4 text-gold" />
                      <div>
                        <p className="text-primary font-medium text-sm">#{order.order_id.substring(0, 8)}</p>
                        <p className="text-muted text-xs">{order.customer_company}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(order.status)}
                      <p className="text-gold font-semibold">${order.total_amount.toFixed(2)}</p>
                      <Button
                        onClick={() => downloadPackingSlip(order.order_id)}
                        size="sm"
                        className="tc-button-secondary"
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Client Search */}
        <Card className="tc-card">
          <div className="tc-card-header">
            <h3 className="text-xl font-semibold text-primary">{t('clientSearch')}</h3>
            <p className="text-secondary text-sm">{t('searchClientsDistributors')}</p>
          </div>
          <CardContent className="tc-card-content">
            <form onSubmit={handleClientSearch} className="flex gap-3 items-end mb-4">
              <div className="flex-1">
                <input
                  className="tc-input tc-focus-ring"
                  type="text"
                  value={clientSearchQuery}
                  onChange={(e) => setClientSearchQuery(e.target.value)}
                  placeholder={t('searchByNameEmailCompany')}
                />
              </div>
              <Button 
                type="submit" 
                disabled={clientSearching}
                className="tc-button-primary"
                size="sm"
              >
                {clientSearching ? t('searching') : t('search')}
              </Button>
              {clientSearchResults !== null && (
                <Button 
                  type="button" 
                  onClick={clearClientSearch}
                  className="tc-button-secondary"
                  size="sm"
                >
                  {t('clear')}
                </Button>
              )}
            </form>
            
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {clientSearchResults === null ? (
                <div className="text-center py-6">
                  <Users className="w-8 h-8 text-muted mx-auto mb-2" />
                  <p className="text-secondary text-sm">{t('useSearchToFindClients')}</p>
                </div>
              ) : clientSearchResults.length === 0 ? (
                <div className="text-center py-6">
                  <Users className="w-8 h-8 text-muted mx-auto mb-2" />
                  <p className="text-secondary text-sm">{t('noClientsFound')}</p>
                </div>
              ) : (
                clientSearchResults.map((client) => (
                  <div key={client.user_id} className="flex items-center justify-between p-3 bg-tc-gold/5 rounded-lg border border-amber-500/20 hover:bg-tc-gold/10 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-primary font-medium text-sm">{client.full_name}</p>
                        <p className="text-muted text-xs">{client.company_name}</p>
                        <p className="text-muted text-xs">{client.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`tc-badge ${
                        client.role === 'elite_partner' ? 'tc-badge-gold' : 'tc-badge-info'
                      } text-xs`}>
                        {client.role === 'elite_partner' ? t('elite') : t('center')}
                      </Badge>
                      <Button
                        onClick={() => viewClientProfile(client)}
                        size="sm"
                        className="tc-button-primary text-xs px-2"
                      >
                        {t('viewProfile')}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="tc-card">
        <div className="tc-card-header">
          <h3 className="text-xl font-semibold text-primary">{t('quickActions')}</h3>
          <p className="text-secondary text-sm">{t('mainAdminActions')}</p>
        </div>
        <CardContent className="tc-card-content">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              onClick={() => setActiveTab('orders')}
              className="tc-button-primary flex flex-col items-center py-6 h-auto hover:scale-105 transition-transform"
            >
              <Package className="w-6 h-6 mb-2" />
              <span>{t('manageOrders')}</span>
            </Button>
            <Button 
              onClick={() => setActiveTab('products')}
              className="tc-button-secondary flex flex-col items-center py-6 h-auto hover:scale-105 transition-transform"
            >
              <ShoppingCart className="w-6 h-6 mb-2" />
              <span>{t('viewProducts')}</span>
            </Button>
            <Button 
              onClick={() => setActiveTab('users')}
              className="tc-button-secondary flex flex-col items-center py-6 h-auto hover:scale-105 transition-transform"
            >
              <Users className="w-6 h-6 mb-2" />
              <span>{t('manageUsersAction')}</span>
            </Button>
            <Button 
              onClick={() => setActiveTab('pricing')}
              className="tc-button-secondary flex flex-col items-center py-6 h-auto hover:scale-105 transition-transform"
            >
              <DollarSign className="w-6 h-6 mb-2" />
              <span>{t('managePrices')}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Client Profile Dialog */}
      <Dialog open={showClientProfile} onOpenChange={setShowClientProfile}>
        <DialogContent className="tc-dialog-content max-w-2xl">
          <div className="tc-dialog-header">
            <DialogTitle className="tc-dialog-title">{t('clientProfile')}</DialogTitle>
            <DialogDescription className="tc-dialog-description">
              {t('detailedClientInfo')}
            </DialogDescription>
          </div>
          <div className="tc-dialog-body">
            {selectedClient && (
              <div className="space-y-6">
                {/* Client Basic Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-lg font-semibold text-primary mb-3">{t('personalInfo')}</h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <User className="w-4 h-4 text-gold" />
                          <div>
                            <p className="text-sm text-muted">{t('fullName')}</p>
                            <p className="text-primary font-medium">{selectedClient.full_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Mail className="w-4 h-4 text-gold" />
                          <div>
                            <p className="text-sm text-muted">Email</p>
                            <p className="text-primary font-medium">{selectedClient.email}</p>
                          </div>
                        </div>
                        {selectedClient.phone && (
                          <div className="flex items-center gap-3">
                            <Phone className="w-4 h-4 text-gold" />
                            <div>
                              <p className="text-sm text-muted">{t('phoneLabel')}</p>
                              <p className="text-primary font-medium">{selectedClient.phone}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="text-lg font-semibold text-primary mb-3">{t('commercialInfo')}</h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <Building className="w-4 h-4 text-gold" />
                          <div>
                            <p className="text-sm text-muted">{t('company')}</p>
                            <p className="text-primary font-medium">{selectedClient.company_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Shield className="w-4 h-4 text-gold" />
                          <div>
                            <p className="text-sm text-muted">{t('clientType')}</p>
                            <Badge className={`tc-badge ${
                              selectedClient.role === 'elite_partner' ? 'tc-badge-gold' : 'tc-badge-info'
                            }`}>
                              {selectedClient.role === 'elite_partner' ? t('eliteTCPartner') : t('installationCenterRole')}
                            </Badge>
                          </div>
                        </div>
                        {selectedClient.address && (
                          <div className="flex items-center gap-3">
                            <MapPin className="w-4 h-4 text-gold" />
                            <div>
                              <p className="text-sm text-muted">{t('address')}</p>
                              <p className="text-primary font-medium">{selectedClient.address}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Client Statistics */}
                {selectedClient.stats && (
                  <div>
                    <h4 className="text-lg font-semibold text-primary mb-3">{t('statistics')}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-tc-gold/5 rounded-lg border border-amber-500/20">
                        <p className="text-2xl font-bold text-gold">{selectedClient.stats.total_orders || 0}</p>
                        <p className="text-sm text-muted">{t('totalOrdersClient')}</p>
                      </div>
                      <div className="text-center p-4 bg-tc-gold/5 rounded-lg border border-amber-500/20">
                        <p className="text-2xl font-bold text-gold">${(selectedClient.stats.total_spent || 0).toFixed(2)}</p>
                        <p className="text-sm text-muted">{t('totalSpentClient')}</p>
                      </div>
                      <div className="text-center p-4 bg-tc-gold/5 rounded-lg border border-amber-500/20">
                        <p className="text-2xl font-bold text-gold">{selectedClient.stats.pending_orders || 0}</p>
                        <p className="text-sm text-muted">{t('pendingOrdersClient')}</p>
                      </div>
                      <div className="text-center p-4 bg-tc-gold/5 rounded-lg border border-amber-500/20">
                        <p className="text-2xl font-bold text-gold">
                          {selectedClient.created_at ? new Date(selectedClient.created_at).getFullYear() : 'N/A'}
                        </p>
                        <p className="text-sm text-muted">{t('clientSince')}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* All Orders - Full List */}
                {selectedClient.orders && selectedClient.orders.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-primary mb-3">{t('allOrders')} ({selectedClient.orders.length})</h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {selectedClient.orders
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .map((order) => (
                        <div key={order.order_id} className="flex items-center justify-between p-3 bg-tc-gold/5 rounded-lg border border-amber-500/20">
                          <div className="flex items-center gap-3">
                            <Hash className="w-4 h-4 text-gold" />
                            <div>
                              <p className="text-sm font-medium text-primary">#{order.order_id.substring(0, 8)}</p>
                              <p className="text-xs text-muted">{new Date(order.created_at).toLocaleDateString()}</p>
                              {order.items && order.items.length > 0 && (
                                <p className="text-xs text-secondary">{order.items.length} {t('items')}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={`tc-badge ${
                              order.status === 'completed' || order.status === 'delivered' ? 'tc-badge-success' :
                              order.status === 'shipped' || order.status === 'processing' ? 'tc-badge-info' :
                              'tc-badge-warning'
                            } text-xs`}>
                              {t(order.status) || order.status}
                            </Badge>
                            <p className="text-sm font-semibold text-gold">${order.total_amount?.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedClient.orders && selectedClient.orders.length === 0 && (
                  <div className="text-center py-4 bg-tc-gold/5 rounded-lg border border-amber-500/20">
                    <Package className="w-8 h-8 text-muted mx-auto mb-2" />
                    <p className="text-secondary text-sm">{t('noOrdersYet')}</p>
                  </div>
                )}

                <div className="flex justify-end pt-4">
                  <Button 
                    onClick={() => setShowClientProfile(false)}
                    className="tc-button-secondary"
                  >
                    Cerrar
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('dashboard');

  return (
    <AppShell
      user={user}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      onLogout={logout}
      t={t}
      languageToggle={<LanguageToggle />}
    >
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">

          <TabsContent value="dashboard">
            {user?.role === 'tc_admin' ? (
              <ExecutiveDashboard user={user} setActiveTab={setActiveTab} />
            ) : user?.role === 'sales_rep' ? (
              <SalesDashboard user={user} setActiveTab={setActiveTab} />
            ) : (
              <DealerDashboard user={user} setActiveTab={setActiveTab} />
            )}
          </TabsContent>

          <TabsContent value="create-order">
            {(user?.role === 'tc_admin' || user?.role === 'sales_rep')
              ? <OrderBuilder user={user} onDone={() => setActiveTab('orders')} />
              : <DealerNewOrder user={user} setActiveTab={setActiveTab} />}
          </TabsContent>

          <TabsContent value="products">
            {user?.role === 'tc_admin'
              ? <ProductsInventory user={user} />
              : <ProductCatalog user={user} setActiveTab={setActiveTab} />}
          </TabsContent>

          <TabsContent value="software">
            <SoftwareCenter user={user} />
          </TabsContent>

          <TabsContent value="warranty">
            <WarrantyCenter user={user} />
          </TabsContent>

          {user?.role === 'sales_rep' && (
            <TabsContent value="my-clients">
              <MyClients />
            </TabsContent>
          )}

          {(user?.role === 'tc_admin' || user?.role === 'sales_rep') && (
            <>
              <TabsContent value="orders">
                <OrderQueue user={user} />
              </TabsContent>
            </>
          )}

          {(user?.role === 'installation_center' || user?.role === 'elite_partner') && (
            <TabsContent value="orders">
              <DealerOrders user={user} />
            </TabsContent>
          )}

          {user?.role === 'tc_admin' && (
            <>
              <TabsContent value="users">
                <UsersSection user={user} />
              </TabsContent>

              <TabsContent value="pricing">
                <PricingWorkspace />
              </TabsContent>
              
              <TabsContent value="serial-search">
                <SerialTraceability user={user} setActiveTab={setActiveTab} />
              </TabsContent>
              
              <TabsContent value="warranty-admin">
                <AdminWarrantyCenter />
              </TabsContent>
              
              <TabsContent value="email-center">
                <EmailCenter />
              </TabsContent>
              
              <TabsContent value="production-barcodes">
                <ProductionBarcodes user={user} setActiveTab={setActiveTab} />
              </TabsContent>
              <TabsContent value="roll-usage">
                <RollUsageSection />
              </TabsContent>
              <TabsContent value="coverage-rules">
                <CoverageRulesSection />
              </TabsContent>
            </>
          )}
        </Tabs>
    </AppShell>
  );
};


// Create Order Section (For Clients and Admin)
const CreateOrderSection = ({ user }) => {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [clients, setClients] = useState([]);  // For admin to select clients
  const [pricingTiers, setPricingTiers] = useState([]);  // Pricing tiers for discount calculation
  const [loading, setLoading] = useState(true);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [newOrder, setNewOrder] = useState({
    items: [{ product_id: '', quantity: 1 }],
    custom_items: [],  // Custom items for admin
    shipping_address: '',
    selected_address_id: '',
    special_instructions: '',
    customer_id: ''  // For admin orders
  });
  const [newAddress, setNewAddress] = useState({
    address_name: '',
    full_address: '',
    city: '',
    state: '',
    zip_code: '',
    is_default: false
  });
  const [newCustomItem, setNewCustomItem] = useState({
    name: '',
    price: '',
    quantity: 1,
    notes: ''
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchAddresses();
    fetchPricingTiers();  // Always fetch pricing tiers for discount calculation
    if (user?.role === 'tc_admin' || user?.role === 'sales_rep') {
      fetchClients();
    }
  }, [user]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/products`);
      // Handle both paginated and non-paginated responses
      const productsData = response.data.products || response.data;
      setProducts(productsData);
    } catch (error) {
      toast.error(t('errorLoadingProducts'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories');
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/addresses`);
      setAddresses(response.data);
    } catch (error) {
      console.error('Error loading addresses');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      const usersData = response.data.users || response.data;
      // Filter to only show non-admin users (clients) - excludes admins and sales reps
      const clientUsers = usersData.filter(u => u.role !== 'tc_admin' && u.role !== 'sales_rep');
      setClients(clientUsers);
    } catch (error) {
      console.error('Error loading clients');
    }
  };

  const fetchPricingTiers = async () => {
    try {
      // Use the public pricing endpoint that any authenticated user can access
      const response = await axios.get(`${API_BASE_URL}/api/pricing/all`);
      setPricingTiers(response.data);
    } catch (error) {
      console.error('Error loading pricing tiers');
    }
  };

  // Get discount percentage for selected customer
  const getCustomerDiscount = () => {
    const canPickCustomer = user?.role === 'tc_admin' || user?.role === 'sales_rep';
    if (canPickCustomer && newOrder.customer_id) {
      const customer = clients.find(c => c.user_id === newOrder.customer_id);
      if (customer) {
        const tier = pricingTiers.find(t => t.role === customer.role);
        return tier ? tier.discount_percentage : 0;
      }
    } else if (!canPickCustomer) {
      const tier = pricingTiers.find(t => t.role === user?.role);
      return tier ? tier.discount_percentage : 0;
    }
    return 0;
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_BASE_URL}/api/addresses`, newAddress);
      toast.success(t('addressSavedSuccess'));
      setShowAddAddress(false);
      setNewAddress({
        address_name: '',
        full_address: '',
        city: '',
        state: '',
        zip_code: '',
        is_default: false
      });
      fetchAddresses();
    } catch (error) {
      toast.error(t('errorSavingAddress'));
    }
  };

  const handleAddressSelect = (addressId) => {
    const selectedAddress = addresses.find(addr => addr.address_id === addressId);
    if (selectedAddress) {
      const fullAddressString = `${selectedAddress.full_address}, ${selectedAddress.city}, ${selectedAddress.state} ${selectedAddress.zip_code}`;
      setNewOrder({
        ...newOrder,
        selected_address_id: addressId,
        shipping_address: fullAddressString
      });
    }
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    
    const canPickCustomer = user?.role === 'tc_admin' || user?.role === 'sales_rep';
    // Validate for admin/sales_rep orders - must select a client
    if (canPickCustomer && !newOrder.customer_id) {
      toast.error(t('selectClientRequired'));
      return;
    }
    
    try {
      const orderData = {
        items: newOrder.items
          .filter(item => item.product_id)
          .map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            ...(typeof item.custom_price === 'number' && item.custom_price >= 0 ? { custom_price: item.custom_price } : {})
          })),
        shipping_address: newOrder.shipping_address,
        special_instructions: newOrder.special_instructions
      };
      
      // Add customer_id for admin/sales_rep orders
      if (canPickCustomer && newOrder.customer_id) {
        orderData.customer_id = newOrder.customer_id;
      }
      
      // Add custom items only for admin orders (sales reps cannot add custom items)
      if (user?.role === 'tc_admin' && newOrder.custom_items.length > 0) {
        orderData.custom_items = newOrder.custom_items;
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/orders`, orderData);
      toast.success(t('orderCreatedSuccess'));
      setNewOrder({
        items: [{ product_id: '', quantity: 1 }],
        custom_items: [],
        shipping_address: '',
        selected_address_id: '',
        special_instructions: '',
        customer_id: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorCreatingOrder'));
    }
  };

  const addOrderItem = () => {
    setNewOrder({
      ...newOrder,
      items: [...newOrder.items, { product_id: '', quantity: 1 }]
    });
  };

  const updateOrderItem = (index, field, value) => {
    const updatedItems = newOrder.items.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    setNewOrder({ ...newOrder, items: updatedItems });
  };

  const removeOrderItem = (index) => {
    const updatedItems = newOrder.items.filter((_, i) => i !== index);
    setNewOrder({ ...newOrder, items: updatedItems });
  };

  // Add custom item (admin only)
  const addCustomItem = () => {
    if (!newCustomItem.name || !newCustomItem.price) return;
    
    setNewOrder({
      ...newOrder,
      custom_items: [...newOrder.custom_items, {
        name: newCustomItem.name,
        price: parseFloat(newCustomItem.price),
        quantity: parseInt(newCustomItem.quantity) || 1,
        notes: newCustomItem.notes
      }]
    });
    
    setNewCustomItem({ name: '', price: '', quantity: 1, notes: '' });
  };

  // Remove custom item
  const removeCustomItem = (index) => {
    const updatedItems = newOrder.custom_items.filter((_, i) => i !== index);
    setNewOrder({ ...newOrder, custom_items: updatedItems });
  };

  // Calculate subtotal from catalog products (using base/list prices)
  const calculateSubtotal = () => {
    return newOrder.items.reduce((total, item) => {
      const product = products.find(p => p.product_id === item.product_id);
      return total + (product ? (product.base_price || product.price) * item.quantity : 0);
    }, 0);
  };

  // Effective catalog total: per-line override wins over the tier discount
  const calculateCatalogEffective = () => {
    const discountPercent = getCustomerDiscount();
    return newOrder.items.reduce((total, item) => {
      const product = products.find(p => p.product_id === item.product_id);
      if (!product) return total;
      const basePrice = product.base_price || product.price;
      const hasOverride = typeof item.custom_price === 'number' && item.custom_price >= 0;
      const eff = hasOverride ? item.custom_price : basePrice * (1 - discountPercent / 100);
      return total + eff * item.quantity;
    }, 0);
  };

  // Calculate discount amount (list subtotal - effective catalog total)
  const calculateDiscount = () => {
    return calculateSubtotal() - calculateCatalogEffective();
  };

  // Calculate custom items total (no discount applied)
  const calculateCustomItemsTotal = () => {
    return newOrder.custom_items.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  };

  // Calculate final total
  const calculateFinalTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const customTotal = calculateCustomItemsTotal();
    return (subtotal - discount) + customTotal;
  };

  // For backwards compatibility
  const calculateTotal = () => calculateSubtotal();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="tc-spinner"></div>
        <span className="ml-4 text-primary">{t('loadingProductsOrder')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-primary mb-2">
          {(user?.role === 'tc_admin' || user?.role === 'sales_rep') ? t('createOrderForClient') : t('createNewOrder')}
        </h2>
        <p className="text-secondary">
          {(user?.role === 'tc_admin' || user?.role === 'sales_rep') ? t('selectClientAndProducts') : t('selectProductsCompleteDetails')}
        </p>
      </div>

      {/* Pricing Tier Banner for Dealers */}
      {user?.role !== 'tc_admin' && user?.role !== 'sales_rep' && getCustomerDiscount() > 0 && (
        <div className="max-w-4xl mx-auto">
          <div className="bg-green-600/20 border border-green-500/50 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="w-6 h-6 text-green-400" />
              <div>
                <p className="text-green-400 font-semibold">
                  {(() => {
                    const tier = pricingTiers.find(t => t.role === user?.role);
                    return tier?.display_name || user?.role;
                  })()}
                </p>
                <p className="text-green-300 text-sm">{t('yourDiscountApplied')}</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold text-green-400">{getCustomerDiscount()}%</span>
              <p className="text-green-300 text-sm">{t('discount')}</p>
            </div>
          </div>
        </div>
      )}

      <Card className="tc-card max-w-4xl mx-auto">
        <CardContent className="tc-card-content">
          <form onSubmit={handleCreateOrder} className="space-y-8">
            {/* Client Selection - Admin & Sales Rep */}
            {(user?.role === 'tc_admin' || user?.role === 'sales_rep') && (
              <div>
                <h3 className="text-xl font-semibold text-primary mb-4">{t('selectClient')}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="tc-label">{t('client')} *</label>
                    <select 
                      className="tc-select tc-focus-ring"
                      value={newOrder.customer_id} 
                      onChange={(e) => setNewOrder({...newOrder, customer_id: e.target.value})}
                      required
                      data-testid="select-client"
                    >
                      <option value="">{t('selectClientOption')}</option>
                      {clients.map((client) => (
                        <option key={client.user_id} value={client.user_id}>
                          {client.full_name} - {client.company_name} ({client.role === 'elite_partner' ? 'Elite Partner' : 'Installation Center'})
                        </option>
                      ))}
                    </select>
                  </div>
                  {clients.length === 0 && (
                    <div className="text-amber-400 text-sm bg-amber-500/10 p-3 rounded-lg border border-amber-500/20">
                      {user?.role === 'sales_rep' ? t('noSalesRepClientsYet') : t('noClientsAvailable')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Products Selection */}
            <div>
              <h3 className="text-xl font-semibold text-primary mb-4">{t('productSelection')}</h3>
              <div className="space-y-4">
                {newOrder.items.map((item, index) => (
                  <Card key={index} className="tc-card">
                    <CardContent className="tc-card-content">
                      <div className={`grid ${user?.role === 'tc_admin' ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4 items-end`}>
                        <div className="md:col-span-2">
                          <label className="tc-label">{t('product')}</label>
                          <select 
                            className="tc-select tc-focus-ring"
                            value={item.product_id} 
                            onChange={(e) => updateOrderItem(index, 'product_id', e.target.value)}
                            required={newOrder.custom_items.length === 0}
                          >
                            <option value="">{t('selectProductOption')}</option>
                            {categories.map((category) => {
                              const categoryProducts = products.filter(p => p.category === category.name);
                              if (categoryProducts.length === 0) return null;
                              return (
                                <optgroup key={category.name} label={`── ${category.display_name} ──`}>
                                  {categoryProducts.map((product) => {
                                    const basePrice = product.base_price || product.price;
                                    const customerDiscountPercent = getCustomerDiscount();
                                    const discountedPrice = basePrice * (1 - customerDiscountPercent / 100);
                                    const hasDiscount = customerDiscountPercent > 0;
                                    return (
                                      <option key={product.product_id} value={product.product_id}>
                                        {product.name} - ${discountedPrice.toFixed(2)}
                                        {hasDiscount && ` (was $${basePrice.toFixed(2)})`}
                                        {user?.role === 'tc_admin' && ` | Stock: ${product.stock_quantity}`}
                                      </option>
                                    );
                                  })}
                                </optgroup>
                              );
                            })}
                          </select>
                        </div>
                        
                        <div>
                          <label className="tc-label">{t('quantity')}</label>
                          <input
                            className="tc-input tc-focus-ring"
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateOrderItem(index, 'quantity', parseInt(e.target.value))}
                            required
                          />
                        </div>

                        {user?.role === 'tc_admin' && (
                          <div>
                            <label className="tc-label">{t('customPriceLabel')}</label>
                            <input
                              className="tc-input tc-focus-ring"
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.custom_price ?? ''}
                              onChange={(e) => updateOrderItem(index, 'custom_price', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                              placeholder={(() => {
                                const p = products.find(pr => pr.product_id === item.product_id);
                                return p ? `${(p.base_price || p.price).toFixed(2)}` : '0.00';
                              })()}
                              data-testid={`order-item-custom-price-${index}`}
                            />
                          </div>
                        )}
                        
                        <div className="flex items-end justify-between">
                          {(() => {
                            const product = products.find(p => p.product_id === item.product_id);
                            if (!product) return null;
                            
                            // Calculate discount based on selected customer tier, not logged-in user
                            const basePrice = product.base_price || product.price;
                            const customerDiscountPercent = getCustomerDiscount();
                            const hasOverride = typeof item.custom_price === 'number' && item.custom_price >= 0;
                            const effectivePrice = hasOverride ? item.custom_price : basePrice * (1 - customerDiscountPercent / 100);
                            const hasDiscount = !hasOverride && customerDiscountPercent > 0;
                            const itemTotal = effectivePrice * item.quantity;
                            const originalTotal = basePrice * item.quantity;
                            
                            return (
                              <div className="text-right">
                                {(hasDiscount || hasOverride) && (
                                  <p className="text-muted text-xs line-through">${originalTotal.toFixed(2)}</p>
                                )}
                                {hasOverride && (
                                  <p className="text-amber-400 text-xs">{t('customPriceLabel')}</p>
                                )}
                                <p className="text-muted text-xs">{t('subtotalLabel')}</p>
                                <p className="text-gold font-bold">${itemTotal.toFixed(2)}</p>
                              </div>
                            );
                          })()}
                          
                          {newOrder.items.length > 1 && (
                            <Button 
                              type="button" 
                              onClick={() => removeOrderItem(index)}
                              className="tc-button-secondary ml-2"
                              size="sm"
                            >
                              {t('remove')}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                <Button 
                  type="button" 
                  onClick={addOrderItem}
                  className="tc-button-secondary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('addProduct')}
                </Button>
              </div>
            </div>

            {/* Custom Items Section - Admin Only */}
            {user?.role === 'tc_admin' && (
              <div>
                <h3 className="text-xl font-semibold text-primary mb-2">{t('customItems') || 'Custom Items'}</h3>
                <p className="text-sm text-muted mb-4">{t('customItemsDescription') || 'Add special items not in catalog. These items will NOT have tier discount applied.'}</p>
                
                <Card className="tc-card mb-4">
                  <CardContent className="tc-card-content">
                    <div className="grid md:grid-cols-5 gap-4 items-end">
                      <div className="md:col-span-2">
                        <label className="tc-label">{t('itemName') || 'Item Name'}</label>
                        <input
                          className="tc-input tc-focus-ring"
                          value={newCustomItem.name}
                          onChange={(e) => setNewCustomItem({...newCustomItem, name: e.target.value})}
                          placeholder={t('customItemNamePlaceholder') || 'e.g., Special PPF Kit'}
                        />
                      </div>
                      <div>
                        <label className="tc-label">{t('priceUSD')}</label>
                        <input
                          className="tc-input tc-focus-ring"
                          type="number"
                          step="0.01"
                          min="0"
                          value={newCustomItem.price}
                          onChange={(e) => setNewCustomItem({...newCustomItem, price: e.target.value})}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="tc-label">{t('quantity')}</label>
                        <input
                          className="tc-input tc-focus-ring"
                          type="number"
                          min="1"
                          value={newCustomItem.quantity}
                          onChange={(e) => setNewCustomItem({...newCustomItem, quantity: parseInt(e.target.value) || 1})}
                        />
                      </div>
                      <div>
                        <Button 
                          type="button" 
                          onClick={addCustomItem}
                          className="tc-button-secondary w-full"
                          disabled={!newCustomItem.name || !newCustomItem.price}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          {t('add') || 'Add'}
                        </Button>
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="tc-label">{t('notes')} ({t('optional') || 'Optional'})</label>
                      <input
                        className="tc-input tc-focus-ring"
                        value={newCustomItem.notes}
                        onChange={(e) => setNewCustomItem({...newCustomItem, notes: e.target.value})}
                        placeholder={t('customItemNotesPlaceholder') || 'e.g., Special order for customer'}
                      />
                    </div>
                  </CardContent>
                </Card>
                
                {/* Display added custom items */}
                {newOrder.custom_items.length > 0 && (
                  <div className="space-y-2">
                    {newOrder.custom_items.map((item, index) => (
                      <div key={index} className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                        <div>
                          <span className="text-primary font-medium">{item.quantity}x {item.name}</span>
                          <span className="text-amber-400 ml-2">@ ${item.price.toFixed(2)}</span>
                          {item.notes && <p className="text-xs text-muted">{item.notes}</p>}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-gold font-bold">${(item.price * item.quantity).toFixed(2)}</span>
                          <Button
                            type="button"
                            onClick={() => removeCustomItem(index)}
                            className="tc-button-danger"
                            size="sm"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Shipping Information */}
            <div>
              <h3 className="text-xl font-semibold text-primary mb-4">{t('shippingInfo')}</h3>
              <div className="space-y-4">
                {addresses.length > 0 && (
                  <div>
                    <label className="tc-label">{t('savedAddresses')}</label>
                    <select 
                      className="tc-select tc-focus-ring"
                      value={newOrder.selected_address_id} 
                      onChange={(e) => handleAddressSelect(e.target.value)}
                    >
                      <option value="">{t('selectSavedAddress')}</option>
                      {addresses.map((address) => (
                        <option key={address.address_id} value={address.address_id}>
                          {address.address_name} - {address.city}, {address.state}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    onClick={() => setShowAddAddress(true)}
                    className="tc-button-secondary"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('addNewAddress')}
                  </Button>
                </div>

                <div>
                  <label className="tc-label">{t('fullShippingAddress')}</label>
                  <textarea
                    className="tc-textarea tc-focus-ring"
                    value={newOrder.shipping_address}
                    onChange={(e) => setNewOrder({...newOrder, shipping_address: e.target.value})}
                    placeholder={t('includeNameAddressCityStateZip')}
                    rows={4}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Special Instructions */}
            <div>
              <h3 className="text-xl font-semibold text-primary mb-4">{t('specialInstructions')}</h3>
              <textarea
                className="tc-textarea tc-focus-ring"
                value={newOrder.special_instructions}
                onChange={(e) => setNewOrder({...newOrder, special_instructions: e.target.value})}
                placeholder={t('anySpecialInstructions')}
                rows={3}
              />
            </div>

            {/* Order Summary */}
            <Card className="tc-card">
              <CardContent className="tc-card-content">
                <h3 className="text-xl font-semibold text-primary mb-4">{t('orderSummary')}</h3>
                
                {/* Catalog Products */}
                <div className="space-y-3 mb-4">
                  {newOrder.items.map((item, index) => {
                    const product = products.find(p => p.product_id === item.product_id);
                    if (!product) return null;
                    
                    // Always use base_price and calculate discount based on selected customer's tier
                    const basePrice = product.base_price || product.price;
                    const customerDiscountPercent = getCustomerDiscount();
                    const hasOverride = typeof item.custom_price === 'number' && item.custom_price >= 0;
                    const effectivePrice = hasOverride ? item.custom_price : basePrice * (1 - customerDiscountPercent / 100);
                    const hasDiscount = !hasOverride && customerDiscountPercent > 0;
                    const originalTotal = basePrice * item.quantity;
                    const discountedTotal = effectivePrice * item.quantity;
                    const itemDiscount = originalTotal - discountedTotal;
                    
                    return (
                      <div key={index} className="bg-secondary/10 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="text-primary font-medium">{item.quantity}x {product.name}</span>
                            <p className="text-xs text-muted">@ ${(hasOverride ? effectivePrice : basePrice).toFixed(2)} each{hasOverride ? ` (${t('customPriceLabel')})` : ''}</p>
                          </div>
                          <div className="text-right">
                            {hasDiscount && (
                              <>
                                <p className="text-muted text-xs line-through">${originalTotal.toFixed(2)}</p>
                                <p className="text-green-400 text-xs">-${itemDiscount.toFixed(2)} ({customerDiscountPercent}%)</p>
                              </>
                            )}
                            {hasOverride && (
                              <p className="text-amber-400 text-xs line-through">${originalTotal.toFixed(2)}</p>
                            )}
                            <p className="text-gold font-bold">${discountedTotal.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Custom Items (if any) */}
                {newOrder.custom_items.length > 0 && (
                  <>
                    <hr className="tc-separator" />
                    <p className="text-sm text-amber-400 font-medium mb-2">{t('customItems') || 'Custom Items'} ({t('noDiscountApplied') || 'No discount applied'})</p>
                    <div className="space-y-2 mb-4">
                      {newOrder.custom_items.map((item, index) => (
                        <div key={index} className="flex justify-between items-center bg-amber-500/10 rounded-lg p-2">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => removeCustomItem(index)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <span className="text-secondary">
                              {item.quantity}x {item.name}
                              {item.notes && <span className="text-xs text-muted ml-1">({item.notes})</span>}
                            </span>
                          </div>
                          <span className="text-amber-400 font-medium">
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                
                <hr className="tc-separator" />
                
                {/* Show subtotal (original prices) */}
                <div className="flex justify-between items-center text-sm">
                  <span className="text-secondary">{t('subtotalLabel')} ({t('catalogProducts') || 'Catalog Products'}):</span>
                  <span className="text-primary">${calculateSubtotal().toFixed(2)}</span>
                </div>
                
                {/* Show discount if applicable */}
                {getCustomerDiscount() > 0 && (
                  <div className="flex justify-between items-center text-sm text-green-400">
                    <span>{t('tierDiscount') || 'Tier Discount'} ({getCustomerDiscount()}%):</span>
                    <span>-${calculateDiscount().toFixed(2)}</span>
                  </div>
                )}
                
                {/* Show custom items total if any */}
                {newOrder.custom_items.length > 0 && (
                  <div className="flex justify-between items-center text-sm text-amber-400">
                    <span>{t('customItems') || 'Custom Items'}:</span>
                    <span>+${calculateCustomItemsTotal().toFixed(2)}</span>
                  </div>
                )}
                
                <hr className="tc-separator" />
                
                {/* Final total */}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-primary">{t('total')}:</span>
                  <span className="text-2xl font-bold text-gold">
                    ${calculateFinalTotal().toFixed(2)}
                  </span>
                </div>
                
                {/* Show pricing tier info */}
                {getCustomerDiscount() > 0 && (
                  <p className="text-xs text-green-400 text-right">
                    {(() => {
                      const customer = user?.role === 'tc_admin' 
                        ? clients.find(c => c.user_id === newOrder.customer_id)
                        : user;
                      const tier = pricingTiers.find(t => t.role === customer?.role);
                      return tier ? `${tier.display_name} - ${tier.discount_percentage}% ${t('discount')}` : '';
                    })()}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="text-center">
              <Button 
                type="submit" 
                className="tc-button-primary px-8 py-3 text-lg"
                disabled={(!newOrder.items[0]?.product_id && newOrder.custom_items.length === 0) || !newOrder.shipping_address}
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {t('createOrderButton')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      
      {/* Add Address Dialog */}
      <Dialog open={showAddAddress} onOpenChange={setShowAddAddress}>
        <DialogContent className="tc-dialog-content">
          <div className="tc-dialog-header">
            <DialogTitle className="tc-dialog-title">Agregar Nueva Dirección</DialogTitle>
            <DialogDescription className="tc-dialog-description">
              Guarda esta dirección para uso futuro en tus órdenes
            </DialogDescription>
          </div>
          <div className="tc-dialog-body">
            <form onSubmit={handleAddAddress} className="space-y-4">
              <div>
                <label className="tc-label">Nombre de la Dirección</label>
                <input
                  className="tc-input tc-focus-ring"
                  value={newAddress.address_name}
                  onChange={(e) => setNewAddress({...newAddress, address_name: e.target.value})}
                  placeholder="Ej: Taller Principal, Almacén, Casa"
                  required
                />
              </div>
              
              <div>
                <label className="tc-label">Dirección Completa</label>
                <input
                  className="tc-input tc-focus-ring"
                  value={newAddress.full_address}
                  onChange={(e) => setNewAddress({...newAddress, full_address: e.target.value})}
                  placeholder="123 Main Street"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="tc-label">Ciudad</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={newAddress.city}
                    onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                    placeholder="Austin"
                    required
                  />
                </div>
                <div>
                  <label className="tc-label">Estado</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={newAddress.state}
                    onChange={(e) => setNewAddress({...newAddress, state: e.target.value})}
                    placeholder="TX"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="tc-label">Código Postal</label>
                <input
                  className="tc-input tc-focus-ring"
                  value={newAddress.zip_code}
                  onChange={(e) => setNewAddress({...newAddress, zip_code: e.target.value})}
                  placeholder="78701"
                  required
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={newAddress.is_default}
                  onChange={(e) => setNewAddress({...newAddress, is_default: e.target.checked})}
                  className="tc-checkbox"
                />
                <label htmlFor="is_default" className="text-sm text-secondary">
                  Establecer como dirección predeterminada
                </label>
              </div>

              <div className="flex gap-4 pt-4">
                <Button 
                  type="button" 
                  onClick={() => setShowAddAddress(false)}
                  className="tc-button-secondary flex-1"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="tc-button-primary flex-1">
                  Guardar Dirección
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Orders Management Section (For Admins)
const OrdersManagementSection = ({ user }) => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('new');
  const [paymentFilter, setPaymentFilter] = useState('all'); // all, paid, pending
  const [showEditOrder, setShowEditOrder] = useState(false);
  const [showEditItems, setShowEditItems] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingItems, setEditingItems] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [orderCategories, setOrderCategories] = useState([]);
  const [statusUpdate, setStatusUpdate] = useState({
    status: '',
    shipping_tracking: '',
    notes: '',
    serial_numbers: []
  });
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [discountOrder, setDiscountOrder] = useState(null);
  const [newDiscountPercent, setNewDiscountPercent] = useState(0);
  const [showStockReturnDialog, setShowStockReturnDialog] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [removedItemsForStock, setRemovedItemsForStock] = useState([]); // Track items to return to stock

  useEffect(() => {
    fetchOrders();
    fetchProducts();
    fetchOrderCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/products`);
      // Handle both paginated and non-paginated responses
      const productsData = response.data.products || response.data;
      setAvailableProducts(productsData);
    } catch (error) {
      console.error('Error loading products for order edit');
    }
  };

  const fetchOrderCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/categories`);
      setOrderCategories(response.data);
    } catch (error) {
      console.error('Error loading categories for order edit');
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/orders`);
      // Handle both paginated and non-paginated responses
      const ordersData = response.data.orders || response.data;
      setOrders(ordersData);
    } catch (error) {
      toast.error('Error al cargar órdenes');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || searchQuery.length < 2) {
      toast.error('Ingresa al menos 2 caracteres para buscar');
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/orders/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Error al buscar órdenes');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const updatePaymentStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API_BASE_URL}/api/orders/${orderId}/payment-status?payment_status=${newStatus}`);
      toast.success(t('paymentStatusUpdated'));
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating payment status');
    }
  };

  const startDiscountEdit = (order) => {
    setDiscountOrder(order);
    setNewDiscountPercent(order.discount_percentage || 0);
    setShowDiscountDialog(true);
  };

  const saveDiscount = async () => {
    if (!discountOrder) return;
    
    try {
      await axios.put(`${API_BASE_URL}/api/orders/${discountOrder.order_id}/discount`, {
        discount_percentage: parseFloat(newDiscountPercent)
      });
      toast.success(t('discountUpdated'));
      setShowDiscountDialog(false);
      setDiscountOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating discount');
    }
  };

  const calculatePreviewTotal = () => {
    if (!discountOrder) return 0;
    const subtotal = discountOrder.subtotal || discountOrder.total_amount || 0;
    const customTotal = discountOrder.custom_items_total || 0;
    const discountAmount = subtotal * (newDiscountPercent / 100);
    return (subtotal - discountAmount) + customTotal;
  };

  const filterOrdersByPayment = (ordersList) => {
    if (paymentFilter === 'all') return ordersList;
    if (paymentFilter === 'paid') return ordersList.filter(o => o.payment_status === 'paid');
    if (paymentFilter === 'pending') return ordersList.filter(o => o.payment_status !== 'paid');
    return ordersList;
  };

  const filterOrdersByStatus = (status) => {
    switch (status) {
      case 'new':
        return filterOrdersByPayment(orders.filter(order => order.status === 'pending')).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'processing':
        return filterOrdersByPayment(orders.filter(order => ['processing', 'shipped'].includes(order.status))).sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
      case 'completed':
        return filterOrdersByPayment(orders.filter(order => ['delivered', 'completed'].includes(order.status))).sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
      default:
        return filterOrdersByPayment(orders).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
  };

  const getOrderCounts = () => {
    const filteredOrders = filterOrdersByPayment(orders);
    return {
      new: filteredOrders.filter(order => order.status === 'pending').length,
      processing: filteredOrders.filter(order => ['processing', 'shipped'].includes(order.status)).length,
      completed: filteredOrders.filter(order => ['delivered', 'completed'].includes(order.status)).length
    };
  };

  const getPaymentCounts = () => {
    return {
      all: orders.length,
      paid: orders.filter(o => o.payment_status === 'paid').length,
      pending: orders.filter(o => o.payment_status !== 'paid').length
    };
  };

  const startStatusUpdate = (order) => {
    setSelectedOrder(order);
    setStatusUpdate({
      status: order.status,
      shipping_tracking: order.shipping_tracking || '',
      notes: order.admin_notes || '',
      serial_numbers: []
    });
    setShowEditOrder(true);
  };

  const addSerialNumber = () => {
    setStatusUpdate({
      ...statusUpdate,
      serial_numbers: [
        ...statusUpdate.serial_numbers,
        { serial_number: '', product_id: '', notes: '' }
      ]
    });
  };

  const updateSerialNumber = (index, field, value) => {
    const updated = [...statusUpdate.serial_numbers];
    updated[index][field] = value;
    setStatusUpdate({
      ...statusUpdate,
      serial_numbers: updated
    });
  };

  const removeSerialNumber = (index) => {
    const updated = statusUpdate.serial_numbers.filter((_, i) => i !== index);
    setStatusUpdate({
      ...statusUpdate,
      serial_numbers: updated
    });
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_BASE_URL}/api/orders/${selectedOrder.order_id}/status`, statusUpdate);
      toast.success('Estado de orden actualizado exitosamente');
      setShowEditOrder(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar orden');
    }
  };

  const deleteOrder = async (orderId, orderNumber) => {
    if (window.confirm(`¿Está seguro que desea eliminar la orden #${orderNumber}? Esta acción no se puede deshacer.`)) {
      try {
        await axios.delete(`${API_BASE_URL}/api/orders/${orderId}`);
        toast.success('Orden eliminada exitosamente');
        fetchOrders();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al eliminar orden');
      }
    }
  };

  const startOrderEdit = (order) => {
    setSelectedOrder(order);
    setStatusUpdate({
      status: order.status,
      shipping_tracking: order.shipping_tracking || '',
      notes: order.admin_notes || '',
      serial_numbers: []
    });
    setShowEditOrder(true);
  };

  const startItemsEdit = (order) => {
    setSelectedOrder(order);
    // Create a copy of items for editing. Use the effective (charged) price so the
    // admin sees and edits the real price; overrides/discounts are preserved.
    const itemsCopy = order.items.map(item => ({
      product_id: item.product_id,
      product_name: item.product_name,
      quantity: item.quantity,
      price: item.effective_unit_price ?? item.price,
      total: (item.effective_unit_price ?? item.price) * item.quantity,
      serial_numbers: item.serial_numbers || [],
      is_custom: item.is_custom || false,
      custom_notes: item.custom_notes || ''
    }));
    setEditingItems(itemsCopy);
    setRemovedItemsForStock([]); // Reset removed items tracking
    setShowEditItems(true);
  };

  const updateItemPrice = (index, newPrice) => {
    const price = Math.max(0, parseFloat(newPrice) || 0);
    const updatedItems = [...editingItems];
    updatedItems[index] = {
      ...updatedItems[index],
      price,
      total: price * updatedItems[index].quantity
    };
    setEditingItems(updatedItems);
  };

  const updateItemQuantity = (index, newQuantity) => {
    const qty = Math.max(1, parseInt(newQuantity) || 1);
    const updatedItems = [...editingItems];
    updatedItems[index] = {
      ...updatedItems[index],
      quantity: qty,
      total: updatedItems[index].price * qty
    };
    setEditingItems(updatedItems);
  };

  const removeItemFromOrder = (index) => {
    if (editingItems.length <= 1) {
      toast.error(t('error') + ': Cannot remove all items from order');
      return;
    }
    // Store the item and index, then show confirmation dialog
    const item = editingItems[index];
    // Skip dialog for custom items (they don't have stock)
    if (item.is_custom || item.product_id?.startsWith('custom_')) {
      const updatedItems = editingItems.filter((_, i) => i !== index);
      setEditingItems(updatedItems);
      return;
    }
    setItemToRemove({ index, item });
    setShowStockReturnDialog(true);
  };

  const handleStockReturnConfirm = (returnToStock) => {
    if (!itemToRemove) return;
    
    const { index, item } = itemToRemove;
    
    if (returnToStock) {
      // Add to list of items to return to stock when saving
      setRemovedItemsForStock([...removedItemsForStock, {
        product_id: item.product_id,
        quantity: item.quantity
      }]);
    }
    
    // Remove the item from the editing list
    const updatedItems = editingItems.filter((_, i) => i !== index);
    setEditingItems(updatedItems);
    
    // Close dialog and reset
    setShowStockReturnDialog(false);
    setItemToRemove(null);
  };

  const cancelStockReturn = () => {
    setShowStockReturnDialog(false);
    setItemToRemove(null);
  };

  const addItemToOrder = (productId) => {
    const product = availableProducts.find(p => p.product_id === productId);
    if (!product) return;
    
    // Check if product already exists in order
    const existingIndex = editingItems.findIndex(item => item.product_id === productId);
    if (existingIndex >= 0) {
      // Increment quantity if already exists
      updateItemQuantity(existingIndex, editingItems[existingIndex].quantity + 1);
      return;
    }
    
    // Add new item
    const newItem = {
      product_id: product.product_id,
      product_name: product.name,
      quantity: 1,
      price: product.price,
      total: product.price,
      serial_numbers: []
    };
    setEditingItems([...editingItems, newItem]);
  };

  const calculateEditedTotal = () => {
    return editingItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const handleSaveItems = async () => {
    if (editingItems.length === 0) {
      toast.error(t('error') + ': Order must have at least one item');
      return;
    }

    try {
      await axios.put(`${API_BASE_URL}/api/orders/${selectedOrder.order_id}`, {
        items: editingItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          serial_numbers: item.serial_numbers,
          custom_price: item.price,
          is_custom: item.is_custom || false,
          product_name: item.product_name,
          notes: item.custom_notes || ''
        })),
        return_to_stock: removedItemsForStock // Items to return to stock
      });
      toast.success(t('orderStatusUpdated'));
      setShowEditItems(false);
      setSelectedOrder(null);
      setEditingItems([]);
      setRemovedItemsForStock([]); // Reset
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorUpdatingOrder'));
    }
  };

  const downloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/orders/${orderId}/invoice`);
      const pdfBase64 = response.data.invoice_pdf;
      downloadPDF(pdfBase64, `invoice-${orderId.substring(0, 8)}.pdf`);
      toast.success('Invoice descargado exitosamente');
    } catch (error) {
      toast.error('Error al descargar invoice');
    }
  };

  const downloadPackingSlip = async (orderId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/orders/${orderId}/packing-slip`);
      const pdfBase64 = response.data.packing_slip_pdf;
      downloadPDF(pdfBase64, `packing-slip-${orderId.substring(0, 8)}.pdf`);
      toast.success('Packing slip descargado exitosamente');
    } catch (error) {
      toast.error('Error al descargar packing slip');
    }
  };

  const downloadPDF = (pdfBase64, filename) => {
    const byteCharacters = atob(pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: t("pending"), class: 'tc-badge-warning' },
      'processing': { label: t("processing"), class: 'tc-badge-info' },
      'shipped': { label: t("shipped"), class: 'tc-badge-info' },
      'delivered': { label: t("delivered"), class: 'tc-badge-success' },
      'completed': { label: t("completed"), class: 'tc-badge-success' }
    };
    
    const statusInfo = statusMap[status] || { label: status, class: 'tc-badge-warning' };
    return <Badge className={`tc-badge ${statusInfo.class}`}>{statusInfo.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="tc-spinner"></div>
        <span className="ml-4 text-primary">{t('loadingOrders')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-primary mb-2">{t('ordersManagementTitle')}</h2>
          <p className="text-secondary">{t('manageAllOrders')}</p>
        </div>
        <Badge className="tc-badge tc-badge-info">{orders.length} {t('totalLabel2')}</Badge>
      </div>

      {/* Order Search */}
      <Card className="tc-card">
        <CardContent className="tc-card-content">
          <h3 className="text-lg font-semibold text-primary mb-4">{t('searchOrdersAdmin')}</h3>
          <form onSubmit={handleOrderSearch} className="flex gap-4 items-end">
            <div className="flex-1">
              <input
                className="tc-input tc-focus-ring"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('searchByOrderClientCompany')}
              />
            </div>
            <Button 
              type="submit" 
              disabled={searching}
              className="tc-button-primary"
            >
              {searching ? t('searching') : t('search')}
            </Button>
            {searchResults !== null && (
              <Button 
                type="button" 
                onClick={clearSearch}
                className="tc-button-secondary"
              >
                {t('clear')}
              </Button>
            )}
          </form>
          
          {searchResults !== null && (
            <div className="mt-6">
              <h4 className="text-md font-medium text-primary mb-3">
                {t('searchResults')} ({searchResults.length})
              </h4>
              {searchResults.length === 0 ? (
                <p className="text-secondary">{t('noOrdersFoundCriteria')}</p>
              ) : (
                <div className="space-y-3">
                  {searchResults.map((order) => (
                    <div key={order.order_id} className="flex items-center justify-between p-4 bg-tc-gold/5 rounded-lg border border-amber-500/20">
                      <div className="flex items-center gap-4">
                        <Hash className="w-5 h-5 text-gold" />
                        <div>
                          <p className="text-primary font-medium">#{order.order_id.substring(0, 8)}</p>
                          <p className="text-muted text-sm">{order.customer_name} - {order.customer_company}</p>
                          <p className="text-muted text-xs">{new Date(order.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(order.status)}
                        <div className="text-right">
                          {order.discount_percentage > 0 && (
                            <p className="text-xs text-green-400">-{order.discount_percentage}% {t('discount')}</p>
                          )}
                          <p className="text-gold font-semibold">${order.total_amount.toFixed(2)}</p>
                        </div>
                        <Button
                          onClick={() => startStatusUpdate(order)}
                          size="sm"
                          className="tc-button-primary"
                        >
                          <Settings className="w-3 h-3 mr-1" />
                          {t('manage')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Status Tabs */}
      <div className="space-y-6">
        {/* Payment Filter */}
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-secondary text-sm mr-2">{t('paymentStatus')}:</span>
          {[
            { id: 'all', label: t('allPayments'), count: getPaymentCounts().all },
            { id: 'pending', label: t('unpaidOrders'), count: getPaymentCounts().pending },
            { id: 'paid', label: t('paidOrders'), count: getPaymentCounts().paid }
          ].map((filter) => (
            <button
              key={filter.id}
              onClick={() => setPaymentFilter(filter.id)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                paymentFilter === filter.id
                  ? filter.id === 'paid' 
                    ? 'bg-green-600 text-white' 
                    : filter.id === 'pending'
                    ? 'bg-red-600 text-white'
                    : 'bg-gold text-black'
                  : 'bg-tc-gold/10 text-secondary hover:text-primary'
              }`}
            >
              {filter.label} ({filter.count})
            </button>
          ))}
        </div>

        {/* Status Tabs */}
        <div className="flex gap-2 border-b border-amber-500/20">
          {[
            { id: 'new', label: t('newOrders'), count: getOrderCounts().new },
            { id: 'processing', label: t('inProcess'), count: getOrderCounts().processing },
            { id: 'completed', label: t('completedTab'), count: getOrderCounts().completed }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'text-gold border-gold'
                  : 'text-secondary border-transparent hover:text-primary'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Orders List */}
        <div className="space-y-4">
          {filterOrdersByStatus(activeTab).length === 0 ? (
            <Card className="tc-card">
              <CardContent className="tc-card-content text-center py-8">
                <Package className="w-12 h-12 text-muted mx-auto mb-4" />
                <p className="text-secondary">{t('noOrdersInCategory')}</p>
              </CardContent>
            </Card>
          ) : (
            filterOrdersByStatus(activeTab).map((order) => (
              <Card key={order.order_id} className="tc-card hover-lift">
                <CardContent className="tc-card-content p-6">
                  {/* Header Row: Order Info + Total */}
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-4">
                    {/* Left: Order Info */}
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-gold to-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Hash className="w-6 h-6 text-black" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-primary">
                          {t('orderNumber')}{order.order_id.substring(0, 8)}
                        </h3>
                        <p className="text-sm text-secondary">
                          {order.customer_name} • {order.customer_company}
                        </p>
                        <p className="text-xs text-muted">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    {/* Right: Status Badges + Total */}
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        {getStatusBadge(order.status)}
                        <Badge className={`tc-badge ${order.payment_status === 'paid' ? 'bg-green-600' : 'bg-red-600'}`}>
                          {order.payment_status === 'paid' ? t('paymentPaid') : t('paymentPending')}
                        </Badge>
                        {order.discount_percentage > 0 && (
                          <Badge className="tc-badge bg-purple-600">
                            -{order.discount_percentage}%
                          </Badge>
                        )}
                      </div>
                      <div className="text-right">
                        {order.discount_percentage > 0 && order.subtotal && (
                          <p className="text-sm text-muted line-through">${order.subtotal?.toFixed(2)}</p>
                        )}
                        <span className="text-2xl font-bold text-gold">
                          ${order.total_amount?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Divider */}
                  <div className="border-t border-amber-500/20 my-4"></div>
                  
                  {/* Action Buttons Row */}
                  <div className="flex flex-wrap gap-2 justify-between items-center">
                    {/* Primary Actions */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => updatePaymentStatus(order.order_id, order.payment_status === 'paid' ? 'pending' : 'paid')}
                        size="sm"
                        className={`${order.payment_status === 'paid' ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'}`}
                        data-testid={`toggle-payment-${order.order_id.substring(0, 8)}`}
                      >
                        <DollarSign className="w-4 h-4 mr-1" />
                        {order.payment_status === 'paid' ? t('markAsPending') : t('markAsPaid')}
                      </Button>
                      <Button
                        onClick={() => startStatusUpdate(order)}
                        size="sm"
                        className="tc-button-primary"
                        data-testid={`manage-status-${order.order_id.substring(0, 8)}`}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        {t('statusLabel')}
                      </Button>
                      <Button
                        onClick={() => startItemsEdit(order)}
                        size="sm"
                        className="tc-button-secondary"
                        data-testid={`edit-items-${order.order_id.substring(0, 8)}`}
                      >
                        <ShoppingCart className="w-4 h-4 mr-1" />
                        {t('editItems')}
                      </Button>
                      <Button
                        onClick={() => startDiscountEdit(order)}
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid={`edit-discount-${order.order_id.substring(0, 8)}`}
                      >
                        <DollarSign className="w-4 h-4 mr-1" />
                        {order.discount_percentage > 0 ? t('editDiscount') : t('applyDiscount')}
                      </Button>
                    </div>
                    
                    {/* Secondary Actions */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => downloadInvoice(order.order_id)}
                        size="sm"
                        variant="outline"
                        className="border-amber-500/30 text-primary hover:bg-amber-500/10"
                        data-testid={`download-invoice-${order.order_id.substring(0, 8)}`}
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        {t('downloadInvoice')}
                      </Button>
                      <Button
                        onClick={() => downloadPackingSlip(order.order_id)}
                        size="sm"
                        variant="outline"
                        className="border-amber-500/30 text-primary hover:bg-amber-500/10"
                        data-testid={`download-packing-${order.order_id.substring(0, 8)}`}
                      >
                        <Package className="w-4 h-4 mr-1" />
                        {t('downloadPacking')}
                      </Button>
                      <Button
                        onClick={() => deleteOrder(order.order_id, order.order_id.substring(0, 8))}
                        size="sm"
                        variant="outline"
                        className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                        data-testid={`delete-order-${order.order_id.substring(0, 8)}`}
                      >
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Order Status Update Dialog */}
      <Dialog open={showEditOrder} onOpenChange={setShowEditOrder}>
        <DialogContent className="tc-dialog-content max-w-3xl">
          <div className="tc-dialog-header">
            <DialogTitle className="tc-dialog-title">
              {t('manageOrderNumber')}{selectedOrder?.order_id?.substring(0, 8)}
            </DialogTitle>
            <DialogDescription className="tc-dialog-description">
              {t('updateStatusAddSerials')}
            </DialogDescription>
          </div>
          <div className="tc-dialog-body">
            <form onSubmit={handleStatusUpdate} className="space-y-6">
              {/* Order Info */}
              <div className="bg-tc-gold/5 p-4 rounded-lg border border-amber-500/20">
                <h4 className="font-semibold text-primary mb-2">{t('orderInfo')}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted">{t('client')}:</span>
                    <p className="text-primary font-medium">{selectedOrder?.customer_name}</p>
                  </div>
                  <div>
                    <span className="text-muted">{t('companyLabel')}:</span>
                    <p className="text-primary font-medium">{selectedOrder?.customer_company}</p>
                  </div>
                  <div>
                    <span className="text-muted">{t('total')}:</span>
                    <p className="text-gold font-bold">${selectedOrder?.total_amount?.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="text-muted">{t('created')}:</span>
                    <p className="text-primary font-medium">{new Date(selectedOrder?.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Status Update */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="tc-label">{t('orderStatus')}</label>
                  <select 
                    className="tc-select tc-focus-ring"
                    value={statusUpdate.status} 
                    onChange={(e) => setStatusUpdate({...statusUpdate, status: e.target.value})}
                    required
                  >
                    <option value="pending">{t('pending')}</option>
                    <option value="processing">{t('processing')}</option>
                    <option value="shipped">{t('shipped')}</option>
                    <option value="delivered">{t('delivered')}</option>
                    <option value="completed">{t('completed')}</option>
                  </select>
                </div>
                
                <div>
                  <label className="tc-label">{t('trackingNumber')}</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={statusUpdate.shipping_tracking}
                    onChange={(e) => setStatusUpdate({...statusUpdate, shipping_tracking: e.target.value})}
                    placeholder={t('optionalTracking')}
                  />
                </div>
              </div>

              {/* Serial Numbers Section */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="text-lg font-semibold text-primary">{t('serialNumbers')}</h4>
                  <Button
                    type="button"
                    onClick={addSerialNumber}
                    className="tc-button-secondary"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('addSerial')}
                  </Button>
                </div>
                
                {statusUpdate.serial_numbers.map((serial, index) => (
                  <div key={index} className="flex gap-3 items-end p-3 bg-tc-gold/5 rounded-lg border border-amber-500/20">
                    <div className="flex-1">
                      <label className="tc-label text-xs">{t('serialNumberLabel')}</label>
                      <input
                        className="tc-input tc-focus-ring text-sm"
                        value={serial.serial_number}
                        onChange={(e) => updateSerialNumber(index, 'serial_number', e.target.value)}
                        placeholder="TC-PPF-001234"
                        required
                      />
                    </div>
                    <div className="flex-1">
                      <label className="tc-label text-xs">{t('product')}</label>
                      <select
                        className="tc-select tc-focus-ring text-sm"
                        value={serial.product_id}
                        onChange={(e) => updateSerialNumber(index, 'product_id', e.target.value)}
                        required
                      >
                        <option value="">{t('selectProductSerial')}</option>
                        {selectedOrder?.items?.map((item) => (
                          <option key={item.product_id} value={item.product_id}>
                            {item.product_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="tc-label text-xs">{t('notes')}</label>
                      <input
                        className="tc-input tc-focus-ring text-sm"
                        value={serial.notes}
                        onChange={(e) => updateSerialNumber(index, 'notes', e.target.value)}
                        placeholder={t('optional')}
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={() => removeSerialNumber(index)}
                      className="text-red-600 hover:text-red-700"
                      size="sm"
                      variant="ghost"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Admin Notes */}
              <div>
                <label className="tc-label">{t('adminNotes')}</label>
                <textarea
                  className="tc-textarea tc-focus-ring"
                  value={statusUpdate.notes}
                  onChange={(e) => setStatusUpdate({...statusUpdate, notes: e.target.value})}
                  placeholder={t('internalOrderNotes')}
                  rows={3}
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button 
                  type="button" 
                  onClick={() => setShowEditOrder(false)}
                  className="tc-button-secondary flex-1"
                >
                  {t('cancel')}
                </Button>
                <Button type="submit" className="tc-button-primary flex-1">
                  {t('updateOrder')}
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Order Items Dialog */}
      <Dialog open={showEditItems} onOpenChange={setShowEditItems}>
        <DialogContent className="tc-dialog-content max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="tc-dialog-header">
            <DialogTitle className="tc-dialog-title">
              {t('editOrderItems')} - #{selectedOrder?.order_id?.substring(0, 8)}
            </DialogTitle>
            <DialogDescription className="tc-dialog-description">
              {t('editItemsDescription')}
            </DialogDescription>
          </div>
          <div className="tc-dialog-body space-y-6">
            {/* Order Info Summary */}
            <div className="bg-tc-gold/5 p-4 rounded-lg border border-amber-500/20">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted">{t('client')}:</span>
                  <p className="text-primary font-medium">{selectedOrder?.customer_name}</p>
                </div>
                <div>
                  <span className="text-muted">{t('companyLabel')}:</span>
                  <p className="text-primary font-medium">{selectedOrder?.customer_company}</p>
                </div>
                <div>
                  <span className="text-muted">{t('orderDate')}:</span>
                  <p className="text-primary font-medium">{selectedOrder?.created_at ? new Date(selectedOrder.created_at).toLocaleDateString() : ''}</p>
                </div>
              </div>
            </div>

            {/* Current Items Section */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-primary">{t('currentItems')}</h4>
              
              {editingItems.length === 0 ? (
                <div className="text-center py-8 text-secondary">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>{t('minOneItem')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {editingItems.map((item, index) => (
                    <div 
                      key={`${item.product_id}-${index}`} 
                      className="flex items-center justify-between p-4 bg-tc-gold/5 rounded-lg border border-amber-500/20"
                      data-testid={`edit-item-row-${index}`}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-10 h-10 bg-gradient-to-br from-gold to-amber-600 rounded-full flex items-center justify-center">
                          <Package className="w-5 h-5 text-black" />
                        </div>
                        <div className="flex-1">
                          <p className="text-primary font-medium">{item.product_name}</p>
                          <p className="text-muted text-sm">${item.price?.toFixed(2)} {t('perUnit')}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <label className="tc-label text-xs">{t('customPriceLabel')}:</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.price}
                            onChange={(e) => updateItemPrice(index, e.target.value)}
                            className="tc-input tc-focus-ring w-24 text-center"
                            data-testid={`edit-price-${index}`}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="tc-label text-xs">{t('quantity')}:</label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItemQuantity(index, e.target.value)}
                            className="tc-input tc-focus-ring w-20 text-center"
                            data-testid={`edit-quantity-${index}`}
                          />
                        </div>
                        <div className="text-right min-w-[80px]">
                          <p className="text-gold font-bold">${(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                        <Button
                          type="button"
                          onClick={() => removeItemFromOrder(index)}
                          className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                          size="sm"
                          variant="ghost"
                          data-testid={`remove-item-${index}`}
                        >
                          <X className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add More Products Section */}
            <div className="space-y-4 border-t border-amber-500/20 pt-6">
              <h4 className="text-lg font-semibold text-primary">{t('addMoreProducts')}</h4>
              <div className="space-y-4">
                {orderCategories.map((category) => {
                  const categoryProducts = availableProducts
                    .filter(product => product.category === category.name)
                    .filter(product => !editingItems.some(item => item.product_id === product.product_id));
                  
                  if (categoryProducts.length === 0) return null;
                  
                  return (
                    <div key={category.name} className="space-y-2">
                      <h5 className="text-sm font-semibold text-gold flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        {category.display_name}
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {categoryProducts.map((product) => (
                          <div 
                            key={product.product_id}
                            className="flex items-center justify-between p-3 bg-tc-gold/5 rounded-lg border border-amber-500/20 hover:border-gold transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <Package className="w-5 h-5 text-gold" />
                              <div>
                                <p className="text-primary text-sm font-medium">{product.name}</p>
                                <p className="text-muted text-xs">${product.price?.toFixed(2)}</p>
                              </div>
                            </div>
                            <Button
                              type="button"
                              onClick={() => addItemToOrder(product.product_id)}
                              size="sm"
                              className="tc-button-secondary"
                              data-testid={`add-product-${product.product_id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              {availableProducts.filter(product => !editingItems.some(item => item.product_id === product.product_id)).length === 0 && (
                <p className="text-secondary text-sm text-center py-4">{t('allProductsAdded')}</p>
              )}
            </div>

            {/* New Total Section */}
            <div className="border-t border-amber-500/20 pt-6">
              <div className="flex justify-between items-center text-xl">
                <span className="text-primary font-semibold">{t('newTotal')}:</span>
                <span className="text-gold font-bold text-2xl" data-testid="edit-order-total">
                  ${calculateEditedTotal().toFixed(2)}
                </span>
              </div>
              {selectedOrder && (
                <div className="flex justify-between items-center text-sm text-muted mt-1">
                  <span>{t('originalTotal')}:</span>
                  <span>${selectedOrder.total_amount?.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <Button 
                type="button" 
                onClick={() => {
                  setShowEditItems(false);
                  setSelectedOrder(null);
                  setEditingItems([]);
                }}
                className="tc-button-secondary flex-1"
                data-testid="cancel-edit-items"
              >
                {t('cancel')}
              </Button>
              <Button 
                type="button"
                onClick={handleSaveItems}
                className="tc-button-primary flex-1"
                disabled={editingItems.length === 0}
                data-testid="save-edit-items"
              >
                {t('saveChanges')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock Return Confirmation Dialog */}
      <Dialog open={showStockReturnDialog} onOpenChange={cancelStockReturn}>
        <DialogContent className="tc-dialog-content max-w-lg">
          <div className="tc-dialog-header">
            <DialogTitle className="tc-dialog-title">
              {t('returnToStock') || 'Return to Stock?'}
            </DialogTitle>
            <DialogDescription className="tc-dialog-description">
              {t('returnToStockDescription') || 'Do you want to return this item back to inventory?'}
            </DialogDescription>
          </div>
          <div className="tc-dialog-body space-y-6">
            {itemToRemove && (
              <div className="bg-tc-gold/5 p-4 rounded-lg border border-amber-500/20">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-gold to-amber-600 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <p className="text-primary font-medium">{itemToRemove.item.product_name}</p>
                    <p className="text-muted text-sm">{t('quantity')}: {itemToRemove.item.quantity}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-4">
              <Button
                type="button"
                onClick={() => handleStockReturnConfirm(true)}
                className="tc-button-primary flex-1"
                data-testid="return-to-stock-yes"
              >
                <Check className="w-4 h-4 mr-2" />
                {t('yesReturnToStock') || 'Yes, Return to Stock'}
              </Button>
              <Button
                type="button"
                onClick={() => handleStockReturnConfirm(false)}
                className="tc-button-secondary flex-1"
                data-testid="return-to-stock-no"
              >
                <X className="w-4 h-4 mr-2" />
                {t('noKeepRemoved') || 'No, Keep Removed'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Discount Dialog */}
      <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
        <DialogContent className="tc-dialog-content max-w-md">
          <div className="tc-dialog-header">
            <DialogTitle className="tc-dialog-title">
              {discountOrder?.discount_percentage > 0 ? t('editDiscount') : t('applyDiscount')}
            </DialogTitle>
            <DialogDescription className="tc-dialog-description">
              {t('enterDiscountPercentage')}
            </DialogDescription>
          </div>
          <div className="tc-dialog-body space-y-6">
            {discountOrder && (
              <>
                {/* Order Info */}
                <div className="bg-tc-gold/5 p-4 rounded-lg border border-amber-500/20">
                  <div className="flex justify-between items-center">
                    <span className="text-secondary">{t('order')} #{discountOrder.order_id?.substring(0, 8)}</span>
                    <span className="text-primary">{discountOrder.customer_name}</span>
                  </div>
                </div>

                {/* Subtotal Display */}
                <div className="flex justify-between items-center">
                  <span className="text-secondary">{t('subtotalLabel')}:</span>
                  <span className="text-primary font-semibold">
                    ${(discountOrder.subtotal || discountOrder.total_amount)?.toFixed(2)}
                  </span>
                </div>

                {/* Current Discount */}
                {discountOrder.discount_percentage > 0 && (
                  <div className="flex justify-between items-center text-green-400">
                    <span>{t('currentDiscount')}:</span>
                    <span>{discountOrder.discount_percentage}% (-${discountOrder.discount_amount?.toFixed(2)})</span>
                  </div>
                )}

                {/* Discount Input */}
                <div>
                  <label className="tc-label">{t('discountPercentage')} (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    className="tc-input tc-focus-ring"
                    value={newDiscountPercent}
                    onChange={(e) => setNewDiscountPercent(parseFloat(e.target.value) || 0)}
                    placeholder="0"
                  />
                </div>

                {/* Preview */}
                <div className="bg-tc-gold/10 p-4 rounded-lg border border-amber-500/30">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-secondary">{t('discount')}:</span>
                    <span className="text-green-400">
                      -{newDiscountPercent}% 
                      (-${((discountOrder.subtotal || discountOrder.total_amount) * (newDiscountPercent / 100)).toFixed(2)})
                    </span>
                  </div>
                  {discountOrder.custom_items_total > 0 && (
                    <div className="flex justify-between items-center mb-2 text-amber-400">
                      <span>{t('customItems') || 'Custom Items'} ({t('noDiscountApplied') || 'no discount'}):</span>
                      <span>+${discountOrder.custom_items_total.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-primary">{t('newTotal')}:</span>
                    <span className="text-2xl font-bold text-gold">
                      ${calculatePreviewTotal().toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-4">
                  <Button 
                    type="button" 
                    onClick={() => {
                      setShowDiscountDialog(false);
                      setDiscountOrder(null);
                    }}
                    className="tc-button-secondary flex-1"
                  >
                    {t('cancel')}
                  </Button>
                  <Button 
                    type="button"
                    onClick={saveDiscount}
                    className="tc-button-primary flex-1"
                  >
                    {t('saveDiscount')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Users Section (Admin only)
const UsersSection = ({ user: currentUser }) => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const isAdmin = currentUser?.role === 'tc_admin';
  const isSalesRep = currentUser?.role === 'sales_rep';
  // Custom pricing dialog state
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [pricingUser, setPricingUser] = useState(null);
  const [pricingCatalog, setPricingCatalog] = useState([]);
  const [pricingDiscount, setPricingDiscount] = useState('');
  const [pricingPerProduct, setPricingPerProduct] = useState({}); // product_id -> string price
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    company_name: '',
    role: 'installation_center',
    phone: '',
    address: '',
    sales_rep_id: ''
  });
  const [ownershipFilter, setOwnershipFilter] = useState('all'); // all | tc_direct | sales_rep
  const [ownerRepFilter, setOwnerRepFilter] = useState('all');   // 'all' | <rep user_id>
  const [portalFilter, setPortalFilter] = useState('all');       // all | not_invited | invitation_sent | active | disabled
  const [customPricingOnly, setCustomPricingOnly] = useState(false);

  const isCustomerRole = (r) => r === 'installation_center' || r === 'elite_partner';
  const repName = (repId) => {
    const r = salesReps.find((x) => x.user_id === repId);
    return r ? (r.full_name || r.username) : null;
  };
  const ownershipOf = (u) => (u.account_management_type || (u.sales_rep_id ? 'SALES_REP' : 'TC_DIRECT'));
  const hasCustomPricing = (u) => (u.custom_prices && Object.keys(u.custom_prices).length > 0) || (typeof u.custom_discount_percent === 'number' && u.custom_discount_percent >= 0);
  const displayedUsers = users.filter((u) => {
    if (portalFilter !== 'all') {
      if (!isCustomerRole(u.role)) return false;
      if ((u.portal_status || 'active') !== portalFilter) return false;
    }
    if (customPricingOnly) {
      if (!isCustomerRole(u.role) || !hasCustomPricing(u)) return false;
    }
    if (ownershipFilter === 'all') return true;
    if (!isCustomerRole(u.role)) return false; // ownership filters apply to customer accounts only
    const own = ownershipOf(u);
    if (ownershipFilter === 'tc_direct') return own === 'TC_DIRECT';
    if (ownershipFilter === 'sales_rep') {
      if (own !== 'SALES_REP') return false;
      return ownerRepFilter === 'all' || u.sales_rep_id === ownerRepFilter;
    }
    return true;
  });

  useEffect(() => {
    fetchUsers();
    if (isAdmin) fetchSalesReps();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      // Handle both paginated and non-paginated responses
      const usersData = response.data.users || response.data;
      setUsers(usersData);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesReps = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users?role=sales_rep`);
      const usersData = response.data.users || response.data;
      setSalesReps(usersData);
    } catch (error) {
      console.error('Error loading sales reps');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {...newUser};
      // Only include sales_rep_id if admin has assigned one to a dealer role
      if (!isAdmin || !payload.sales_rep_id || payload.role === 'sales_rep' || payload.role === 'tc_admin') {
        delete payload.sales_rep_id;
      }
      await axios.post(`${API_BASE_URL}/api/users`, payload);
      toast.success('Usuario creado exitosamente');
      setShowCreateUser(false);
      setNewUser({
        username: '',
        email: '',
        password: '',
        full_name: '',
        company_name: '',
        role: 'installation_center',
        phone: '',
        address: '',
        sales_rep_id: ''
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const startEditUser = (user) => {
    setEditingUser({
      ...user,
      password: '' // Don't show existing password
    });
    setShowEditUser(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        email: editingUser.email,
        username: editingUser.username,
        full_name: editingUser.full_name,
        company_name: editingUser.company_name,
        phone: editingUser.phone,
        address: editingUser.address,
        role: editingUser.role
      };

      // Account ownership (admin-only; backend derives account_management_type from sales_rep_id)
      if (editingUser.role === 'installation_center' || editingUser.role === 'elite_partner') {
        if (editingUser.account_management_type === 'SALES_REP') {
          if (!editingUser.sales_rep_id) {
            toast.error('Please select a Sales Representative or choose TC Direct.');
            return;
          }
          updateData.sales_rep_id = editingUser.sales_rep_id;
        } else {
          updateData.sales_rep_id = ''; // TC Direct → clear assignment
        }
      }
      
      // Only include password if it was changed
      if (editingUser.password && editingUser.password.trim()) {
        updateData.password = editingUser.password;
      }
      
      await axios.put(`${API_BASE_URL}/api/users/${editingUser.user_id}`, updateData);
      toast.success(t('userUpdatedSuccess'));
      setShowEditUser(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorUpdatingUser'));
    }
  };

  const deleteUser = async (userId, userName) => {
    if (window.confirm(`¿Está seguro que desea eliminar al usuario "${userName}"? Esta acción no se puede deshacer.`)) {
      try {
        await axios.delete(`${API_BASE_URL}/api/users/${userId}`);
        toast.success('Usuario eliminado exitosamente');
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
      }
    }
  };

  const toggleUserStatus = async (userId) => {
    try {
      await axios.put(`${API_BASE_URL}/api/users/${userId}/toggle`);
      toast.success('Estado del usuario actualizado');
      fetchUsers();
    } catch (error) {
      toast.error(t('errorUpdatingUser'));
    }
  };

  const openPricingDialog = async (dealerUser) => {
    setPricingUser(dealerUser);
    setShowPricingDialog(true);
    setPricingLoading(true);
    try {
      const [productsRes, pricingRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/products?limit=500`),
        axios.get(`${API_BASE_URL}/api/users/${dealerUser.user_id}/custom-pricing`),
      ]);
      const catalog = (productsRes.data.products || productsRes.data || []).map(p => ({
        product_id: p.product_id,
        name: p.name,
        category: p.category,
        list_price: p.base_price ?? p.price ?? 0,
      }));
      setPricingCatalog(catalog);
      const disc = pricingRes.data.custom_discount_percent;
      setPricingDiscount(disc === null || disc === undefined ? '' : String(disc));
      const cp = pricingRes.data.custom_prices || {};
      const stringy = {};
      Object.keys(cp).forEach(k => { stringy[k] = String(cp[k]); });
      setPricingPerProduct(stringy);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorLoadingPricing'));
      setShowPricingDialog(false);
    } finally {
      setPricingLoading(false);
    }
  };

  const savePricing = async () => {
    if (!pricingUser) return;
    setPricingSaving(true);
    try {
      const payload = {};
      // Discount handling: empty string = clear (send -1); numeric = set
      if (pricingDiscount === '' || pricingDiscount === null) {
        payload.custom_discount_percent = -1;
      } else {
        const num = parseFloat(pricingDiscount);
        if (isNaN(num) || num < 0 || num > 100) {
          toast.error(t('invalidDiscountRange'));
          setPricingSaving(false);
          return;
        }
        payload.custom_discount_percent = num;
      }
      // Per-product prices: include only entries with a non-empty value
      const cp = {};
      let hasInvalid = false;
      Object.keys(pricingPerProduct).forEach(pid => {
        const v = pricingPerProduct[pid];
        if (v === '' || v === null || v === undefined) return;
        const num = parseFloat(v);
        if (isNaN(num) || num < 0) { hasInvalid = true; return; }
        cp[pid] = num;
      });
      if (hasInvalid) {
        toast.error(t('invalidCustomPrice'));
        setPricingSaving(false);
        return;
      }
      payload.custom_prices = cp;

      await axios.put(`${API_BASE_URL}/api/users/${pricingUser.user_id}/custom-pricing`, payload);
      toast.success(t('pricingUpdatedSuccess'));
      setShowPricingDialog(false);
      setPricingUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorUpdatingPricing'));
    } finally {
      setPricingSaving(false);
    }
  };

  const clearAllCustomPricing = async () => {
    if (!pricingUser) return;
    if (!window.confirm(t('clearAllPricingConfirm'))) return;
    setPricingSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/users/${pricingUser.user_id}/custom-pricing`, {
        custom_discount_percent: -1,
        custom_prices: {},
      });
      toast.success(t('pricingClearedSuccess'));
      setShowPricingDialog(false);
      setPricingUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorUpdatingPricing'));
    } finally {
      setPricingSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="tc-spinner"></div>
        <span className="ml-4 text-primary">{t('loadingUsers')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-primary mb-2">
            {t('userManagement')}
          </h2>
          <p className="text-secondary">{t('manageClientDistributorAccounts')}</p>
        </div>
        <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
          <DialogTrigger asChild>
            <Button className="tc-button-primary hover-lift">
              <Plus className="w-4 h-4 mr-2" />
              {t('createUserButton')}
            </Button>
          </DialogTrigger>
          <DialogContent className="tc-dialog-content">
            <div className="tc-dialog-header">
              <DialogTitle className="tc-dialog-title">{t('createNewUser')}</DialogTitle>
              <DialogDescription className="tc-dialog-description">
                {t('completeInfoCreateAccount')}
              </DialogDescription>
            </div>
            <div className="tc-dialog-body">
              <form onSubmit={handleCreateUser} className="tc-form-grid space-y-6">
                <div className="tc-form-grid tc-form-grid-2 gap-4">
                  <div>
                    <label className="tc-label">{t('usernameLabel')}</label>
                    <input
                      className="tc-input tc-focus-ring"
                      value={newUser.username}
                      onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                      placeholder="usuario123"
                      data-testid="new-user-username-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="tc-label">{t('emailLabel')}</label>
                    <input
                      className="tc-input tc-focus-ring"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      placeholder="usuario@empresa.com"
                      data-testid="new-user-email-input"
                      required
                    />
                  </div>
                </div>
                
                <div className="tc-form-grid tc-form-grid-2 gap-4">
                  <div>
                    <label className="tc-label">{t('passwordLabel')}</label>
                    <input
                      className="tc-input tc-focus-ring"
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      placeholder="••••••••"
                      data-testid="new-user-password-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="tc-label">{t('fullNameLabel')}</label>
                    <input
                      className="tc-input tc-focus-ring"
                      value={newUser.full_name}
                      onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                      placeholder="Juan Pérez"
                      data-testid="new-user-fullname-input"
                      required
                    />
                  </div>
                </div>

                <div className="tc-form-grid tc-form-grid-2 gap-4">
                  <div>
                    <label className="tc-label">{t('companyNameLabel')}</label>
                    <input
                      className="tc-input tc-focus-ring"
                      value={newUser.company_name}
                      onChange={(e) => setNewUser({...newUser, company_name: e.target.value})}
                      placeholder="Empresa PPF Austin"
                      data-testid="new-user-company-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="tc-label">{t('roleLabel')}</label>
                    <select 
                      className="tc-select tc-focus-ring"
                      value={newUser.role} 
                      onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                      disabled={isSalesRep}
                    >
                      <option value="installation_center">{t('installationCenter')}</option>
                      <option value="elite_partner">{t('elitePartner')}</option>
                      {isAdmin && (
                        <>
                          <option value="sales_rep">{t('salesRepRole')}</option>
                          <option value="tc_admin">{t('tcAdmin')}</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>

                {/* Sales Rep Assignment (admin only, dealer roles only) */}
                {isAdmin && (newUser.role === 'installation_center' || newUser.role === 'elite_partner') && salesReps.length > 0 && (
                  <div className="tc-form-grid tc-form-grid-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="tc-label">{t('assignSalesRepLabel')}</label>
                      <select
                        className="tc-select tc-focus-ring"
                        value={newUser.sales_rep_id}
                        onChange={(e) => setNewUser({...newUser, sales_rep_id: e.target.value})}
                        data-testid="new-user-sales-rep-select"
                      >
                        <option value="">{t('noSalesRepAssigned')}</option>
                        {salesReps.map((sr) => (
                          <option key={sr.user_id} value={sr.user_id}>
                            {sr.full_name} — {sr.username}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className="tc-form-grid tc-form-grid-2 gap-4">
                  <div>
                    <label className="tc-label">{t('phoneLabel')}</label>
                    <input
                      className="tc-input tc-focus-ring"
                      value={newUser.phone}
                      onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                      placeholder="+1 (512) 000-0000"
                    />
                  </div>
                  <div>
                    <label className="tc-label">{t('addressLabel')}</label>
                    <input
                      className="tc-input tc-focus-ring"
                      value={newUser.address}
                      onChange={(e) => setNewUser({...newUser, address: e.target.value})}
                      placeholder="Austin, Texas"
                    />
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button 
                    type="button" 
                    onClick={() => setShowCreateUser(false)}
                    className="tc-button-secondary flex-1"
                  >
                    {t('cancel')}
                  </Button>
                  <Button type="submit" className="tc-button-primary flex-1" data-testid="create-user-submit-btn">
                    {t('createUserButton')}
                  </Button>
                </div>
              </form>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
          <DialogContent className="tc-dialog-content">
            <div className="tc-dialog-header">
              <DialogTitle className="tc-dialog-title">{t('editUser')}</DialogTitle>
              <DialogDescription className="tc-dialog-description">
                {t('editUserInfo')}
              </DialogDescription>
            </div>
            <div className="tc-dialog-body">
              {editingUser && (
                <form onSubmit={handleEditUser} className="tc-form-grid space-y-6">
                  <div className="tc-form-grid tc-form-grid-2 gap-4">
                    <div>
                      <label className="tc-label">{t('usernameLabel')}</label>
                      <input
                        className="tc-input tc-focus-ring"
                        value={editingUser.username || ''}
                        onChange={(e) => setEditingUser({...editingUser, username: e.target.value})}
                        data-testid="edit-user-username-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="tc-label">{t('email')}</label>
                      <input
                        type="email"
                        className="tc-input tc-focus-ring"
                        value={editingUser.email || ''}
                        onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="tc-form-grid tc-form-grid-2 gap-4">
                    <div>
                      <label className="tc-label">{t('fullName')}</label>
                      <input
                        className="tc-input tc-focus-ring"
                        value={editingUser.full_name || ''}
                        onChange={(e) => setEditingUser({...editingUser, full_name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <label className="tc-label">{t('company')}</label>
                      <input
                        className="tc-input tc-focus-ring"
                        value={editingUser.company_name || ''}
                        onChange={(e) => setEditingUser({...editingUser, company_name: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="tc-form-grid tc-form-grid-2 gap-4">
                    <div>
                      <label className="tc-label">{t('roleLabel')}</label>
                      <select
                        className="tc-select tc-focus-ring"
                        value={editingUser.role || 'installation_center'}
                        onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                      >
                        <option value="installation_center">{t('installationCenter')}</option>
                        <option value="elite_partner">{t('elitePartner')}</option>
                        {isAdmin && (
                          <>
                            <option value="sales_rep">{t('salesRepRole')}</option>
                            <option value="tc_admin">{t('tcAdmin')}</option>
                          </>
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="tc-label">{t('newPasswordOptional')}</label>
                      <input
                        type="password"
                        className="tc-input tc-focus-ring"
                        value={editingUser.password || ''}
                        onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
                        placeholder="••••••••"
                      />
                    </div>
                  </div>

                  <div className="tc-form-grid tc-form-grid-2 gap-4">
                    <div>
                      <label className="tc-label">{t('phoneLabel')}</label>
                      <input
                        type="tel"
                        className="tc-input tc-focus-ring"
                        value={editingUser.phone || ''}
                        onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="tc-label">{t('addressLabel')}</label>
                      <input
                        className="tc-input tc-focus-ring"
                        value={editingUser.address || ''}
                        onChange={(e) => setEditingUser({...editingUser, address: e.target.value})}
                      />
                    </div>
                  </div>

                  {/* Account Management (ownership) — admin only, customer accounts only */}
                  {isAdmin && (editingUser.role === 'installation_center' || editingUser.role === 'elite_partner') && (
                    <div className="rounded-lg border border-[#C6A15B]/30 bg-black/30 p-4 space-y-3" data-testid="account-management-section">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#C6A15B]" />
                        <h4 className="text-sm font-semibold text-[#E7CB85]">Account Management</h4>
                      </div>
                      <div>
                        <label className="tc-label">Managed By</label>
                        <div className="grid grid-cols-2 gap-3 mt-1">
                          <button type="button" data-testid="managed-by-tc-direct"
                            onClick={() => setEditingUser({...editingUser, account_management_type: 'TC_DIRECT', sales_rep_id: ''})}
                            className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                              (editingUser.account_management_type || (editingUser.sales_rep_id ? 'SALES_REP' : 'TC_DIRECT')) === 'TC_DIRECT'
                                ? 'bg-[#C6A15B] text-black border-[#C6A15B]'
                                : 'bg-black/40 text-slate-300 border-white/10 hover:border-[#C6A15B]/50'}`}>
                            TC Direct
                          </button>
                          <button type="button" data-testid="managed-by-sales-rep"
                            onClick={() => setEditingUser({...editingUser, account_management_type: 'SALES_REP'})}
                            className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                              (editingUser.account_management_type || (editingUser.sales_rep_id ? 'SALES_REP' : 'TC_DIRECT')) === 'SALES_REP'
                                ? 'bg-[#C6A15B] text-black border-[#C6A15B]'
                                : 'bg-black/40 text-slate-300 border-white/10 hover:border-[#C6A15B]/50'}`}>
                            Sales Representative
                          </button>
                        </div>
                      </div>
                      {(editingUser.account_management_type || (editingUser.sales_rep_id ? 'SALES_REP' : 'TC_DIRECT')) === 'SALES_REP' && (
                        <div>
                          <label className="tc-label">Assigned Salesperson</label>
                          <select
                            className="tc-select tc-focus-ring"
                            value={editingUser.sales_rep_id || ''}
                            onChange={(e) => setEditingUser({...editingUser, sales_rep_id: e.target.value})}
                            data-testid="assigned-salesperson-select"
                          >
                            <option value="">Select Salesperson…</option>
                            {salesReps.filter((r) => r.is_active !== false).map((r) => (
                              <option key={r.user_id} value={r.user_id}>{r.full_name || r.username}</option>
                            ))}
                          </select>
                          <p className="text-[11px] text-slate-500 mt-1">Changing ownership does not alter past orders, invoices or historical attribution.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Commercial panels — customer (dealer) accounts only */}
                  {isAdmin && (editingUser.role === 'installation_center' || editingUser.role === 'elite_partner') && editingUser.user_id && (
                    <>
                      <PortalAccessCard customerId={editingUser.user_id} />
                      <PricingPanel customerId={editingUser.user_id} />
                      <OwnershipHistory customerId={editingUser.user_id} />
                      <CustomerActivity customerId={editingUser.user_id} />
                    </>
                  )}

                  {/* Sales rep activity rollup (lightweight admin visibility) */}
                  {isAdmin && editingUser.role === 'sales_rep' && editingUser.user_id && (
                    <SalesRepStatsStrip userId={editingUser.user_id} />
                  )}

                  <div className="flex gap-4 pt-4">
                    <Button 
                      type="button" 
                      onClick={() => {
                        setShowEditUser(false);
                        setEditingUser(null);
                      }}
                      className="tc-button-secondary flex-1"
                    >
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="tc-button-primary flex-1" data-testid="edit-user-submit-btn">
                      {t('updateUser')}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="tc-card">
        <div className="tc-card-header">
          <h3 className="text-xl font-semibold text-primary mb-1">{t('userList')}</h3>
          <p className="text-secondary text-sm">{t('registeredUsers')}</p>
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="ownership-filter-bar">
              {[
                { k: 'all', label: 'All Accounts' },
                { k: 'tc_direct', label: 'TC Direct' },
                { k: 'sales_rep', label: 'Sales Rep' },
              ].map((f) => (
                <button key={f.k} type="button"
                  onClick={() => { setOwnershipFilter(f.k); if (f.k !== 'sales_rep') setOwnerRepFilter('all'); }}
                  data-testid={`ownership-filter-${f.k}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    ownershipFilter === f.k
                      ? 'bg-[#C6A15B] text-black border-[#C6A15B]'
                      : 'bg-black/40 text-slate-300 border-white/10 hover:border-[#C6A15B]/50'}`}>
                  {f.label}
                </button>
              ))}
              {ownershipFilter === 'sales_rep' && (
                <select
                  className="tc-select tc-focus-ring !w-auto text-xs py-1.5"
                  value={ownerRepFilter}
                  onChange={(e) => setOwnerRepFilter(e.target.value)}
                  data-testid="ownership-rep-filter"
                >
                  <option value="all">All Salespeople</option>
                  {salesReps.map((r) => (
                    <option key={r.user_id} value={r.user_id}>{r.full_name || r.username}</option>
                  ))}
                </select>
              )}
              <span className="w-px h-5 bg-white/10 mx-1" />
              <select
                className="tc-select tc-focus-ring !w-auto text-xs py-1.5"
                value={portalFilter}
                onChange={(e) => setPortalFilter(e.target.value)}
                data-testid="portal-status-filter"
              >
                <option value="all">All Portal Statuses</option>
                <option value="not_invited">Not Invited</option>
                <option value="invitation_sent">Invitation Sent</option>
                <option value="active">Portal Active</option>
                <option value="disabled">Portal Disabled</option>
              </select>
              <button type="button" onClick={() => setCustomPricingOnly((v) => !v)}
                data-testid="custom-pricing-filter"
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  customPricingOnly ? 'bg-[#C6A15B] text-black border-[#C6A15B]' : 'bg-black/40 text-slate-300 border-white/10 hover:border-[#C6A15B]/50'}`}>
                Custom Pricing
              </button>
            </div>
          )}
        </div>
        <CardContent className="tc-card-content p-0">
          <div className="tc-table">
            <table className="w-full">
              <thead className="tc-table-header">
                <tr>
                  <th className="tc-table-header th">{t('user')}</th>
                  <th className="tc-table-header th">{t('company')}</th>
                  <th className="tc-table-header th">{t('roleLabel')}</th>
                  <th className="tc-table-header th">{t('status')}</th>
                  <th className="tc-table-header th">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {displayedUsers.map((user) => (
                  <tr key={user.user_id} className="tc-table-row">
                    <td className="tc-table-cell">
                      <div>
                        <p className="text-primary font-medium text-sm">{user.full_name}</p>
                        <p className="text-muted text-xs">{user.email}</p>
                        <p className="text-secondary text-xs">@{user.username}</p>
                      </div>
                    </td>
                    <td className="tc-table-cell">
                      <div>
                        <p className="text-primary text-sm font-medium">{user.company_name}</p>
                        {user.phone && (
                          <p className="text-muted text-xs flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {user.phone}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="tc-table-cell">
                      <Badge className={`tc-badge ${
                        user.role === 'tc_admin' ? 'tc-badge-error' :
                        user.role === 'elite_partner' ? 'tc-badge-gold' :
                        user.role === 'sales_rep' ? 'tc-badge-success' :
                        'tc-badge-info'
                      }`}>
                        {user.role === 'tc_admin' ? t('admin') :
                         user.role === 'elite_partner' ? t('elitePartner') :
                         user.role === 'sales_rep' ? t('salesRepRole') :
                         t('installationCenter')}
                      </Badge>
                      {isCustomerRole(user.role) && (
                        <div className="mt-1.5" data-testid={`ownership-badge-${user.username}`}>
                          {ownershipOf(user) === 'SALES_REP' ? (
                            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">
                              <Users className="w-3 h-3" /> Sales Rep{repName(user.sales_rep_id) ? ` · ${repName(user.sales_rep_id)}` : ''}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] text-[#E7CB85] bg-[#C6A15B]/10 border border-[#C6A15B]/30 rounded-full px-2 py-0.5">
                              <Shield className="w-3 h-3" /> TC Direct
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="tc-table-cell">
                      <Badge className={`tc-badge ${user.is_active ? 'tc-badge-success' : 'tc-badge-warning'}`}>
                        {user.is_active ? t('activeStatus') : t('inactiveStatus')}
                      </Badge>
                    </td>
                    <td className="tc-table-cell">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEditUser(user)}
                          className="text-xs px-2 py-1 tc-button-secondary"
                          data-testid={`edit-user-btn-${user.username}`}
                        >
                          <Settings className="w-3 h-3 mr-1" />
                          {t('edit')}
                        </Button>
                        {isAdmin && (user.role === 'installation_center' || user.role === 'elite_partner') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPricingDialog(user)}
                            className="text-xs px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600"
                            data-testid={`custom-pricing-btn-${user.user_id}`}
                          >
                            <DollarSign className="w-3 h-3 mr-1" />
                            {t('customPricingBtn')}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleUserStatus(user.user_id)}
                          className={`text-xs px-2 py-1 ${
                            user.is_active ? 'bg-yellow-600 hover:bg-yellow-700' : 'tc-button-primary'
                          }`}
                        >
                          {user.is_active ? t('deactivate') : t('activate')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteUser(user.user_id, user.full_name)}
                          className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white border-red-600"
                          data-testid={`delete-user-btn-${user.username}`}
                        >
                          {t('delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {users.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-muted mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-primary mb-2">{t('noUsersRegistered')}</h3>
              <p className="text-secondary mb-6">{t('startCreatingAccounts')}</p>
              <Button 
                onClick={() => setShowCreateUser(true)}
                className="tc-button-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('createFirstUser')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Custom Pricing Dialog */}
      <Dialog open={showPricingDialog} onOpenChange={(open) => { if (!open) { setShowPricingDialog(false); setPricingUser(null); } }}>
        <DialogContent className="tc-dialog max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="custom-pricing-dialog">
          <DialogHeader>
            <DialogTitle className="tc-dialog-title flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              {t('customPricingTitle')} — {pricingUser?.full_name}
            </DialogTitle>
            <DialogDescription className="tc-dialog-description">
              {t('customPricingSubtitle')}
            </DialogDescription>
          </DialogHeader>

          {pricingLoading ? (
            <div className="flex justify-center py-8">
              <div className="tc-spinner"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Custom discount % */}
              <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-4">
                <label className="tc-label block mb-2">{t('customDiscountLabel')}</label>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    placeholder={t('leaveEmptyForTier')}
                    value={pricingDiscount}
                    onChange={(e) => setPricingDiscount(e.target.value)}
                    className="tc-input w-32"
                    data-testid="custom-discount-input"
                  />
                  <span className="text-emerald-300">%</span>
                  {pricingDiscount === '' && (
                    <span className="text-slate-400 text-sm italic">{t('willUseTierDiscount')}</span>
                  )}
                </div>
                <p className="text-emerald-400 text-xs mt-2">{t('customDiscountHelper')}</p>
              </div>

              {/* Per-product custom prices */}
              <div>
                <h4 className="text-primary font-semibold mb-3">{t('customPerProductLabel')}</h4>
                <p className="text-secondary text-xs mb-3">{t('customPerProductHelper')}</p>
                <div className="max-h-96 overflow-y-auto border border-slate-700 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-800 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-slate-300">{t('productLabel') || 'Product'}</th>
                        <th className="text-right p-3 text-slate-300">{t('listPriceLabel')}</th>
                        <th className="text-right p-3 text-slate-300">{t('customPriceLabel')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pricingCatalog.map((p) => (
                        <tr key={p.product_id} className="border-t border-slate-700 hover:bg-slate-800/50">
                          <td className="p-3">
                            <div className="text-primary">{p.name}</div>
                            {p.category && <div className="text-xs text-muted">{p.category}</div>}
                          </td>
                          <td className="p-3 text-right text-slate-300">${p.list_price.toFixed(2)}</td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-slate-400">$</span>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="—"
                                value={pricingPerProduct[p.product_id] || ''}
                                onChange={(e) => setPricingPerProduct({...pricingPerProduct, [p.product_id]: e.target.value})}
                                className="tc-input w-28 text-right"
                                data-testid={`custom-price-${p.product_id}`}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between gap-3 pt-4 border-t border-slate-700">
                <Button
                  variant="outline"
                  onClick={clearAllCustomPricing}
                  className="text-red-400 border-red-500/50 hover:bg-red-500/10"
                  disabled={pricingSaving}
                  data-testid="clear-all-pricing-btn"
                >
                  {t('clearAllPricing')}
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { setShowPricingDialog(false); setPricingUser(null); }}
                    className="tc-button-secondary"
                    disabled={pricingSaving}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    onClick={savePricing}
                    className="tc-button-primary"
                    disabled={pricingSaving}
                    data-testid="save-pricing-btn"
                  >
                    {pricingSaving ? t('saving') : t('savePricingBtn')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Warranty Registration Section (For Clients/Dealers)
const WarrantySection = ({ user }) => {
  const { t } = useLanguage();
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [serialVerification, setSerialVerification] = useState(null);
  const [verifyingSerial, setVerifyingSerial] = useState(false);
  const [activeTab, setActiveTab] = useState('my-warranties');
  const [coverageType, setCoverageType] = useState('package');
  const [packageValue, setPackageValue] = useState('');
  const [coverageItems, setCoverageItems] = useState([]);
  const [rollAvailability, setRollAvailability] = useState(null);
  const [warrantyForm, setWarrantyForm] = useState({
    serial_number: '',
    roll_number: '',
    client_name: user?.full_name || '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: new Date().getFullYear(),
    vin_number: '',
    vehicle_mileage: '',
    installation_date: new Date().toISOString().split('T')[0],
    installation_location: '',
    additional_notes: ''
  });

  useEffect(() => {
    fetchWarranties();
  }, []);

  // Fetch roll capacity/eligibility when the roll number changes (debounced).
  useEffect(() => {
    const rn = (warrantyForm.roll_number || '').trim();
    if (rn.length < 3) { setRollAvailability(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/warranties/roll-availability/${encodeURIComponent(rn)}`);
        setRollAvailability(res.data);
      } catch (e) { setRollAvailability(null); }
    }, 600);
    return () => clearTimeout(timer);
  }, [warrantyForm.roll_number]);

  const fetchWarranties = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warranties`);
      setWarranties(response.data);
    } catch (error) {
      toast.error('Error al cargar garantías');
    } finally {
      setLoading(false);
    }
  };

  const verifySerialNumber = async (serialNumber) => {
    if (!serialNumber || serialNumber.length < 5) {
      setSerialVerification(null);
      return;
    }

    setVerifyingSerial(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warranties/verify-serial/${encodeURIComponent(serialNumber)}`);
      setSerialVerification(response.data);
    } catch (error) {
      setSerialVerification({
        valid: false,
        message: 'Error al verificar número de serie'
      });
    } finally {
      setVerifyingSerial(false);
    }
  };

  const handleSerialNumberChange = (value) => {
    setWarrantyForm({...warrantyForm, serial_number: value.toUpperCase()});
    
    // Debounce verification
    setTimeout(() => {
      if (value === warrantyForm.serial_number) {
        verifySerialNumber(value);
      }
    }, 1000);
  };

  const handleWarrantySubmit = async (e) => {
    e.preventDefault();
    
    // Validate serial number is provided (no longer needs to match orders)
    if (!warrantyForm.serial_number || warrantyForm.serial_number.trim().length < 3) {
      toast.error(t('mustEnterValidSerial'));
      return;
    }

    if (!warrantyForm.roll_number || warrantyForm.roll_number.trim().length < 3) {
      toast.error(t('mustEnterRollNumber'));
      return;
    }

    // Validate coverage selection
    if (coverageType === 'package') {
      if (!packageValue) { toast.error('Please select an installation package.'); return; }
    } else {
      if (!coverageItems || coverageItems.length === 0) { toast.error('Please add at least one installation area.'); return; }
    }

    const payload = {
      ...warrantyForm,
      coverage_type: coverageType,
      installation_location: coverageType === 'package' ? packageValue : null,
      coverage_items: coverageType === 'individual'
        ? coverageItems.map((it) => ({
            area_key: it.area_key,
            side: it.side || null,
            custom_description: it.custom_description || null,
            custom_notes: it.custom_notes || null,
          }))
        : null,
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/api/warranties/request`, payload);
      toast.success(t('warrantyRequestSentSuccess'));
      setShowRequestForm(false);
      setWarrantyForm({
        serial_number: '',
        roll_number: '',
        client_name: user?.full_name || '',
        vehicle_make: '',
        vehicle_model: '',
        vehicle_year: new Date().getFullYear(),
        vin_number: '',
        vehicle_mileage: '',
        installation_date: new Date().toISOString().split('T')[0],
        installation_location: '',
        additional_notes: ''
      });
      setCoverageType('package');
      setPackageValue('');
      setCoverageItems([]);
      setRollAvailability(null);
      setSerialVerification(null);
      fetchWarranties();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorSendingWarrantyRequest'));
    }
  };

  const handleWarrantySearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || searchQuery.length < 2) {
      toast.error(t('minCharactersSearch'));
      return;
    }

    setSearching(true);
    try {
      // Filter user's warranties by search query
      const filtered = warranties.filter(warranty => 
        warranty.serial_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        warranty.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        warranty.vin_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        `${warranty.vehicle_make} ${warranty.vehicle_model}`.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered);
    } catch (error) {
      toast.error(t('errorLoadingWarranties'));
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const getInstallationLocationLabel = (location) => {
    const locations = {
      'full_front': t('fullFront'),
      'partial_front': t('partialFront'),
      'full_body': t('fullBody'),
      'interior': t('interior'),
      'windshield': t('windshield'),
      'change_finish': t('changeFinish'),
      'full_wrap': t('fullWrap'),
      'protection_kit': t('protectionKit')
    };
    return locations[location] || location;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: t('pending'), class: 'tc-badge-warning' },
      'approved': { label: t('approvedTab'), class: 'tc-badge-success' },
      'rejected': { label: t('rejectedTab'), class: 'tc-badge-error' }
    };
    
    const statusInfo = statusMap[status] || { label: status, class: 'tc-badge-warning' };
    return <Badge className={`tc-badge ${statusInfo.class}`}>{statusInfo.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="tc-spinner"></div>
        <span className="ml-4 text-primary">{t('loadingWarranties')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-primary mb-2">
            {t('warrantyManagementClient')}
          </h2>
          <p className="text-secondary">{t('requestWarrantiesCheckHistory')}</p>
        </div>
        <Button 
          onClick={() => setShowRequestForm(true)}
          className="tc-button-primary hover-lift"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('requestWarranty')}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-amber-500/20">
        <button
          onClick={() => setActiveTab('my-warranties')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'my-warranties'
              ? 'text-gold border-gold'
              : 'text-secondary border-transparent hover:text-primary'
          }`}
        >
          {t('myWarranties')} ({warranties.length})
        </button>
        <button
          onClick={() => setActiveTab('search')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'search'
              ? 'text-gold border-gold'
              : 'text-secondary border-transparent hover:text-primary'
          }`}
        >
          {t('searchWarranties')}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'search' && (
        <Card className="tc-card">
          <CardContent className="tc-card-content">
            <h3 className="text-lg font-semibold text-primary mb-4">{t('searchInMyWarranties')}</h3>
            <form onSubmit={handleWarrantySearch} className="flex gap-4 items-end">
              <div className="flex-1">
                <input
                  className="tc-input tc-focus-ring"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('searchBySerialClientVINVehicle')}
                />
              </div>
              <Button 
                type="submit" 
                disabled={searching}
                className="tc-button-primary"
              >
                {searching ? t('searching') : t('search')}
              </Button>
              {searchResults !== null && (
                <Button 
                  type="button" 
                  onClick={clearSearch}
                  className="tc-button-secondary"
                >
                  {t('clear')}
                </Button>
              )}
            </form>
            
            {searchResults !== null && (
              <div className="mt-6">
                <h4 className="text-md font-medium text-primary mb-3">
                  {t('searchResults')} ({searchResults.length})
                </h4>
                {searchResults.length === 0 ? (
                  <p className="text-secondary">{t('noWarrantiesFoundCriteria')}</p>
                ) : (
                  <div className="space-y-4">
                    {searchResults.map((warranty) => (
                      <WarrantyCard key={warranty.warranty_id} warranty={warranty} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Warranty Request Form */}
      <Dialog open={showRequestForm} onOpenChange={setShowRequestForm}>
        <DialogContent className="tc-dialog-content max-w-2xl">
          <div className="tc-dialog-header">
            <DialogTitle className="tc-dialog-title">{t('requestWarrantyTitle')}</DialogTitle>
            <DialogDescription className="tc-dialog-description">
              {t('completeInstallationInfo')}
            </DialogDescription>
          </div>
          <div className="tc-dialog-body">
            <form onSubmit={handleWarrantySubmit} className="space-y-6">
              {/* Serial Number Input */}
              <div>
                <label className="tc-label">{t('serialNumberRequired')}</label>
                <input
                  className="tc-input tc-focus-ring"
                  value={warrantyForm.serial_number}
                  onChange={(e) => setWarrantyForm({...warrantyForm, serial_number: e.target.value.toUpperCase()})}
                  placeholder="TC-PPF-001234"
                  required
                  data-testid="warranty-serial-input"
                />
                <p className="text-xs text-muted mt-1">{t('enterSerialFromProduct')}</p>
              </div>

              {/* Roll Number / Tube Serial Input */}
              <div>
                <label className="tc-label">{t('rollNumberRequired')}</label>
                <input
                  className="tc-input tc-focus-ring"
                  value={warrantyForm.roll_number}
                  onChange={(e) => setWarrantyForm({...warrantyForm, roll_number: e.target.value.toUpperCase()})}
                  placeholder="TKL99R2504P2378"
                  required
                  data-testid="warranty-roll-number-input"
                />
                <p className="text-xs text-muted mt-1">{t('enterRollNumberHint')}</p>
              </div>

              {/* Client Information */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="tc-label">{t('finalClientName')}</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={warrantyForm.client_name}
                    onChange={(e) => setWarrantyForm({...warrantyForm, client_name: e.target.value})}
                    placeholder={t('vehicleOwnerName')}
                    required
                  />
                </div>
              </div>

              {/* Vehicle Information */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="tc-label">{t('vehicleMakeRequired')}</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={warrantyForm.vehicle_make}
                    onChange={(e) => setWarrantyForm({...warrantyForm, vehicle_make: e.target.value})}
                    placeholder="Toyota, BMW, etc."
                    required
                  />
                </div>
                <div>
                  <label className="tc-label">{t('modelRequired')}</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={warrantyForm.vehicle_model}
                    onChange={(e) => setWarrantyForm({...warrantyForm, vehicle_model: e.target.value})}
                    placeholder="Camry, X3, etc."
                    required
                  />
                </div>
                <div>
                  <label className="tc-label">{t('yearRequired')}</label>
                  <input
                    className="tc-input tc-focus-ring"
                    type="number"
                    min="1990"
                    max="2030"
                    value={warrantyForm.vehicle_year}
                    onChange={(e) => setWarrantyForm({...warrantyForm, vehicle_year: parseInt(e.target.value)})}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="tc-label">{t('vinRequired')}</label>
                <input
                  className="tc-input tc-focus-ring"
                  value={warrantyForm.vin_number}
                  onChange={(e) => setWarrantyForm({...warrantyForm, vin_number: e.target.value.toUpperCase()})}
                  placeholder={t('vinCharacters')}
                  maxLength="17"
                  required
                />
              </div>

              <div>
                <label className="tc-label">{t('vehicleMileageRequired')}</label>
                <input
                  className="tc-input tc-focus-ring"
                  type="number"
                  min="0"
                  max="999999"
                  value={warrantyForm.vehicle_mileage}
                  onChange={(e) => setWarrantyForm({...warrantyForm, vehicle_mileage: parseInt(e.target.value) || ''})}
                  placeholder={t('vehicleMileagePlaceholder')}
                  required
                  data-testid="warranty-mileage-input"
                />
              </div>

              {/* Installation Details */}
              <div>
                <label className="tc-label">{t('installationDateRequired')}</label>
                <input
                  className="tc-input tc-focus-ring"
                  type="date"
                  value={warrantyForm.installation_date}
                  onChange={(e) => setWarrantyForm({...warrantyForm, installation_date: e.target.value})}
                  required
                />
              </div>

              {/* TC Roll Credit coverage selector */}
              <CoverageSelector
                coverageType={coverageType}
                setCoverageType={setCoverageType}
                packageValue={packageValue}
                setPackageValue={setPackageValue}
                items={coverageItems}
                setItems={setCoverageItems}
                availability={rollAvailability}
              />

              {/* Additional Notes */}
              <div>
                <label className="tc-label">{t('additionalNotesOptional')}</label>
                <textarea
                  className="tc-textarea tc-focus-ring"
                  value={warrantyForm.additional_notes}
                  onChange={(e) => setWarrantyForm({...warrantyForm, additional_notes: e.target.value})}
                  placeholder={t('additionalInstallationInfo')}
                  rows={3}
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button 
                  type="button" 
                  onClick={() => setShowRequestForm(false)}
                  className="tc-button-secondary flex-1"
                >
                  {t('cancel')}
                </Button>
                <Button 
                  type="submit" 
                  className="tc-button-primary flex-1"
                  data-testid="warranty-submit-btn"
                  disabled={!warrantyForm.serial_number || warrantyForm.serial_number.trim().length < 3}
                >
                  {t('sendRequest')}
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* My Warranties Tab */}
      {activeTab === 'my-warranties' && (
        <Card className="tc-card">
          <div className="tc-card-header">
            <h3 className="text-xl font-semibold text-primary mb-1">{t('myWarrantyRequests')}</h3>
            <p className="text-secondary text-sm">{t('warrantyHistoryStatus')}</p>
          </div>
          <CardContent className="tc-card-content p-0">
            {warranties.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="w-16 h-16 text-muted mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-primary mb-2">{t('noWarrantiesRequested')}</h3>
                <p className="text-secondary mb-6">{t('requestWarrantiesToProvide')}</p>
                <Button 
                  onClick={() => setShowRequestForm(true)}
                  className="tc-button-primary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('requestFirstWarranty')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4 p-6">
                {warranties.map((warranty) => (
                  <WarrantyCard key={warranty.warranty_id} warranty={warranty} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Warranty Card Component
const WarrantyCard = ({ warranty }) => {
  const { t } = useLanguage();
  const [downloading, setDownloading] = useState(false);
  
  const getInstallationLocationLabel = (location) => {
    const locations = {
      'full_front': t('fullFront'),
      'partial_front': t('partialFront'),
      'full_body': t('fullBody'),
      'interior': t('interior'),
      'windshield': t('windshield'),
      'change_finish': t('changeFinish'),
      'full_wrap': t('fullWrap'),
      'protection_kit': t('protectionKit')
    };
    return locations[location] || location;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: t('underReview'), class: 'tc-badge-warning', icon: '⏳' },
      'approved': { label: t('approvedTab'), class: 'tc-badge-success', icon: '✅' },
      'rejected': { label: t('rejectedTab'), class: 'tc-badge-error', icon: '❌' }
    };
    
    const statusInfo = statusMap[status] || { label: status, class: 'tc-badge-warning', icon: '❓' };
    return (
      <Badge className={`tc-badge ${statusInfo.class}`}>
        {statusInfo.icon} {statusInfo.label}
      </Badge>
    );
  };

  const handleDownloadCertificate = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warranties/${warranty.warranty_id}/certificate`);
      const pdfData = response.data.certificate_pdf;
      
      // Convert base64 to blob and download
      const byteCharacters = atob(pdfData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `warranty_certificate_${warranty.serial_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('certificateDownloaded'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorDownloadingCertificate'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="tc-card hover-lift">
      <CardContent className="tc-card-content">
        <div className="flex justify-between items-start">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              warranty.status === 'approved' ? 'bg-gradient-to-br from-green-500 to-green-600' :
              warranty.status === 'rejected' ? 'bg-gradient-to-br from-red-500 to-red-600' :
              'bg-gradient-to-br from-amber-500 to-amber-600'
            }`}>
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div className="space-y-2">
              <div>
                <h4 className="text-lg font-semibold text-primary">
                  {warranty.vehicle_year} {warranty.vehicle_make} {warranty.vehicle_model}
                </h4>
                <p className="text-secondary text-sm">{t('clientLabel')}: {warranty.client_name}</p>
                <p className="text-muted text-xs">{t('vinLabel')}: {warranty.vin_number} | {t('mileageLabel')}: {warranty.vehicle_mileage?.toLocaleString() || 'N/A'} mi</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted">{t('serialLabel')}:</span>
                  <p className="text-primary font-mono font-bold">{warranty.serial_number}</p>
                </div>
                <div>
                  <span className="text-muted">{t('rollNumberShort')}:</span>
                  <p className="text-tc-gold font-mono font-bold" data-testid={`warranty-roll-${warranty.warranty_id}`}>{warranty.roll_number || '—'}</p>
                </div>
                <div>
                  <span className="text-muted">{t('installedLabel')}:</span>
                  <p className="text-primary font-medium">{new Date(warranty.installation_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-muted">{t('areaLabel')}:</span>
                  <p className="text-primary font-medium">{warranty.coverage_summary || getInstallationLocationLabel(warranty.installation_location)}</p>
                </div>
                <div>
                  <span className="text-muted">{t('requestedLabel')}:</span>
                  <p className="text-primary font-medium">{new Date(warranty.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              
              {warranty.additional_notes && (
                <div>
                  <span className="text-muted text-sm">{t('notesLabel')}:</span>
                  <p className="text-secondary text-sm italic">{warranty.additional_notes}</p>
                </div>
              )}

              {warranty.admin_notes && (
                <div className={`p-3 rounded-lg border ${
                  warranty.status === 'rejected' 
                    ? 'bg-red-900/20 border-red-500/30' 
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <span className={`text-sm font-medium ${
                    warranty.status === 'rejected' ? 'text-red-400' : 'text-blue-700'
                  }`}>{warranty.status === 'rejected' ? t('rejectionReason') : t('adminCommentsLabel')}:</span>
                  <p className={`text-sm ${
                    warranty.status === 'rejected' ? 'text-red-300' : 'text-blue-600'
                  }`}>{warranty.admin_notes}</p>
                </div>
              )}

              {warranty.status === 'approved' && warranty.warranty_end_date && (
                <div className="bg-green-900/20 p-3 rounded-lg border border-green-500/30">
                  <span className="text-green-400 text-sm font-medium">{t('warrantyValidUntil')}:</span>
                  <p className="text-green-300 text-sm">{new Date(warranty.warranty_end_date).toLocaleDateString()}</p>
                </div>
              )}

              {/* Download Certificate Button for Approved Warranties */}
              {warranty.status === 'approved' && (
                <Button
                  onClick={handleDownloadCertificate}
                  disabled={downloading}
                  className="mt-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
                  data-testid={`download-certificate-btn-${warranty.warranty_id}`}
                >
                  {downloading ? (
                    <>
                      <div className="tc-spinner-small mr-2"></div>
                      {t('downloading')}
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      {t('downloadCertificate')}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          
          <div className="text-right">
            {getStatusBadge(warranty.status)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Admin Warranty Management Section
const AdminWarrantySection = () => {
  const { t } = useLanguage();
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [statusCounts, setStatusCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedWarranty, setSelectedWarranty] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [approvalAction, setApprovalAction] = useState('approve');
  const [adminNotes, setAdminNotes] = useState('');
  const [warrantyDurationMonths, setWarrantyDurationMonths] = useState(5);
  const [processing, setProcessing] = useState(false);
  const [customCredits, setCustomCredits] = useState({});
  const [showVoidDialog, setShowVoidDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [editForm, setEditForm] = useState({
    client_name: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    vin_number: '',
    vehicle_mileage: '',
    installation_date: '',
    installation_location: '',
    additional_notes: '',
    admin_notes: '',
    status: '',
    warranty_start_date: '',
    warranty_end_date: ''
  });

  useEffect(() => {
    fetchWarranties();
  }, [activeTab]);


  const fetchWarranties = async () => {
    setLoading(true);
    try {
      const status = activeTab === 'all' ? '' : activeTab;
      const response = await axios.get(`${API_BASE_URL}/api/admin/warranties${status ? `?status=${status}` : ''}`);
      setWarranties(response.data.warranties || []);
      setStatusCounts(response.data.status_counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (error) {
      toast.error('Error al cargar garantías');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || searchQuery.length < 2) {
      toast.error('Ingresa al menos 2 caracteres para buscar');
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/warranties/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Error al buscar garantías');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const openApprovalDialog = (warranty, action) => {
    setSelectedWarranty(warranty);
    setApprovalAction(action);
    setAdminNotes('');
    setWarrantyDurationMonths(5);
    setCustomCredits({});
    setShowApprovalDialog(true);
  };

  const handleVoid = (warranty) => {
    setVoidTarget(warranty);
    setVoidReason('');
    setShowVoidDialog(true);
  };

  const confirmVoid = async () => {
    if (!voidTarget || !voidReason.trim()) { toast.error('A reason is required to void a certificate.'); return; }
    setProcessing(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/admin/warranties/${voidTarget.warranty_id}/void`, { reason: voidReason.trim() });
      toast.success(`Certificate voided. ${res.data.capacity_returned} credits returned.`);
      setShowVoidDialog(false);
      setVoidTarget(null);
      fetchWarranties();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to void certificate');
    } finally {
      setProcessing(false);
    }
  };

  const handleRestore = (warranty) => {
    setVoidTarget(warranty);
    setShowRestoreDialog(true);
  };

  const confirmRestore = async () => {
    if (!voidTarget) return;
    setProcessing(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/warranties/${voidTarget.warranty_id}/restore`, {});
      toast.success('Certificate restored.');
      setShowRestoreDialog(false);
      setVoidTarget(null);
      fetchWarranties();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore blocked');
    } finally {
      setProcessing(false);
    }
  };

  const handleApproval = async () => {
    if (!selectedWarranty) return;
    
    // Validate duration on approval
    if (approvalAction === 'approve') {
      const mos = parseInt(warrantyDurationMonths, 10);
      if (!mos || mos < 5 || mos > 600) {
        toast.error(t('invalidWarrantyDuration'));
        return;
      }
    }

    setProcessing(true);
    try {
      const payload = {
        action: approvalAction,
        admin_notes: adminNotes,
      };
      if (approvalAction === 'approve') {
        payload.warranty_duration_months = parseInt(warrantyDurationMonths, 10);
        const reviewItems = (selectedWarranty.coverage_items || []).filter((i) => i.requires_review);
        if (reviewItems.length > 0) {
          const cc = {};
          for (const it of reviewItems) {
            const v = parseInt(customCredits[it.area_key], 10);
            if (!v || v < 0) { toast.error(`Assign a credit value for "${it.area_name_snapshot}"`); setProcessing(false); return; }
            cc[it.area_key] = v;
          }
          payload.custom_credits = cc;
        }
      }
      await axios.put(`${API_BASE_URL}/api/admin/warranties/${selectedWarranty.warranty_id}/approve`, payload);
      
      toast.success(
        approvalAction === 'approve' 
          ? 'Garantía aprobada exitosamente' 
          : 'Garantía rechazada'
      );
      setShowApprovalDialog(false);
      setSelectedWarranty(null);
      setAdminNotes('');
      setWarrantyDurationMonths(5);
      fetchWarranties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setProcessing(false);
    }
  };

  const openEditDialog = (warranty) => {
    setSelectedWarranty(warranty);
    setEditForm({
      client_name: warranty.client_name || '',
      vehicle_make: warranty.vehicle_make || '',
      vehicle_model: warranty.vehicle_model || '',
      vehicle_year: warranty.vehicle_year || '',
      vin_number: warranty.vin_number || '',
      vehicle_mileage: warranty.vehicle_mileage || '',
      installation_date: warranty.installation_date?.split('T')[0] || '',
      installation_location: warranty.installation_location || '',
      additional_notes: warranty.additional_notes || '',
      admin_notes: warranty.admin_notes || '',
      status: warranty.status || '',
      warranty_start_date: warranty.warranty_start_date?.split('T')[0] || '',
      warranty_end_date: warranty.warranty_end_date?.split('T')[0] || ''
    });
    setShowEditDialog(true);
  };

  const handleEdit = async () => {
    if (!selectedWarranty) return;
    
    setProcessing(true);
    try {
      await axios.put(`${API_BASE_URL}/api/admin/warranties/${selectedWarranty.warranty_id}`, editForm);
      toast.success(t('warrantyUpdatedSuccess'));
      setShowEditDialog(false);
      setSelectedWarranty(null);
      fetchWarranties();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorUpdatingWarranty'));
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteWarranty = async (warranty) => {
    if (!window.confirm(t('confirmDeleteWarranty'))) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/warranties/${warranty.warranty_id}`);
      toast.success(t('warrantyDeletedSuccess'));
      fetchWarranties();
      if (searchResults) {
        setSearchResults(searchResults.filter(w => w.warranty_id !== warranty.warranty_id));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorDeletingWarranty'));
    }
  };

  const getInstallationLocationLabel = (location) => {
    const locations = {
      'full_front': t('fullFront'),
      'partial_front': t('partialFront'),
      'full_body': t('fullBody'),
      'interior': t('interior'),
      'windshield': t('windshield'),
      'change_finish': t('changeFinish'),
      'full_wrap': t('fullWrap'),
      'protection_kit': t('protectionKit')
    };
    return locations[location] || location;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: t('pending'), class: 'tc-badge-warning', icon: '⏳' },
      'approved': { label: t('approvedTab'), class: 'tc-badge-success', icon: '✅' },
      'rejected': { label: t('rejectedTab'), class: 'tc-badge-error', icon: '❌' },
      'voided': { label: 'Voided', class: 'tc-badge-error', icon: '🚫' }
    };
    const statusInfo = statusMap[status] || { label: status, class: 'tc-badge-warning', icon: '❓' };
    return (
      <Badge className={`tc-badge ${statusInfo.class}`}>
        {statusInfo.icon} {statusInfo.label}
      </Badge>
    );
  };

  const WarrantyAdminCard = ({ warranty, showActions = true }) => (
    <Card className="tc-card hover-lift" data-testid={`warranty-card-${warranty.warranty_id}`}>
      <CardContent className="tc-card-content">
        <div className="flex justify-between items-start gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
              warranty.status === 'approved' ? 'bg-gradient-to-br from-green-500 to-green-600' :
              warranty.status === 'rejected' ? 'bg-gradient-to-br from-red-500 to-red-600' :
              'bg-gradient-to-br from-amber-500 to-amber-600'
            }`}>
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div className="space-y-3 flex-1 min-w-0">
              <div>
                <h4 className="text-lg font-semibold text-primary">
                  {warranty.vehicle_year} {warranty.vehicle_make} {warranty.vehicle_model}
                </h4>
                <p className="text-secondary text-sm">{t('clientLabel')}: {warranty.client_name}</p>
                <p className="text-muted text-xs">{t('vinLabel')}: {warranty.vin_number} | {t('mileageLabel')}: {warranty.vehicle_mileage?.toLocaleString() || 'N/A'} mi</p>
                <p className="text-muted text-xs">{t('distributorLabel')}: {warranty.user_company || 'N/A'}</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-muted block">{t('serialLabel')}:</span>
                  <p className="text-primary font-mono font-bold">{warranty.serial_number}</p>
                </div>
                <div>
                  <span className="text-muted block">{t('rollNumberShort')}:</span>
                  <p className="text-tc-gold font-mono font-bold" data-testid={`admin-warranty-roll-${warranty.warranty_id}`}>{warranty.roll_number || '—'}</p>
                </div>
                <div>
                  <span className="text-muted block">{t('installationLabel')}:</span>
                  <p className="text-primary font-medium">{new Date(warranty.installation_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-muted block">{t('areaLabel')}:</span>
                  <p className="text-primary font-medium">{warranty.coverage_summary || getInstallationLocationLabel(warranty.installation_location)}</p>
                </div>
                <div>
                  <span className="text-muted block">{t('requestedLabel')}:</span>
                  <p className="text-primary font-medium">{new Date(warranty.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              
              {warranty.additional_notes && (
                <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700">
                  <span className="text-muted text-xs">{t('applicantNotes')}:</span>
                  <p className="text-secondary text-sm italic">{warranty.additional_notes}</p>
                </div>
              )}

              {warranty.admin_notes && (
                <div className="bg-blue-900/30 p-3 rounded-lg border border-blue-500/30">
                  <span className="text-blue-400 text-xs font-medium">Comentarios del administrador:</span>
                  <p className="text-blue-300 text-sm">{warranty.admin_notes}</p>
                </div>
              )}

              {warranty.status === 'approved' && warranty.warranty_end_date && (
                <div className="bg-green-900/30 p-3 rounded-lg border border-green-500/30">
                  <span className="text-green-400 text-xs font-medium">Garantía válida hasta:</span>
                  <p className="text-green-300 text-sm font-medium">{new Date(warranty.warranty_end_date).toLocaleDateString()}</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="text-right flex-shrink-0">
            {getStatusBadge(warranty.status)}
            <p className="text-xs text-muted mt-2">
              Orden #{warranty.order_id?.substring(0, 8)}
            </p>
            
            {showActions && warranty.status === 'pending' && (
              <div className="flex flex-col gap-2 mt-4">
                <Button 
                  size="sm"
                  onClick={() => openApprovalDialog(warranty, 'approve')}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid={`approve-btn-${warranty.warranty_id}`}
                >
                  ✓ Aprobar
                </Button>
                <Button 
                  size="sm"
                  onClick={() => openApprovalDialog(warranty, 'reject')}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid={`reject-btn-${warranty.warranty_id}`}
                >
                  ✕ Rechazar
                </Button>
              </div>
            )}
            
            {/* Edit button for all statuses */}
            {showActions && (
              <Button 
                size="sm"
                onClick={() => openEditDialog(warranty)}
                className="tc-button-secondary mt-2"
                data-testid={`edit-btn-${warranty.warranty_id}`}
              >
                <Edit className="w-3 h-3 mr-1" /> {t('edit')}
              </Button>
            )}
            
            {/* Delete button for admins */}
            {showActions && (
              <Button 
                size="sm"
                onClick={() => handleDeleteWarranty(warranty)}
                className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white border-red-600 mt-2"
                data-testid={`delete-warranty-btn-${warranty.warranty_id}`}
              >
                <Trash className="w-3 h-3 mr-1" /> {t('delete')}
              </Button>
            )}

            {/* Void / Restore (TC Roll Credit lifecycle) */}
            {showActions && warranty.status === 'approved' && (
              <Button
                size="sm"
                onClick={() => handleVoid(warranty)}
                className="text-xs px-2 py-1 bg-orange-600 hover:bg-orange-700 text-white mt-2"
                data-testid={`void-warranty-btn-${warranty.warranty_id}`}
              >
                <X className="w-3 h-3 mr-1" /> Void
              </Button>
            )}
            {showActions && warranty.status === 'voided' && (
              <Button
                size="sm"
                onClick={() => handleRestore(warranty)}
                className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white mt-2"
                data-testid={`restore-warranty-btn-${warranty.warranty_id}`}
              >
                <RotateCcw className="w-3 h-3 mr-1" /> Restore
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading && warranties.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="tc-spinner"></div>
        <span className="ml-4 text-primary">{t('loadingWarrantiesAdmin')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-warranty-section">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-primary mb-2">
            {t('warrantyManagementAdmin')}
          </h2>
          <p className="text-secondary">{t('manageDistributorWarrantyRequests')}</p>
        </div>
        <div className="flex gap-4">
          <div className="text-center px-4 py-2 bg-amber-500/20 rounded-lg border border-amber-500/30">
            <p className="text-2xl font-bold text-amber-400">{statusCounts.pending}</p>
            <p className="text-xs text-muted">{t('pendingTab')}</p>
          </div>
          <div className="text-center px-4 py-2 bg-green-500/20 rounded-lg border border-green-500/30">
            <p className="text-2xl font-bold text-green-400">{statusCounts.approved}</p>
            <p className="text-xs text-muted">{t('approvedTab')}</p>
          </div>
          <div className="text-center px-4 py-2 bg-red-500/20 rounded-lg border border-red-500/30">
            <p className="text-2xl font-bold text-red-400">{statusCounts.rejected}</p>
            <p className="text-xs text-muted">{t('rejectedTab')}</p>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <Card className="tc-card">
        <CardContent className="tc-card-content">
          <h3 className="text-lg font-semibold text-primary mb-4">{t('searchWarrantiesAdmin')}</h3>
          <form onSubmit={handleSearch} className="flex gap-4 items-end">
            <div className="flex-1">
              <input
                className="tc-input tc-focus-ring w-full"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('searchBySerialVINClientVehicleDistributor')}
                data-testid="warranty-search-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={searching}
              className="tc-button-primary"
              data-testid="warranty-search-btn"
            >
              {searching ? t('searching') : t('search')}
            </Button>
            {searchResults !== null && (
              <Button 
                type="button" 
                onClick={clearSearch}
                className="tc-button-secondary"
              >
                {t('clear')}
              </Button>
            )}
          </form>
          
          {searchResults !== null && (
            <div className="mt-6">
              <h4 className="text-md font-medium text-primary mb-3">
                {t('searchResults')} ({searchResults.length})
              </h4>
              {searchResults.length === 0 ? (
                <p className="text-secondary">{t('noWarrantiesFoundCriteria')}</p>
              ) : (
                <div className="space-y-4">
                  {searchResults.map((warranty) => (
                    <WarrantyAdminCard key={warranty.warranty_id} warranty={warranty} />
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Tabs */}
      <div className="flex gap-2 border-b border-amber-500/20">
        <button
          onClick={() => { setActiveTab('pending'); clearSearch(); }}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'pending'
              ? 'text-amber-400 border-amber-400'
              : 'text-secondary border-transparent hover:text-primary'
          }`}
          data-testid="tab-pending"
        >
          {t('pendingTab')} ({statusCounts.pending})
        </button>
        <button
          onClick={() => { setActiveTab('approved'); clearSearch(); }}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'approved'
              ? 'text-green-400 border-green-400'
              : 'text-secondary border-transparent hover:text-primary'
          }`}
          data-testid="tab-approved"
        >
          {t('approvedTab')} ({statusCounts.approved})
        </button>
        <button
          onClick={() => { setActiveTab('rejected'); clearSearch(); }}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'rejected'
              ? 'text-red-400 border-red-400'
              : 'text-secondary border-transparent hover:text-primary'
          }`}
          data-testid="tab-rejected"
        >
          {t('rejectedTab')} ({statusCounts.rejected})
        </button>
        <button
          onClick={() => { setActiveTab('voided'); clearSearch(); }}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'voided'
              ? 'text-orange-400 border-orange-400'
              : 'text-secondary border-transparent hover:text-primary'
          }`}
          data-testid="tab-voided"
        >
          Voided
        </button>
        <button
          onClick={() => { setActiveTab('all'); clearSearch(); }}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'all'
              ? 'text-gold border-gold'
              : 'text-secondary border-transparent hover:text-primary'
          }`}
          data-testid="tab-all"
        >
          {t('allTab')}
        </button>
      </div>

      {/* Warranties List */}
      {!searchResults && (
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="tc-spinner"></div>
              <span className="ml-4 text-primary">{t('loading')}</span>
            </div>
          ) : warranties.length === 0 ? (
            <Card className="tc-card">
              <CardContent className="tc-card-content text-center py-12">
                <Shield className="w-16 h-16 text-muted mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-primary mb-2">
                  {activeTab === 'pending' ? t('noPendingWarranties') : 
                   activeTab === 'approved' ? t('noApprovedWarranties') : 
                   activeTab === 'rejected' ? t('noRejectedWarranties') : 
                   t('noWarrantiesWithStatus')}
                </h3>
                <p className="text-secondary">
                  {activeTab === 'pending' 
                    ? t('allRequestsProcessed')
                    : t('noWarrantiesWithStatus')}
                </p>
              </CardContent>
            </Card>
          ) : (
            warranties.map((warranty) => (
              <WarrantyAdminCard key={warranty.warranty_id} warranty={warranty} />
            ))
          )}
        </div>
      )}

      {/* Approval/Rejection Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="tc-dialog-content max-w-lg">
          <div className="tc-dialog-header">
            <DialogTitle className="tc-dialog-title">
              {approvalAction === 'approve' ? `✅ ${t('approveWarranty')}` : `❌ ${t('rejectWarranty')}`}
            </DialogTitle>
            <DialogDescription className="tc-dialog-description">
              {approvalAction === 'approve' 
                ? t('warrantyActivatedClientNotified')
                : t('warrantyRejectedProvideReason')}
            </DialogDescription>
          </div>
          
          {selectedWarranty && (
            <div className="tc-dialog-body space-y-4">
              {/* Warranty Summary */}
              <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                <h4 className="font-semibold text-primary mb-2">
                  {selectedWarranty.vehicle_year} {selectedWarranty.vehicle_make} {selectedWarranty.vehicle_model}
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted">Cliente:</span>
                    <p className="text-secondary">{selectedWarranty.client_name}</p>
                  </div>
                  <div>
                    <span className="text-muted">Serie:</span>
                    <p className="text-secondary font-mono">{selectedWarranty.serial_number}</p>
                  </div>
                  <div>
                    <span className="text-muted">{t('rollNumberShort')}:</span>
                    <p className="text-tc-gold font-mono">{selectedWarranty.roll_number || '—'}</p>
                  </div>
                  <div>
                    <span className="text-muted">VIN:</span>
                    <p className="text-secondary font-mono">{selectedWarranty.vin_number}</p>
                  </div>
                  <div>
                    <span className="text-muted">Instalación:</span>
                    <p className="text-secondary">{new Date(selectedWarranty.installation_date).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <span className="text-muted text-xs">Installation Coverage:</span>
                  <p className="text-tc-gold font-medium" data-testid="approve-coverage-summary">
                    {selectedWarranty.coverage_summary || getInstallationLocationLabel(selectedWarranty.installation_location)}
                  </p>
                  {selectedWarranty.capacity_consumed != null && (
                    <p className="text-secondary text-xs mt-1">TC Roll Credits: <span className="font-mono text-tc-gold">{selectedWarranty.capacity_consumed}</span></p>
                  )}
                </div>
              </div>

              {/* Custom / review coverage credit assignment */}
              {approvalAction === 'approve' && (selectedWarranty.coverage_items || []).some((i) => i.requires_review) && (
                <div className="bg-amber-900/20 p-4 rounded-lg border border-amber-500/40 space-y-3" data-testid="custom-credit-assign">
                  <p className="text-amber-300 text-sm font-medium">Assign credits for custom areas before approving:</p>
                  {(selectedWarranty.coverage_items || []).filter((i) => i.requires_review).map((it) => (
                    <div key={it.area_key} className="flex items-center gap-3">
                      <div className="flex-1">
                        <p className="text-amber-100 text-sm">{it.area_name_snapshot}</p>
                        {it.custom_description && <p className="text-amber-400/70 text-xs">{it.custom_description}</p>}
                      </div>
                      <Input type="number" min="0" className="tc-input tc-focus-ring w-24"
                        placeholder="credits"
                        data-testid={`custom-credit-${it.area_key}`}
                        value={customCredits[it.area_key] || ''}
                        onChange={(e) => setCustomCredits({ ...customCredits, [it.area_key]: e.target.value })} />
                    </div>
                  ))}
                </div>
              )}

              {/* Admin Notes Field */}
              <div>
                <label className="tc-label">
                  {approvalAction === 'approve' ? 'Comentarios (Opcional)' : 'Motivo del Rechazo *'}
                </label>
                <Textarea
                  className="tc-input tc-focus-ring min-h-[100px]"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder={
                    approvalAction === 'approve' 
                      ? 'Añada comentarios o instrucciones adicionales...'
                      : 'Explique el motivo del rechazo de esta garantía...'
                  }
                  data-testid="admin-notes-textarea"
                />
              </div>

              {approvalAction === 'approve' && (
                <div className="bg-green-900/30 p-4 rounded-lg border border-green-500/30 space-y-3">
                  <label className="tc-label block">
                    {t('warrantyDurationMonthsLabel')}
                  </label>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Input
                      type="number"
                      min="5"
                      max="600"
                      step="1"
                      value={warrantyDurationMonths}
                      onChange={(e) => setWarrantyDurationMonths(e.target.value)}
                      className="tc-input tc-focus-ring w-28"
                      data-testid="warranty-duration-months-input"
                    />
                    <span className="text-green-300 text-sm">{t('monthsWord')}</span>
                    <div className="flex flex-wrap gap-2 ml-auto">
                      {[
                        { m: 6, label: '6mo' },
                        { m: 12, label: '1y' },
                        { m: 24, label: '2y' },
                        { m: 60, label: '5y' },
                        { m: 84, label: '7y' },
                        { m: 120, label: '10y' },
                      ].map((p) => (
                        <button
                          key={p.m}
                          type="button"
                          onClick={() => setWarrantyDurationMonths(p.m)}
                          className={`px-3 py-1 text-xs rounded-full border transition ${
                            parseInt(warrantyDurationMonths, 10) === p.m
                              ? 'bg-green-600 border-green-400 text-white'
                              : 'bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700'
                          }`}
                          data-testid={`duration-preset-${p.m}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-green-400 text-xs">
                    {t('warrantyDurationHelper')}
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button"
                  onClick={() => setShowApprovalDialog(false)}
                  className="tc-button-secondary flex-1"
                  disabled={processing}
                >
                  Cancelar
                </Button>
                <Button 
                  type="button"
                  onClick={handleApproval}
                  disabled={processing || (approvalAction === 'reject' && !adminNotes.trim())}
                  className={`flex-1 ${
                    approvalAction === 'approve'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                  data-testid="confirm-approval-btn"
                >
                  {processing 
                    ? 'Procesando...' 
                    : approvalAction === 'approve' ? 'Confirmar Aprobación' : 'Confirmar Rechazo'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Void Certificate Dialog */}
      <Dialog open={showVoidDialog} onOpenChange={setShowVoidDialog}>
        <DialogContent className="tc-dialog-content max-w-lg">
          <div className="tc-dialog-header">
            <DialogTitle className="tc-dialog-title">Void Certificate</DialogTitle>
            <DialogDescription className="tc-dialog-description">
              The consumed TC Roll Credits will be returned to the roll. The record is preserved, not deleted.
            </DialogDescription>
          </div>
          <div className="tc-dialog-body space-y-4">
            {voidTarget && (
              <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700 text-sm">
                <p className="text-primary font-medium">{voidTarget.coverage_summary || voidTarget.installation_location}</p>
                <p className="text-muted text-xs mt-1">Roll {voidTarget.roll_number} · {voidTarget.capacity_consumed} credits</p>
              </div>
            )}
            <div>
              <label className="tc-label">Reason for voiding *</label>
              <Textarea
                className="tc-input tc-focus-ring min-h-[90px]"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Explain why this certificate is being voided…"
                data-testid="void-reason-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" onClick={() => setShowVoidDialog(false)} className="tc-button-secondary flex-1" disabled={processing}>Cancel</Button>
              <Button type="button" onClick={confirmVoid} disabled={processing || !voidReason.trim()}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" data-testid="confirm-void-btn">
                {processing ? 'Processing…' : 'Void Certificate'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Restore Certificate Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent className="tc-dialog-content max-w-lg">
          <div className="tc-dialog-header">
            <DialogTitle className="tc-dialog-title">Restore Certificate</DialogTitle>
            <DialogDescription className="tc-dialog-description">
              Roll capacity will be re-checked. Restoration is blocked if the credits are no longer available.
            </DialogDescription>
          </div>
          <div className="tc-dialog-body space-y-4">
            {voidTarget && (
              <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700 text-sm">
                <p className="text-primary font-medium">{voidTarget.coverage_summary || voidTarget.installation_location}</p>
                <p className="text-muted text-xs mt-1">Roll {voidTarget.roll_number} · needs {voidTarget.capacity_consumed} credits</p>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="button" onClick={() => setShowRestoreDialog(false)} className="tc-button-secondary flex-1" disabled={processing}>Cancel</Button>
              <Button type="button" onClick={confirmRestore} disabled={processing}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-restore-btn">
                {processing ? 'Processing…' : 'Restore Certificate'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Edit Warranty Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="tc-dialog-content max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="tc-dialog-header">
            <DialogTitle className="tc-dialog-title">Editar Garantía</DialogTitle>
            <DialogDescription className="tc-dialog-description">
              Modifique los detalles de la garantía
            </DialogDescription>
          </div>
          
          {selectedWarranty && (
            <div className="tc-dialog-body space-y-4">
              {/* Serial Number (read-only) */}
              <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700">
                <span className="text-muted text-xs">{t('serialLabel')}:</span>
                <p className="text-primary font-mono font-bold">{selectedWarranty.serial_number}</p>
              </div>

              <div className="bg-slate-800/50 p-3 rounded-lg border border-tc-gold/40">
                <span className="text-muted text-xs">{t('rollNumberRequired').replace(' *','')}:</span>
                <p className="text-tc-gold font-mono font-bold" data-testid="approve-modal-roll-number">{selectedWarranty.roll_number || '—'}</p>
              </div>

              {/* Status */}
              <div>
                <label className="tc-label">Estado</label>
                <select
                  className="tc-select tc-focus-ring w-full"
                  value={editForm.status}
                  onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                >
                  <option value="pending">Pendiente</option>
                  <option value="approved">Aprobada</option>
                  <option value="rejected">Rechazada</option>
                </select>
              </div>

              {/* Client Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="tc-label">Nombre del Cliente</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={editForm.client_name}
                    onChange={(e) => setEditForm({...editForm, client_name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="tc-label">VIN</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={editForm.vin_number}
                    onChange={(e) => setEditForm({...editForm, vin_number: e.target.value.toUpperCase()})}
                  />
                </div>
                <div>
                  <label className="tc-label">{t('mileageLabel')}</label>
                  <input
                    type="number"
                    min="0"
                    className="tc-input tc-focus-ring"
                    value={editForm.vehicle_mileage}
                    onChange={(e) => setEditForm({...editForm, vehicle_mileage: parseInt(e.target.value) || ''})}
                    data-testid="edit-warranty-mileage-input"
                  />
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="tc-label">Marca</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={editForm.vehicle_make}
                    onChange={(e) => setEditForm({...editForm, vehicle_make: e.target.value})}
                  />
                </div>
                <div>
                  <label className="tc-label">Modelo</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={editForm.vehicle_model}
                    onChange={(e) => setEditForm({...editForm, vehicle_model: e.target.value})}
                  />
                </div>
                <div>
                  <label className="tc-label">Año</label>
                  <input
                    type="number"
                    className="tc-input tc-focus-ring"
                    value={editForm.vehicle_year}
                    onChange={(e) => setEditForm({...editForm, vehicle_year: parseInt(e.target.value)})}
                  />
                </div>
              </div>

              {/* Installation Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="tc-label">Fecha de Instalación</label>
                  <input
                    type="date"
                    className="tc-input tc-focus-ring"
                    value={editForm.installation_date}
                    onChange={(e) => setEditForm({...editForm, installation_date: e.target.value})}
                  />
                </div>
                <div>
                  <label className="tc-label">Área de Instalación</label>
                  <select
                    className="tc-select tc-focus-ring w-full"
                    value={editForm.installation_location}
                    onChange={(e) => setEditForm({...editForm, installation_location: e.target.value})}
                  >
                    <option value="full_front">Frente Completo</option>
                    <option value="partial_front">Frente Parcial</option>
                    <option value="full_body">Carrocería Completa</option>
                    <option value="interior">Interior</option>
                    <option value="windshield">Parabrisas</option>
                    <option value="change_finish">Cambio de Acabado</option>
                    <option value="full_wrap">Envoltura Completa</option>
                    <option value="protection_kit">Kit de Protección</option>
                  </select>
                </div>
              </div>

              {/* Warranty Dates (only for approved) */}
              {editForm.status === 'approved' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="tc-label">Inicio de Garantía</label>
                    <input
                      type="date"
                      className="tc-input tc-focus-ring"
                      value={editForm.warranty_start_date}
                      onChange={(e) => setEditForm({...editForm, warranty_start_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="tc-label">Fin de Garantía</label>
                    <input
                      type="date"
                      className="tc-input tc-focus-ring"
                      value={editForm.warranty_end_date}
                      onChange={(e) => setEditForm({...editForm, warranty_end_date: e.target.value})}
                    />
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="tc-label">Notas del Cliente</label>
                <Textarea
                  className="tc-input tc-focus-ring min-h-[60px]"
                  value={editForm.additional_notes}
                  onChange={(e) => setEditForm({...editForm, additional_notes: e.target.value})}
                />
              </div>

              <div>
                <label className="tc-label">Notas del Administrador</label>
                <Textarea
                  className="tc-input tc-focus-ring min-h-[60px]"
                  value={editForm.admin_notes}
                  onChange={(e) => setEditForm({...editForm, admin_notes: e.target.value})}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button"
                  onClick={() => setShowEditDialog(false)}
                  className="tc-button-secondary flex-1"
                  disabled={processing}
                >
                  Cancelar
                </Button>
                <Button 
                  type="button"
                  onClick={handleEdit}
                  className="tc-button-primary flex-1"
                  disabled={processing}
                >
                  {processing ? t('saving') : t('save')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Software Section (For all users)
const SoftwareSection = ({ user }) => {
  const { t } = useLanguage();
  // Check if user is admin
  const isAdmin = user?.role === 'tc_admin';
  
  // Admin state
  const [requests, setRequests] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [adminActiveTab, setAdminActiveTab] = useState('pending');
  const [renewalData, setRenewalData] = useState(null);
  const RENEWAL_TAB_KEY = { active: 'active', expiring: 'expiring_soon', expired: 'expired' };
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [approvalAction, setApprovalAction] = useState('approve');
  const [processing, setProcessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  
  // Approval form state
  const [approvalForm, setApprovalForm] = useState({
    plan_type: 'Basic',
    username: '',
    password: '',
    subscription_start: new Date().toISOString().split('T')[0],
    subscription_end: '',
    admin_notes: ''
  });
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    plan_type: '',
    username: '',
    password: '',
    subscription_start: '',
    subscription_end: '',
    admin_notes: '',
    status: ''
  });

  // Direct-create software account (admin skips request flow)
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createForm, setCreateForm] = useState({
    business_name: '',
    contact_person: '',
    email: '',
    phone: '',
    shop_type: 'body_shop',
    plan_type: 'Basic',
    username: '',
    password: '',
    subscription_start: new Date().toISOString().split('T')[0],
    subscription_end: '',
    admin_notes: '',
  });

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!createForm.subscription_end) {
      toast.error(t('subscriptionEndRequired'));
      return;
    }
    setCreateSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/software-accounts`, {
        ...createForm,
        subscription_start: new Date(createForm.subscription_start).toISOString(),
        subscription_end: new Date(createForm.subscription_end).toISOString(),
      });
      toast.success(t('softwareAccountCreatedSuccess'));
      setShowCreateAccount(false);
      setCreateForm({
        business_name: '', contact_person: '', email: '', phone: '',
        shop_type: 'body_shop', plan_type: 'Basic', username: '', password: '',
        subscription_start: new Date().toISOString().split('T')[0],
        subscription_end: '', admin_notes: '',
      });
      fetchAllRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorCreatingSoftwareAccount'));
    } finally {
      setCreateSaving(false);
    }
  };
  
  // Client state
  const [myRequest, setMyRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({
    business_name: user?.company_name || '',
    contact_person: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    shop_type: '',
    current_software: '',
    monthly_volume: '',
    additional_info: ''
  });

  useEffect(() => {
    if (isAdmin) {
      fetchAllRequests();
      fetchRenewalData();
    } else {
      fetchMyRequest();
    }
  }, [isAdmin, adminActiveTab]);

  const fetchRenewalData = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/software-accounts/renewal-status`);
      setRenewalData(res.data);
    } catch (error) {
      /* non-blocking */
    }
  };

  const fetchAllRequests = async () => {
    setLoading(true);
    try {
      const lifecycle = ['active', 'expiring', 'expired'].includes(adminActiveTab);
      const status = lifecycle ? 'approved' : (adminActiveTab === 'all' ? '' : adminActiveTab);
      const response = await axios.get(`${API_BASE_URL}/api/admin/software-requests${status ? `?status=${status}` : ''}`);
      setRequests(response.data.requests || []);
      setStatusCounts(response.data.status_counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (error) {
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRequest = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/software-requests/my-request`);
      setMyRequest(response.data);
    } catch (error) {
      console.error('No request found');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/api/software-requests`, requestForm);
      toast.success('¡Solicitud enviada exitosamente! Será revisada por nuestro equipo.');
      setShowRequestForm(false);
      fetchMyRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || searchQuery.length < 2) {
      toast.error('Ingresa al menos 2 caracteres para buscar');
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/software-requests/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Error al buscar');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const openApprovalDialog = (request, action) => {
    setSelectedRequest(request);
    setApprovalAction(action);
    setApprovalForm({
      plan_type: 'Basic',
      username: '',
      password: '',
      subscription_start: new Date().toISOString().split('T')[0],
      subscription_end: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      admin_notes: ''
    });
    setShowApprovalDialog(true);
  };

  const openEditDialog = (request) => {
    setSelectedRequest(request);
    setEditForm({
      plan_type: request.plan_type || '',
      username: request.software_username || '',
      password: request.software_password || '',
      subscription_start: request.subscription_start?.split('T')[0] || '',
      subscription_end: request.subscription_end?.split('T')[0] || '',
      admin_notes: request.admin_notes || '',
      status: request.status || ''
    });
    setShowEditDialog(true);
  };

  const handleApproval = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      const payload = {
        action: approvalAction,
        admin_notes: approvalForm.admin_notes
      };
      
      if (approvalAction === 'approve') {
        payload.plan_type = approvalForm.plan_type;
        payload.username = approvalForm.username;
        payload.password = approvalForm.password;
        payload.subscription_start = approvalForm.subscription_start;
        payload.subscription_end = approvalForm.subscription_end;
      }
      
      await axios.put(`${API_BASE_URL}/api/admin/software-requests/${selectedRequest.request_id}/approve`, payload);
      toast.success(approvalAction === 'approve' ? t('accessApprovedSuccess') : t('requestRejectedNotification'));
      setShowApprovalDialog(false);
      fetchAllRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorProcessingRequestSoftware'));
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      await axios.put(`${API_BASE_URL}/api/admin/software-requests/${selectedRequest.request_id}`, {
        plan_type: editForm.plan_type,
        username: editForm.username,
        password: editForm.password,
        subscription_start: editForm.subscription_start,
        subscription_end: editForm.subscription_end,
        admin_notes: editForm.admin_notes,
        status: editForm.status
      });
      toast.success(t('subscriptionUpdatedSuccess'));
      setShowEditDialog(false);
      fetchAllRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorUpdatingSoftware'));
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteSoftwareRequest = async (request) => {
    if (!window.confirm(t('confirmDeleteSoftwareRequest'))) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/software-requests/${request.request_id}`);
      toast.success(t('softwareRequestDeletedSuccess'));
      fetchAllRequests();
      if (searchResults) {
        setSearchResults(searchResults.filter(r => r.request_id !== request.request_id));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorDeletingSoftwareRequest'));
    }
  };

  const getShopTypeLabel = (type) => {
    const types = {
      'auto_detailing': t('autoDetailing'),
      'tint_shop': t('tintShop'),
      'body_shop': t('bodyShop'),
      'dealership': t('dealership'),
      'mobile_service': t('mobileService'),
      'other': t('other')
    };
    return types[type] || type;
  };

  const getPlanBadge = (plan) => {
    const plans = {
      'Basic': { class: 'bg-blue-500', label: t('basicPlan').split(' - ')[0] },
      'Pro': { class: 'bg-purple-500', label: 'Professional' },
      'Enterprise': { class: 'bg-amber-500', label: 'Enterprise' }
    };
    const planInfo = plans[plan] || { class: 'bg-gray-500', label: plan };
    return <Badge className={`${planInfo.class} text-white`}>{planInfo.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const statuses = {
      'pending': { class: 'tc-badge-warning', label: t('pending'), icon: '⏳' },
      'approved': { class: 'tc-badge-success', label: t('approvedTab'), icon: '✅' },
      'rejected': { class: 'tc-badge-error', label: t('rejectedTab'), icon: '❌' }
    };
    const statusInfo = statuses[status] || { class: 'tc-badge-warning', label: status, icon: '❓' };
    return <Badge className={`tc-badge ${statusInfo.class}`}>{statusInfo.icon} {statusInfo.label}</Badge>;
  };

  // Software Request Card component (shared by admin and client views)
  const SoftwareRequestCard = ({ request, onApprove, onEdit, onDelete }) => (
    <Card className="tc-card hover-lift">
      <CardContent className="tc-card-content">
        <div className="flex justify-between items-start gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
              request.status === 'approved' ? 'bg-gradient-to-br from-green-500 to-green-600' :
              request.status === 'rejected' ? 'bg-gradient-to-br from-red-500 to-red-600' :
              'bg-gradient-to-br from-amber-500 to-amber-600'
            }`}>
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div className="space-y-2 flex-1">
              <div>
                <h4 className="text-lg font-semibold text-primary">{request.business_name}</h4>
                <p className="text-secondary text-sm">{request.contact_person} - {request.email}</p>
                <p className="text-muted text-xs">{getShopTypeLabel(request.shop_type)} • {request.monthly_volume} vehículos/mes</p>
              </div>
              
              {request.status === 'approved' && (
                <div className="bg-green-900/30 p-3 rounded-lg border border-green-500/30 space-y-1">
                  <div className="flex items-center gap-4">
                    {getPlanBadge(request.plan_type)}
                    <span className="text-green-400 text-sm">Usuario: <strong>{request.software_username}</strong></span>
                    <span className="text-green-400 text-sm">Contraseña: <strong>{request.software_password}</strong></span>
                  </div>
                  {request.subscription_end && (
                    <p className="text-green-400 text-xs">Válido hasta: {new Date(request.subscription_end).toLocaleDateString()}</p>
                  )}
                </div>
              )}
              
              {request.admin_notes && (
                <div className="bg-blue-900/30 p-2 rounded border border-blue-500/30">
                  <span className="text-blue-400 text-xs">Notas: </span>
                  <span className="text-blue-300 text-sm">{request.admin_notes}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="text-right flex-shrink-0">
            {getStatusBadge(request.status)}
            <p className="text-xs text-muted mt-2">{new Date(request.created_at).toLocaleDateString()}</p>
            
            {request.status === 'pending' && (
              <div className="flex flex-col gap-2 mt-4">
                <Button size="sm" onClick={() => onApprove(request, 'approve')} className="bg-green-600 hover:bg-green-700 text-white">
                  ✓ {t('approveBtn')}
                </Button>
                <Button size="sm" onClick={() => onApprove(request, 'reject')} className="bg-red-600 hover:bg-red-700 text-white">
                  ✕ {t('rejectBtn')}
                </Button>
              </div>
            )}
            
            {request.status === 'approved' && (
              <Button size="sm" onClick={() => onEdit(request)} className="tc-button-secondary mt-4">
                <Edit className="w-3 h-3 mr-1" /> {t('edit')}
              </Button>
            )}
            
            {/* Delete button for all statuses */}
            <Button 
              size="sm" 
              onClick={() => onDelete(request)} 
              className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white border-red-600 mt-2"
              data-testid={`delete-software-btn-${request.request_id}`}
            >
              <Trash className="w-3 h-3 mr-1" /> {t('delete')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Admin View
  if (isAdmin) {
    return (
      <div className="space-y-8" data-testid="admin-software-section">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-primary mb-2">
              Gestión de Software TC CutPro
            </h2>
            <p className="text-secondary">Administra las solicitudes de acceso al software</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setShowCreateAccount(true)}
              className="tc-button-primary"
              data-testid="create-software-account-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('createSoftwareAccountBtn')}
            </Button>
            <div className="text-center px-4 py-2 bg-amber-500/20 rounded-lg border border-amber-500/30">
              <p className="text-2xl font-bold text-amber-400">{statusCounts.pending}</p>
              <p className="text-xs text-muted">Pendientes</p>
            </div>
            <div className="text-center px-4 py-2 bg-green-500/20 rounded-lg border border-green-500/30">
              <p className="text-2xl font-bold text-green-400">{statusCounts.approved}</p>
              <p className="text-xs text-muted">Activas</p>
            </div>
            <div className="text-center px-4 py-2 bg-red-500/20 rounded-lg border border-red-500/30">
              <p className="text-2xl font-bold text-red-400">{statusCounts.rejected}</p>
              <p className="text-xs text-muted">Rechazadas</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <Card className="tc-card">
          <CardContent className="tc-card-content">
            <h3 className="text-lg font-semibold text-primary mb-4">Buscar Suscripciones</h3>
            <form onSubmit={handleSearch} className="flex gap-4 items-end">
              <div className="flex-1">
                <input
                  className="tc-input tc-focus-ring w-full"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por nombre, email, empresa o usuario..."
                  data-testid="software-search-input"
                />
              </div>
              <Button type="submit" disabled={searching} className="tc-button-primary">
                {searching ? 'Buscando...' : 'Buscar'}
              </Button>
              {searchResults !== null && (
                <Button type="button" onClick={clearSearch} className="tc-button-secondary">
                  Limpiar
                </Button>
              )}
            </form>
            
            {searchResults !== null && (
              <div className="mt-6">
                <h4 className="text-md font-medium text-primary mb-3">
                  Resultados ({searchResults.length})
                </h4>
                {searchResults.length === 0 ? (
                  <p className="text-secondary">No se encontraron resultados.</p>
                ) : (
                  <div className="space-y-4">
                    {searchResults.map((req) => (
                      <SoftwareRequestCard key={req.request_id} request={req} onApprove={openApprovalDialog} onEdit={openEditDialog} onDelete={handleDeleteSoftwareRequest} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Tabs */}
        <div className="flex gap-2 border-b border-amber-500/20 flex-wrap" data-testid="software-status-tabs">
          <button
            onClick={() => { setAdminActiveTab('pending'); clearSearch(); }}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              adminActiveTab === 'pending' ? 'text-amber-400 border-amber-400' : 'text-secondary border-transparent hover:text-primary'
            }`}
            data-testid="tab-pending"
          >
            {t('swTabPending')} ({statusCounts.pending})
          </button>
          <button
            onClick={() => { setAdminActiveTab('active'); clearSearch(); }}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              adminActiveTab === 'active' ? 'text-green-400 border-green-400' : 'text-secondary border-transparent hover:text-primary'
            }`}
            data-testid="tab-active"
          >
            {t('swTabActive')} ({renewalData ? (renewalData.counts.active + renewalData.counts.no_end_date) : 0})
          </button>
          <button
            onClick={() => { setAdminActiveTab('expiring'); clearSearch(); }}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              adminActiveTab === 'expiring' ? 'text-amber-300 border-amber-300' : 'text-secondary border-transparent hover:text-primary'
            }`}
            data-testid="tab-expiring"
          >
            {t('swTabExpiring')} ({renewalData ? renewalData.counts.expiring_soon : 0})
          </button>
          <button
            onClick={() => { setAdminActiveTab('expired'); clearSearch(); }}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              adminActiveTab === 'expired' ? 'text-red-400 border-red-400' : 'text-secondary border-transparent hover:text-primary'
            }`}
            data-testid="tab-expired"
          >
            {t('swTabExpired')} ({renewalData ? renewalData.counts.expired : 0})
          </button>
          <button
            onClick={() => { setAdminActiveTab('rejected'); clearSearch(); }}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              adminActiveTab === 'rejected' ? 'text-red-400 border-red-400' : 'text-secondary border-transparent hover:text-primary'
            }`}
            data-testid="tab-rejected"
          >
            {t('swTabRejected')} ({statusCounts.rejected})
          </button>
          <button
            onClick={() => { setAdminActiveTab('all'); clearSearch(); }}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              adminActiveTab === 'all' ? 'text-gold border-gold' : 'text-secondary border-transparent hover:text-primary'
            }`}
            data-testid="tab-all"
          >
            {t('swTabAll')}
          </button>
        </div>

        {/* Requests / Accounts List */}
        {!searchResults && (() => {
          const isLifecycle = ['active', 'expiring', 'expired'].includes(adminActiveTab);
          let displayList = requests;
          if (isLifecycle && renewalData) {
            displayList = renewalData[RENEWAL_TAB_KEY[adminActiveTab]] || [];
            if (adminActiveTab === 'active') displayList = [...displayList, ...(renewalData.no_end_date || [])];
          }
          return (
          <div className="space-y-4" data-testid="software-accounts-list">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="tc-spinner"></div>
                <span className="ml-4 text-primary">{t("loading")}</span>
              </div>
            ) : displayList.length === 0 ? (
              <Card className="tc-card">
                <CardContent className="tc-card-content text-center py-12">
                  <Settings className="w-16 h-16 text-muted mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-primary mb-2">
                    {t('swNoAccounts')}
                  </h3>
                </CardContent>
              </Card>
            ) : (
              displayList.map((req) => (
                <SoftwareRequestCard key={req.request_id} request={req} onApprove={openApprovalDialog} onEdit={openEditDialog} onDelete={handleDeleteSoftwareRequest} />
              ))
            )}
          </div>
          );
        })()}


        {/* Create Software Account Dialog (Admin - skips request flow) */}
        <Dialog open={showCreateAccount} onOpenChange={(open) => { if (!open) setShowCreateAccount(false); }}>
          <DialogContent className="tc-dialog-content max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-software-account-dialog">
            <div className="tc-dialog-header">
              <DialogTitle className="tc-dialog-title flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-400" />
                {t('createSoftwareAccountTitle')}
              </DialogTitle>
              <DialogDescription className="tc-dialog-description">
                {t('createSoftwareAccountSubtitle')}
              </DialogDescription>
            </div>
            <form onSubmit={handleCreateAccount} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="tc-label">{t('businessNameLabel')} *</label>
                  <Input type="text" required value={createForm.business_name}
                    onChange={(e) => setCreateForm({...createForm, business_name: e.target.value})}
                    className="tc-input" data-testid="create-sw-business" />
                </div>
                <div>
                  <label className="tc-label">{t('contactPersonLabel')} *</label>
                  <Input type="text" required value={createForm.contact_person}
                    onChange={(e) => setCreateForm({...createForm, contact_person: e.target.value})}
                    className="tc-input" data-testid="create-sw-contact" />
                </div>
                <div>
                  <label className="tc-label">Email *</label>
                  <Input type="email" required value={createForm.email}
                    onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                    className="tc-input" data-testid="create-sw-email" />
                </div>
                <div>
                  <label className="tc-label">{t('phoneLabel')}</label>
                  <Input type="text" value={createForm.phone}
                    onChange={(e) => setCreateForm({...createForm, phone: e.target.value})}
                    className="tc-input" />
                </div>
                <div>
                  <label className="tc-label">{t('planTypeLabel')} *</label>
                  <select className="tc-select" value={createForm.plan_type}
                    onChange={(e) => setCreateForm({...createForm, plan_type: e.target.value})}
                    data-testid="create-sw-plan">
                    <option value="Basic">Basic</option>
                    <option value="Pro">Pro</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="tc-label">{t('shopTypeLabel') || 'Shop Type'}</label>
                  <select className="tc-select" value={createForm.shop_type}
                    onChange={(e) => setCreateForm({...createForm, shop_type: e.target.value})}>
                    <option value="body_shop">Body Shop</option>
                    <option value="tint_shop">Tint Shop</option>
                    <option value="auto_detailing">Auto Detailing</option>
                    <option value="dealership">Dealership</option>
                    <option value="mobile_service">Mobile Service</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="tc-label">{t('softwareUsernameLabel')} *</label>
                  <Input type="text" required value={createForm.username}
                    onChange={(e) => setCreateForm({...createForm, username: e.target.value})}
                    className="tc-input" data-testid="create-sw-username" />
                </div>
                <div>
                  <label className="tc-label">{t('softwarePasswordLabel')} *</label>
                  <Input type="text" required value={createForm.password}
                    onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                    className="tc-input" data-testid="create-sw-password" />
                </div>
                <div>
                  <label className="tc-label">{t('subscriptionStartLabel')}</label>
                  <Input type="date" value={createForm.subscription_start}
                    onChange={(e) => setCreateForm({...createForm, subscription_start: e.target.value})}
                    className="tc-input" />
                </div>
                <div>
                  <label className="tc-label">{t('subscriptionEndLabel')} *</label>
                  <Input type="date" required value={createForm.subscription_end}
                    onChange={(e) => setCreateForm({...createForm, subscription_end: e.target.value})}
                    className="tc-input" data-testid="create-sw-end" />
                </div>
              </div>
              <div>
                <label className="tc-label">{t('adminNotesLabel') || 'Admin Notes'}</label>
                <textarea rows={2} value={createForm.admin_notes}
                  onChange={(e) => setCreateForm({...createForm, admin_notes: e.target.value})}
                  className="tc-input w-full" />
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-slate-700">
                <Button type="button" variant="outline" onClick={() => setShowCreateAccount(false)}
                  className="tc-button-secondary" disabled={createSaving}>
                  {t('cancel')}
                </Button>
                <Button type="submit" className="tc-button-primary"
                  disabled={createSaving} data-testid="create-sw-submit-btn">
                  {createSaving ? t('saving') : t('createAccountBtn')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>


        {/* Approval Dialog */}
        <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
          <DialogContent className="tc-dialog-content max-w-lg">
            <div className="tc-dialog-header">
              <DialogTitle className="tc-dialog-title">
                {approvalAction === 'approve' ? '✅ Aprobar Acceso' : '❌ Rechazar Solicitud'}
              </DialogTitle>
              <DialogDescription className="tc-dialog-description">
                {approvalAction === 'approve' 
                  ? 'Configure los detalles de la suscripción'
                  : 'Proporcione un motivo para el rechazo'}
              </DialogDescription>
            </div>
            
            {selectedRequest && (
              <div className="tc-dialog-body space-y-4">
                {/* Request Summary */}
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                  <h4 className="font-semibold text-primary mb-2">{selectedRequest.business_name}</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted">Contacto:</span> <span className="text-secondary">{selectedRequest.contact_person}</span></div>
                    <div><span className="text-muted">Email:</span> <span className="text-secondary">{selectedRequest.email}</span></div>
                    <div><span className="text-muted">Tipo:</span> <span className="text-secondary">{getShopTypeLabel(selectedRequest.shop_type)}</span></div>
                    <div><span className="text-muted">Volumen:</span> <span className="text-secondary">{selectedRequest.monthly_volume}</span></div>
                  </div>
                </div>

                {approvalAction === 'approve' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="tc-label">Plan</label>
                        <select
                          className="tc-select tc-focus-ring w-full"
                          value={approvalForm.plan_type}
                          onChange={(e) => setApprovalForm({...approvalForm, plan_type: e.target.value})}
                        >
                          <option value="Basic">Básico - $299/mes</option>
                          <option value="Pro">Professional - $599/mes</option>
                          <option value="Enterprise">Enterprise - Personalizado</option>
                        </select>
                      </div>
                      <div>
                        <label className="tc-label">Usuario *</label>
                        <input
                          className="tc-input tc-focus-ring"
                          value={approvalForm.username}
                          onChange={(e) => setApprovalForm({...approvalForm, username: e.target.value})}
                          placeholder="usuario_cutpro"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="tc-label">Contraseña *</label>
                      <input
                        className="tc-input tc-focus-ring"
                        value={approvalForm.password}
                        onChange={(e) => setApprovalForm({...approvalForm, password: e.target.value})}
                        placeholder="Contraseña para el software"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="tc-label">Inicio Suscripción</label>
                        <input
                          type="date"
                          className="tc-input tc-focus-ring"
                          value={approvalForm.subscription_start}
                          onChange={(e) => setApprovalForm({...approvalForm, subscription_start: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="tc-label">Fin Suscripción</label>
                        <input
                          type="date"
                          className="tc-input tc-focus-ring"
                          value={approvalForm.subscription_end}
                          onChange={(e) => setApprovalForm({...approvalForm, subscription_end: e.target.value})}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label className="tc-label">{approvalAction === 'approve' ? 'Notas (Opcional)' : 'Motivo del Rechazo *'}</label>
                  <Textarea
                    className="tc-input tc-focus-ring min-h-[80px]"
                    value={approvalForm.admin_notes}
                    onChange={(e) => setApprovalForm({...approvalForm, admin_notes: e.target.value})}
                    placeholder={approvalAction === 'approve' ? 'Notas adicionales...' : 'Explique el motivo...'}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" onClick={() => setShowApprovalDialog(false)} className="tc-button-secondary flex-1" disabled={processing}>
                    Cancelar
                  </Button>
                  <Button 
                    type="button" 
                    onClick={handleApproval}
                    disabled={processing || (approvalAction === 'approve' && (!approvalForm.username || !approvalForm.password)) || (approvalAction === 'reject' && !approvalForm.admin_notes.trim())}
                    className={`flex-1 ${approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
                  >
                    {processing ? 'Procesando...' : approvalAction === 'approve' ? 'Aprobar' : 'Rechazar'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="tc-dialog-content max-w-lg">
            <div className="tc-dialog-header">
              <DialogTitle className="tc-dialog-title">Editar Suscripción</DialogTitle>
              <DialogDescription className="tc-dialog-description">
                Actualice los detalles de la suscripción
              </DialogDescription>
            </div>
            
            {selectedRequest && (
              <div className="tc-dialog-body space-y-4">
                <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700">
                  <p className="text-primary font-medium">{selectedRequest.business_name}</p>
                  <p className="text-secondary text-sm">{selectedRequest.contact_person} - {selectedRequest.email}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="tc-label">Plan</label>
                    <select
                      className="tc-select tc-focus-ring w-full"
                      value={editForm.plan_type}
                      onChange={(e) => setEditForm({...editForm, plan_type: e.target.value})}
                    >
                      <option value="Basic">Básico</option>
                      <option value="Pro">Professional</option>
                      <option value="Enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div>
                    <label className="tc-label">Estado</label>
                    <select
                      className="tc-select tc-focus-ring w-full"
                      value={editForm.status}
                      onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                    >
                      <option value="pending">Pendiente</option>
                      <option value="approved">Aprobado</option>
                      <option value="rejected">Rechazado</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="tc-label">{t("username")}</label>
                    <input
                      className="tc-input tc-focus-ring"
                      value={editForm.username}
                      onChange={(e) => setEditForm({...editForm, username: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="tc-label">{t("password")}</label>
                    <input
                      className="tc-input tc-focus-ring"
                      value={editForm.password}
                      onChange={(e) => setEditForm({...editForm, password: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="tc-label">Inicio</label>
                    <input
                      type="date"
                      className="tc-input tc-focus-ring"
                      value={editForm.subscription_start}
                      onChange={(e) => setEditForm({...editForm, subscription_start: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="tc-label">Fin</label>
                    <input
                      type="date"
                      className="tc-input tc-focus-ring"
                      value={editForm.subscription_end}
                      onChange={(e) => setEditForm({...editForm, subscription_end: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <label className="tc-label">Notas</label>
                  <Textarea
                    className="tc-input tc-focus-ring min-h-[80px]"
                    value={editForm.admin_notes}
                    onChange={(e) => setEditForm({...editForm, admin_notes: e.target.value})}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" onClick={() => setShowEditDialog(false)} className="tc-button-secondary flex-1" disabled={processing}>
                    Cancelar
                  </Button>
                  <Button type="button" onClick={handleEdit} className="tc-button-primary flex-1" disabled={processing}>
                    {processing ? t('saving') : t('save')}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Client View
  return (
    <div className="space-y-8">
      {/* Show subscription status if exists */}
      {myRequest && (
        <Card className={`tc-card border-2 ${
          myRequest.status === 'approved' ? 'border-green-500/50' :
          myRequest.status === 'rejected' ? 'border-red-500/50' :
          'border-amber-500/50'
        }`}>
          <CardContent className="tc-card-content">
            <div className="flex items-start gap-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                myRequest.status === 'approved' ? 'bg-gradient-to-br from-green-500 to-green-600' :
                myRequest.status === 'rejected' ? 'bg-gradient-to-br from-red-500 to-red-600' :
                'bg-gradient-to-br from-amber-500 to-amber-600'
              }`}>
                <Settings className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-bold text-primary">Estado de tu Solicitud</h3>
                  {getStatusBadge(myRequest.status)}
                </div>
                
                {myRequest.status === 'pending' && (
                  <p className="text-secondary">Tu solicitud está siendo revisada por nuestro equipo. Te notificaremos cuando sea procesada.</p>
                )}
                
                {myRequest.status === 'approved' && (
                  <div className="space-y-4">
                    <p className="text-green-400">¡Tu acceso ha sido aprobado! Usa las siguientes credenciales:</p>
                    <div className="bg-green-900/30 p-4 rounded-lg border border-green-500/30">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <span className="text-muted text-sm">Plan:</span>
                          <p className="text-primary font-semibold">{myRequest.plan_type}</p>
                        </div>
                        <div>
                          <span className="text-muted text-sm">Válido hasta:</span>
                          <p className="text-primary font-semibold">
                            {myRequest.subscription_end ? new Date(myRequest.subscription_end).toLocaleDateString() : 'Sin fecha límite'}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted text-sm">Usuario:</span>
                          <p className="text-green-400 font-mono font-bold text-lg">{myRequest.software_username}</p>
                        </div>
                        <div>
                          <span className="text-muted text-sm">Contraseña:</span>
                          <p className="text-green-400 font-mono font-bold text-lg">{myRequest.software_password}</p>
                        </div>
                      </div>
                    </div>
                    {myRequest.admin_notes && (
                      <div className="bg-blue-900/30 p-3 rounded-lg border border-blue-500/30">
                        <span className="text-blue-400 text-sm font-medium">Mensaje del administrador:</span>
                        <p className="text-blue-300">{myRequest.admin_notes}</p>
                      </div>
                    )}
                  </div>
                )}
                
                {myRequest.status === 'rejected' && (
                  <div className="space-y-2">
                    <p className="text-red-400">Tu solicitud fue rechazada.</p>
                    {myRequest.admin_notes && (
                      <div className="bg-red-900/30 p-3 rounded-lg border border-red-500/30">
                        <span className="text-red-400 text-sm font-medium">Motivo:</span>
                        <p className="text-red-300">{myRequest.admin_notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-gray-900 to-black border border-amber-500/20">
        <div className="absolute inset-0 bg-pattern opacity-10"></div>
        <div className="relative p-12 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full mb-6">
            <Settings className="w-10 h-10 text-black" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            {t('softwareTCCutPro')}
          </h1>
          <p className="text-xl text-amber-400 mb-2">
            {t('preciseCuttingPatterns')}
          </p>
          <p className="text-gray-300 max-w-2xl mx-auto">
            {t('mostAdvancedSoftware')}
          </p>
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        <Card className="tc-card hover-lift">
          <CardContent className="tc-card-content text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-primary mb-3">{t('patternsCount')}</h3>
            <p className="text-secondary">
              {t('completeDatabase')}
            </p>
          </CardContent>
        </Card>

        <Card className="tc-card hover-lift">
          <CardContent className="tc-card-content text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Eye className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-primary mb-3">{t('laserPrecisionTitle')}</h3>
            <p className="text-secondary">
              {t('laserPrecisionDesc')}
            </p>
          </CardContent>
        </Card>

        <Card className="tc-card hover-lift">
          <CardContent className="tc-card-content text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Settings className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-primary mb-3">{t('easyToUseTitle')}</h3>
            <p className="text-secondary">
              {t('easyToUseDesc')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* CTA - Only show if no approved request */}
      {(!myRequest || myRequest.status !== 'approved') && (
        <Card className="tc-card">
          <CardContent className="tc-card-content text-center py-12">
            <h2 className="text-3xl font-bold text-primary mb-4">
              {t('readyToRevolutionize')}
            </h2>
            <p className="text-secondary text-lg mb-8 max-w-2xl mx-auto">
              {t('joinShops')}
            </p>
            
            {!myRequest && (
              <Button onClick={() => setShowRequestForm(true)} className="tc-button-primary text-lg px-8 py-4">
                <Settings className="w-5 h-5 mr-2" />
                {t('requestAccessBtn')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Request Access Dialog */}
      <Dialog open={showRequestForm} onOpenChange={setShowRequestForm}>
        <DialogContent className="tc-dialog-content max-w-2xl">
          <div className="tc-dialog-header">
            <DialogTitle className="tc-dialog-title">{t('requestAccessToCutPro')}</DialogTitle>
            <DialogDescription className="tc-dialog-description">
              {t('completeInfoWeWillContact')}
            </DialogDescription>
          </div>
          <div className="tc-dialog-body">
            <form onSubmit={handleRequestSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="tc-label">{t('businessNameField')}</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={requestForm.business_name}
                    onChange={(e) => setRequestForm({...requestForm, business_name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="tc-label">{t('contactPersonField')}</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={requestForm.contact_person}
                    onChange={(e) => setRequestForm({...requestForm, contact_person: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="tc-label">{t('email')}</label>
                  <input
                    type="email"
                    className="tc-input tc-focus-ring"
                    value={requestForm.email}
                    onChange={(e) => setRequestForm({...requestForm, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="tc-label">{t('phone')}</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={requestForm.phone}
                    onChange={(e) => setRequestForm({...requestForm, phone: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="tc-label">{t('shopType')}</label>
                  <select 
                    className="tc-select tc-focus-ring"
                    value={requestForm.shop_type} 
                    onChange={(e) => setRequestForm({...requestForm, shop_type: e.target.value})}
                    required
                  >
                    <option value="">{t('selectOption')}</option>
                    <option value="auto_detailing">{t('autoDetailing')}</option>
                    <option value="tint_shop">{t('tintShop')}</option>
                    <option value="body_shop">{t('bodyShop')}</option>
                    <option value="dealership">{t('dealership')}</option>
                    <option value="mobile_service">{t('mobileService')}</option>
                    <option value="other">{t('other')}</option>
                  </select>
                </div>
                <div>
                  <label className="tc-label">{t('monthlyVolume')}</label>
                  <select 
                    className="tc-select tc-focus-ring"
                    value={requestForm.monthly_volume} 
                    onChange={(e) => setRequestForm({...requestForm, monthly_volume: e.target.value})}
                    required
                  >
                    <option value="">{t('selectOption')}</option>
                    <option value="1-10">{t('vehicles1to10')}</option>
                    <option value="11-25">{t('vehicles11to25')}</option>
                    <option value="26-50">{t('vehicles26to50')}</option>
                    <option value="51-100">{t('vehicles51to100')}</option>
                    <option value="100+">{t('vehiclesOver100')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="tc-label">{t('currentSoftware')}</label>
                <input
                  className="tc-input tc-focus-ring"
                  value={requestForm.current_software}
                  onChange={(e) => setRequestForm({...requestForm, current_software: e.target.value})}
                  placeholder={t('currentSoftwarePlaceholder')}
                />
              </div>

              <div>
                <label className="tc-label">{t('additionalInfo')}</label>
                <textarea
                  className="tc-textarea tc-focus-ring"
                  value={requestForm.additional_info}
                  onChange={(e) => setRequestForm({...requestForm, additional_info: e.target.value})}
                  placeholder={t('tellUsYourNeeds')}
                  rows={4}
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="button" onClick={() => setShowRequestForm(false)} className="tc-button-secondary flex-1">
                  {t('cancel')}
                </Button>
                <Button type="submit" className="tc-button-primary flex-1">
                  {t('sendRequest')}
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Serial Number Search Section (For Admins)
const SerialNumberSearchSection = () => {
  const { t } = useLanguage();
  const [serialRecords, setSerialRecords] = useState([]);
  const [warranties, setWarranties] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [warrantySearchResults, setWarrantySearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('serials');

  useEffect(() => {
    fetchAllSerialNumbers();
    fetchAllWarranties();
  }, []);

  const fetchAllSerialNumbers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/serial-numbers?limit=50`);
      setSerialRecords(response.data.serial_numbers);
    } catch (error) {
      toast.error('Error al cargar números de serie');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllWarranties = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/warranties?limit=50`);
      setWarranties(response.data.warranties);
    } catch (error) {
      console.error('Error al cargar garantías');
    }
  };

  const handleSerialSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || searchQuery.length < 3) {
      toast.error('Ingresa al menos 3 caracteres para buscar');
      return;
    }

    setSearching(true);
    try {
      // Search serials and warranties simultaneously
      const [serialResponse, warrantyResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/serial-numbers/search?serial=${encodeURIComponent(searchQuery)}`),
        axios.get(`${API_BASE_URL}/api/admin/warranties/search?q=${encodeURIComponent(searchQuery)}`)
      ]);
      
      setSearchResults(serialResponse.data);
      setWarrantySearchResults(warrantyResponse.data);
    } catch (error) {
      toast.error('Error al buscar');
      setSearchResults([]);
      setWarrantySearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setWarrantySearchResults(null);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: t("pending"), class: 'tc-badge-warning' },
      'processing': { label: t("processing"), class: 'tc-badge-info' },
      'shipped': { label: t("shipped"), class: 'tc-badge-info' },
      'delivered': { label: t("delivered"), class: 'tc-badge-success' },
      'completed': { label: t("completed"), class: 'tc-badge-success' }
    };
    
    const statusInfo = statusMap[status] || { label: status, class: 'tc-badge-warning' };
    return <Badge className={`tc-badge ${statusInfo.class}`}>{statusInfo.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="tc-spinner"></div>
        <span className="ml-4 text-primary">Cargando números de serie...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-primary mb-2">
            Búsqueda de Series y Garantías
          </h2>
          <p className="text-secondary">Rastrea números de serie y gestiona garantías registradas</p>
        </div>
        <div className="flex gap-2">
          <Badge className="tc-badge tc-badge-info">
            {serialRecords.length} Series
          </Badge>
          <Badge className="tc-badge tc-badge-success">
            {warranties.length} Garantías
          </Badge>
        </div>
      </div>

      {/* Tabs for Serials and Warranties */}
      <div className="flex gap-2 border-b border-amber-500/20">
        <button
          onClick={() => setActiveTab('serials')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'serials'
              ? 'text-gold border-gold'
              : 'text-secondary border-transparent hover:text-primary'
          }`}
        >
          Números de Serie ({serialRecords.length})
        </button>
        <button
          onClick={() => setActiveTab('warranties')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'warranties'
              ? 'text-gold border-gold'
              : 'text-secondary border-transparent hover:text-primary'
          }`}
        >
          Garantías Registradas ({warranties.length})
        </button>
      </div>

      {/* Search */}
      <Card className="tc-card">
        <CardContent className="tc-card-content">
          <h3 className="text-lg font-semibold text-primary mb-4">
            Buscar {activeTab === 'serials' ? 'Número de Serie' : 'Garantías'}
          </h3>
          <form onSubmit={handleSerialSearch} className="flex gap-4 items-end">
            <div className="flex-1">
              <input
                className="tc-input tc-focus-ring"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                placeholder={activeTab === 'serials' 
                  ? "Ingresa número de serie (ej: TC-PPF-001234)"
                  : "Buscar por serie, VIN, cliente, vehículo..."
                }
              />
            </div>
            <Button 
              type="submit" 
              disabled={searching}
              className="tc-button-primary"
            >
              {searching ? 'Buscando...' : 'Buscar'}
            </Button>
            {(searchResults !== null || warrantySearchResults !== null) && (
              <Button 
                type="button" 
                onClick={clearSearch}
                className="tc-button-secondary"
              >
                Limpiar
              </Button>
            )}
          </form>
          
          {/* Search Results */}
          {activeTab === 'serials' && searchResults !== null && (
            <div className="mt-6">
              <h4 className="text-md font-medium text-primary mb-3">
                Resultados de números de serie ({searchResults.length})
              </h4>
              {searchResults.length === 0 ? (
                <div className="text-center py-8">
                  <Hash className="w-12 h-12 text-muted mx-auto mb-4" />
                  <p className="text-secondary">No se encontró ningún registro con ese número de serie.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {searchResults.map((record) => (
                    <Card key={record.serial_id} className="tc-card border-2 border-gold/30">
                      <CardContent className="tc-card-content">
                        <div className="flex justify-between items-start">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-gold to-amber-600 rounded-full flex items-center justify-center">
                              <Hash className="w-6 h-6 text-black" />
                            </div>
                            <div className="space-y-2">
                              <div>
                                <h3 className="text-xl font-bold text-primary">{record.serial_number}</h3>
                                <p className="text-secondary">Número de Serie</p>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <span className="text-muted">Orden:</span>
                                  <p className="text-primary font-medium">#{record.order_id?.substring(0, 8)}</p>
                                </div>
                                <div>
                                  <span className="text-muted">Cliente:</span>
                                  <p className="text-primary font-medium">{record.customer_name}</p>
                                </div>
                                <div>
                                  <span className="text-muted">Empresa:</span>
                                  <p className="text-primary font-medium">{record.customer_company}</p>
                                </div>
                                <div>
                                  <span className="text-muted">Asignado:</span>
                                  <p className="text-primary font-medium">{new Date(record.assigned_date).toLocaleDateString()}</p>
                                </div>
                              </div>
                              {record.notes && (
                                <div>
                                  <span className="text-muted text-sm">Notas:</span>
                                  <p className="text-secondary text-sm italic">{record.notes}</p>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            {getStatusBadge(record.status)}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Warranty Search Results */}
          {activeTab === 'warranties' && warrantySearchResults !== null && (
            <div className="mt-6">
              <h4 className="text-md font-medium text-primary mb-3">
                Resultados de garantías ({warrantySearchResults.length})
              </h4>
              {warrantySearchResults.length === 0 ? (
                <div className="text-center py-8">
                  <Shield className="w-12 h-12 text-muted mx-auto mb-4" />
                  <p className="text-secondary">No se encontraron garantías con esos criterios.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {warrantySearchResults.map((warranty) => (
                    <Card key={warranty.warranty_id} className="tc-card border-2 border-green-300">
                      <CardContent className="tc-card-content">
                        <div className="flex justify-between items-start">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                              <Shield className="w-6 h-6 text-white" />
                            </div>
                            <div className="space-y-2">
                              <div>
                                <h3 className="text-lg font-bold text-primary">
                                  {warranty.vehicle_year} {warranty.vehicle_make} {warranty.vehicle_model}
                                </h3>
                                <p className="text-secondary">Cliente: {warranty.client_name}</p>
                                <p className="text-muted text-xs">VIN: {warranty.vin_number}</p>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <span className="text-muted">Serie:</span>
                                  <p className="text-primary font-mono font-bold">{warranty.serial_number}</p>
                                </div>
                                <div>
                                  <span className="text-muted">Instalado:</span>
                                  <p className="text-primary font-medium">{new Date(warranty.installation_date).toLocaleDateString()}</p>
                                </div>
                                <div>
                                  <span className="text-muted">Área:</span>
                                  <p className="text-primary font-medium">{warranty.installation_location}</p>
                                </div>
                                <div>
                                  <span className="text-muted">Registrado:</span>
                                  <p className="text-primary font-medium">{new Date(warranty.registration_date).toLocaleDateString()}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className="tc-badge tc-badge-success">Activa</Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tab Content */}
      {activeTab === 'serials' && (
        <Card className="tc-card">
          <div className="tc-card-header">
            <h3 className="text-xl font-semibold text-primary mb-1">Números de Serie Recientes</h3>
            <p className="text-secondary text-sm">Últimos 50 números de serie asignados</p>
          </div>
          <CardContent className="tc-card-content p-0">
            {serialRecords.length === 0 ? (
              <div className="text-center py-12">
                <Hash className="w-16 h-16 text-muted mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-primary mb-2">No hay números de serie</h3>
                <p className="text-secondary mb-6">Los números de serie aparecerán aquí cuando se asignen a órdenes</p>
              </div>
            ) : (
              <div className="tc-table">
                <table className="w-full">
                  <thead className="tc-table-header">
                    <tr>
                      <th className="tc-table-header th text-left">Número de Serie</th>
                      <th className="tc-table-header th text-left">Orden</th>
                      <th className="tc-table-header th text-left">Cliente</th>
                      <th className="tc-table-header th text-left">Estado</th>
                      <th className="tc-table-header th text-left">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serialRecords.map((record) => (
                      <tr key={record.serial_id} className="tc-table-row">
                        <td className="tc-table-cell">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-gold to-amber-600 rounded-full flex items-center justify-center">
                              <Hash className="w-4 h-4 text-black" />
                            </div>
                            <div>
                              <p className="text-primary font-mono font-bold text-sm">{record.serial_number}</p>
                              {record.notes && (
                                <p className="text-muted text-xs">{record.notes}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="tc-table-cell">
                          <p className="text-primary font-medium">#{record.order_id?.substring(0, 8)}</p>
                        </td>
                        <td className="tc-table-cell">
                          <div>
                            <p className="text-primary font-medium text-sm">{record.customer_name}</p>
                            <p className="text-muted text-xs">{record.customer_company}</p>
                          </div>
                        </td>
                        <td className="tc-table-cell">
                          {getStatusBadge(record.status)}
                        </td>
                        <td className="tc-table-cell">
                          <p className="text-secondary text-sm">{new Date(record.assigned_date).toLocaleDateString()}</p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'warranties' && (
        <Card className="tc-card">
          <div className="tc-card-header">
            <h3 className="text-xl font-semibold text-primary mb-1">Garantías Registradas Recientes</h3>
            <p className="text-secondary text-sm">Últimas 50 garantías registradas por clientes</p>
          </div>
          <CardContent className="tc-card-content p-0">
            {warranties.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="w-16 h-16 text-muted mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-primary mb-2">No hay garantías registradas</h3>
                <p className="text-secondary mb-6">Las garantías aparecerán aquí cuando los clientes las registren</p>
              </div>
            ) : (
              <div className="tc-table">
                <table className="w-full">
                  <thead className="tc-table-header">
                    <tr>
                      <th className="tc-table-header th text-left">Cliente</th>
                      <th className="tc-table-header th text-left">Vehículo</th>
                      <th className="tc-table-header th text-left">Serie</th>
                      <th className="tc-table-header th text-left">Instalación</th>
                      <th className="tc-table-header th text-left">Registrado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {warranties.map((warranty) => (
                      <tr key={warranty.warranty_id} className="tc-table-row">
                        <td className="tc-table-cell">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                              <Shield className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <p className="text-primary font-medium text-sm">{warranty.client_name}</p>
                              <p className="text-muted text-xs">VIN: {warranty.vin_number}</p>
                            </div>
                          </div>
                        </td>
                        <td className="tc-table-cell">
                          <div>
                            <p className="text-primary font-medium text-sm">
                              {warranty.vehicle_year} {warranty.vehicle_make}
                            </p>
                            <p className="text-secondary text-xs">{warranty.vehicle_model}</p>
                          </div>
                        </td>
                        <td className="tc-table-cell">
                          <p className="text-primary font-mono font-bold text-sm">{warranty.serial_number}</p>
                        </td>
                        <td className="tc-table-cell">
                          <div>
                            <p className="text-primary font-medium text-sm">{new Date(warranty.installation_date).toLocaleDateString()}</p>
                            <p className="text-muted text-xs">{warranty.installation_location}</p>
                          </div>
                        </td>
                        <td className="tc-table-cell">
                          <p className="text-secondary text-sm">{new Date(warranty.registration_date).toLocaleDateString()}</p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Pricing Management Section (For Admins)

// =============================================================================
// EMAIL CENTER — admin manages templates + sends marketing / system emails
// =============================================================================

// Quill toolbar config — designed for email HTML: headers, formatting, lists, links, colors, alignment, code
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    ['clean'],
  ],
};
const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike', 'color', 'background',
  'list', 'align', 'blockquote', 'code-block', 'link', 'image',
];

// Friendly labels + descriptions for template variables so admins never see raw {{codes}}.
const VARIABLE_META = {
  headline: { label: 'Main Headline', placeholder: 'e.g. Big News from TC Protection Film', kind: 'text' },
  body_html: { label: 'Email Body', placeholder: 'Write your message here…', kind: 'html' },
  cta_label: { label: 'Button Text', placeholder: 'e.g. View Details', kind: 'text' },
  cta_url: { label: 'Button Link (URL)', placeholder: 'https://tcfilmprotection.com/…', kind: 'url' },
  update_title: { label: 'Update Title', placeholder: 'e.g. New Feature Released', kind: 'text' },
  update_body: { label: 'Update Details', placeholder: 'Explain the update…', kind: 'html' },
  when: { label: 'When', placeholder: 'e.g. Monday, March 3 · 8pm EST', kind: 'text' },
  impact: { label: 'Impact / Who this affects', placeholder: 'e.g. All dealers', kind: 'text' },
  expired_count: { label: 'Expired accounts', kind: 'number' },
  urgent_count: { label: 'Urgent (≤7d) accounts', kind: 'number' },
  warning_count: { label: 'Warning (≤30d) accounts', kind: 'number' },
  total_flagged: { label: 'Total flagged', kind: 'number' },
  today: { label: 'Today (date)', kind: 'text' },
  accounts_html: { label: 'Accounts List (rich text)', kind: 'html' },
  vehicle_year: { label: 'Vehicle year', kind: 'text' },
  vehicle_make: { label: 'Vehicle make', kind: 'text' },
  vehicle_model: { label: 'Vehicle model', kind: 'text' },
  vehicle_vin: { label: 'VIN', kind: 'text' },
  dealer_name: { label: 'Dealer name', kind: 'text' },
  client_name: { label: 'Client name', kind: 'text' },
  warranty_id: { label: 'Warranty ID', kind: 'text' },
  serial_number: { label: 'Serial number', kind: 'text' },
  install_date: { label: 'Install date', kind: 'text' },
  warranty_end: { label: 'Warranty end date', kind: 'text' },
};
const AUTO_FILLED_VARS = new Set(['first_name', 'full_name', 'recipient_email', 'recipient_role']);
const getVarLabel = (name, fallbackDescription) => {
  const meta = VARIABLE_META[name];
  if (meta) return meta.label;
  // fallback: humanize snake_case
  const humanized = name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return fallbackDescription || humanized;
};
const getVarPlaceholder = (name, fallback) => VARIABLE_META[name]?.placeholder || fallback || '';
const isHtmlVar = (name) => VARIABLE_META[name]?.kind === 'html' || name.toLowerCase().includes('html') || name === 'body_html' || name === 'update_body' || name === 'accounts_html';

const EmailCenterSection = () => {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', category: 'custom', description: '', subject: '', html_body: '' });
  const [showEdit, setShowEdit] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewVarsText, setPreviewVarsText] = useState('{}');
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastAudience, setBroadcastAudience] = useState('all_admins');
  const [broadcastCustomEmails, setBroadcastCustomEmails] = useState('');
  const [broadcastVars, setBroadcastVars] = useState({});
  const [broadcastSubjectOverride, setBroadcastSubjectOverride] = useState('');
  const [broadcastCount, setBroadcastCount] = useState({ count: 0, preview: [] });
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ template_id: '', name: '', category: 'custom', description: '', subject: '', html_body: '' });
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [emailLog, setEmailLog] = useState([]);
  const [showLog, setShowLog] = useState(false);

  // --- Simple composer (primary flow: just subject + message + audience) ---
  const [quick, setQuick] = useState({ subject: '', message: '', audience: 'all_dealers', custom_emails: '' });
  const [quickCount, setQuickCount] = useState({ count: 0, preview: [] });
  const [quickSending, setQuickSending] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/admin/email-audience-count?audience=${quick.audience}`)
      .then(res => setQuickCount({ count: res.data.count, preview: res.data.preview }))
      .catch(() => setQuickCount({ count: 0, preview: [] }));
  }, [quick.audience]);

  const sendQuick = async () => {
    if (!quick.subject.trim()) { toast.error(t('quickSubjectRequired')); return; }
    if (!quick.message.trim()) { toast.error(t('quickMessageRequired')); return; }
    if (quick.audience === 'custom' && !quick.custom_emails.trim()) { toast.error(t('quickEmailsRequired')); return; }
    setQuickSending(true);
    try {
      const payload = { subject: quick.subject, message: quick.message, audience: quick.audience };
      if (quick.audience === 'custom') payload.custom_emails = quick.custom_emails.split(',').map(e => e.trim()).filter(Boolean);
      const res = await axios.post(`${API_BASE_URL}/api/admin/email-quick-send`, payload);
      const { status, sent_count, failed_count } = res.data;
      if (status === 'sent') toast.success(t('quickSentOk').replace('{n}', sent_count));
      else if (status === 'partial') toast.warning(t('quickSentPartial').replace('{sent}', sent_count).replace('{failed}', failed_count));
      else toast.error(t('quickSendFailed'));
      if (status !== 'failed') setQuick({ ...quick, subject: '', message: '' });
    } catch (e) { toast.error(e.response?.data?.detail || t('quickSendFailed')); }
    finally { setQuickSending(false); }
  };

  const AUDIENCE_OPTIONS = [
    { value: 'all_dealers', label: t('audAllDealers') },
    { value: 'installation_center', label: t('audInstallationCenters') },
    { value: 'elite_partner', label: t('audElitePartners') },
    { value: 'sales_rep', label: t('audSalesReps') },
    { value: 'all_partners', label: t('audEveryone') },
    { value: 'all_admins', label: t('audAdmins') },
    { value: 'custom', label: t('audCustom') },
  ];

  useEffect(() => { fetchTemplates(); }, []);
  useEffect(() => {
    // Refresh audience count whenever broadcastAudience changes
    if (showBroadcast) {
      axios.get(`${API_BASE_URL}/api/admin/email-audience-count?audience=${broadcastAudience}`)
        .then(res => setBroadcastCount({ count: res.data.count, preview: res.data.preview }))
        .catch(() => setBroadcastCount({ count: 0, preview: [] }));
    }
  }, [broadcastAudience, showBroadcast]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/email-templates`);
      setTemplates(res.data.templates || []);
    } catch (e) {
      toast.error(t('errorLoadingTemplates'));
    } finally { setLoading(false); }
  };

  const fetchEmailLog = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/email-log?limit=30`);
      setEmailLog(res.data.logs || []);
      setShowLog(true);
    } catch (e) { toast.error('Error loading email log'); }
  };

  const openEdit = (tpl) => {
    setSelected(tpl);
    setEditForm({
      name: tpl.name, category: tpl.category, description: tpl.description || '',
      subject: tpl.subject, html_body: tpl.html_body,
    });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API_BASE_URL}/api/admin/email-templates/${selected.template_id}`, editForm);
      toast.success(t('templateSavedSuccess'));
      setShowEdit(false);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errorSavingTemplate'));
    }
  };

  const resetSystemTemplate = async (tpl) => {
    if (!window.confirm(t('resetTemplateConfirm'))) return;
    try {
      await axios.post(`${API_BASE_URL}/api/admin/email-templates/${tpl.template_id}/reset`);
      toast.success(t('templateResetSuccess'));
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(t('deleteTemplateConfirm'))) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/email-templates/${tpl.template_id}`);
      toast.success(t('templateDeletedSuccess'));
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openPreview = async (tpl, extraVars = null) => {
    setSelected(tpl);
    let vars = {};
    if (extraVars) vars = extraVars;
    else {
      try { vars = JSON.parse(previewVarsText || '{}'); } catch (e) { vars = {}; }
    }
    try {
      const res = await axios.post(`${API_BASE_URL}/api/admin/email-templates/${tpl.template_id}/preview`, { variables: vars });
      setPreviewHtml(res.data.html);
      setPreviewSubject(res.data.subject);
      setShowPreview(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
  };

  const sendTest = async () => {
    if (!selected || !testEmail) { toast.error(t('testEmailRequired')); return; }
    setTestSending(true);
    try {
      let vars = {};
      try { vars = JSON.parse(previewVarsText || '{}'); } catch (e) { vars = {}; }
      const res = await axios.post(`${API_BASE_URL}/api/admin/email-templates/${selected.template_id}/test-send`,
        { to_email: testEmail, variables: vars });
      if (res.data.ok) toast.success(t('testEmailSent').replace('{email}', testEmail));
      else toast.error(res.data.result?.error || t('testEmailFailed'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setTestSending(false); }
  };

  const openBroadcast = (tpl) => {
    setSelected(tpl);
    // Prefill variables based on template
    const vars = {};
    (tpl.variables || []).forEach(v => { vars[v.name] = ''; });
    setBroadcastVars(vars);
    setBroadcastSubjectOverride('');
    setBroadcastCustomEmails('');
    setBroadcastAudience('all_admins');
    setShowBroadcast(true);
  };

  const sendBroadcast = async () => {
    if (!selected) return;
    setBroadcastSending(true);
    try {
      const payload = {
        template_id: selected.template_id,
        audience: broadcastAudience,
        variables: broadcastVars,
      };
      if (broadcastSubjectOverride) payload.subject_override = broadcastSubjectOverride;
      if (broadcastAudience === 'custom') {
        payload.custom_emails = broadcastCustomEmails.split(',').map(e => e.trim()).filter(Boolean);
      }
      const res = await axios.post(`${API_BASE_URL}/api/admin/email-broadcast`, payload);
      const { sent_count, failed_count, status } = res.data;
      if (status === 'sent') toast.success(t('broadcastSuccess').replace('{n}', sent_count));
      else if (status === 'partial') toast.warning(t('broadcastPartial').replace('{sent}', sent_count).replace('{failed}', failed_count));
      else toast.error(t('broadcastFailed'));
      setShowBroadcast(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('broadcastFailed'));
    } finally { setBroadcastSending(false); }
  };

  const createTemplate = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/email-templates`, createForm);
      toast.success(t('templateCreatedSuccess'));
      setShowCreate(false);
      setCreateForm({ template_id: '', name: '', category: 'custom', description: '', subject: '', html_body: '' });
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const categoryBadge = (cat) => {
    const colors = { system: 'bg-blue-600', transactional: 'bg-emerald-600', marketing: 'bg-purple-600', custom: 'bg-slate-600' };
    return <Badge className={`${colors[cat] || 'bg-slate-600'} text-white text-xs`}>{cat}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="email-center-section">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-primary mb-2 flex items-center gap-3">
            <Mail className="w-7 h-7 text-tc-gold" />
            {t('emailCenterTitle')}
          </h2>
          <p className="text-secondary text-sm">{t('emailCenterSubtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchEmailLog} className="tc-button-secondary" data-testid="view-email-log-btn">
            <Eye className="w-4 h-4 mr-1" /> {t('viewEmailLog')}
          </Button>
        </div>
      </div>

      {/* SIMPLE COMPOSER — type subject + message, pick audience, send */}
      <Card className="tc-card" data-testid="quick-composer">
        <CardContent className="tc-card-content space-y-4">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-tc-gold" />
            <h3 className="text-lg font-semibold text-primary">{t('quickComposerTitle')}</h3>
          </div>
          <p className="text-muted text-sm">{t('quickComposerSubtitle')}</p>

          <div>
            <label className="tc-label">{t('quickSendTo')}</label>
            <select
              className="tc-select tc-focus-ring w-full"
              value={quick.audience}
              onChange={(e) => setQuick({ ...quick, audience: e.target.value })}
              data-testid="quick-audience-select"
            >
              {AUDIENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {quick.audience === 'custom' ? (
              <input
                className="tc-input tc-focus-ring w-full mt-2"
                placeholder={t('quickCustomEmailsPlaceholder')}
                value={quick.custom_emails}
                onChange={(e) => setQuick({ ...quick, custom_emails: e.target.value })}
                data-testid="quick-custom-emails"
              />
            ) : (
              <p className="text-xs text-tc-gold mt-1" data-testid="quick-recipient-count">
                {t('quickRecipientCount').replace('{n}', quickCount.count)}
              </p>
            )}
          </div>

          <div>
            <label className="tc-label">{t('quickSubject')}</label>
            <input
              className="tc-input tc-focus-ring w-full"
              placeholder={t('quickSubjectPlaceholder')}
              value={quick.subject}
              onChange={(e) => setQuick({ ...quick, subject: e.target.value })}
              data-testid="quick-subject-input"
            />
          </div>

          <div>
            <label className="tc-label">{t('quickMessage')}</label>
            <textarea
              className="tc-input tc-focus-ring w-full"
              rows={8}
              placeholder={t('quickMessagePlaceholder')}
              value={quick.message}
              onChange={(e) => setQuick({ ...quick, message: e.target.value })}
              data-testid="quick-message-input"
            />
            <p className="text-xs text-muted mt-1">{t('quickMessageHint')}</p>
          </div>

          <div className="flex justify-end">
            <Button onClick={sendQuick} disabled={quickSending} className="tc-button-primary" data-testid="quick-send-btn">
              <Send className="w-4 h-4 mr-2" />
              {quickSending ? t('quickSending') : t('quickSendButton')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Advanced templates toggle */}
      <div className="flex items-center justify-between pt-2">
        <button
          className="text-sm text-muted hover:text-primary transition-colors underline"
          onClick={() => setShowAdvanced(v => !v)}
          data-testid="toggle-advanced-btn"
        >
          {showAdvanced ? t('hideAdvancedTemplates') : t('showAdvancedTemplates')}
        </button>
        {showAdvanced && (
          <Button onClick={() => setShowCreate(true)} className="tc-button-primary" size="sm" data-testid="new-template-btn">
            <Plus className="w-4 h-4 mr-1" /> {t('newTemplateBtn')}
          </Button>
        )}
      </div>

      {/* Templates Grid (advanced) */}
      {showAdvanced && (loading ? (
        <div className="flex justify-center py-12"><div className="tc-spinner"></div></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map(tpl => (
            <Card key={tpl.template_id} className="tc-card" data-testid={`template-card-${tpl.template_id}`}>
              <CardContent className="tc-card-content">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-primary">{tpl.name}</h3>
                      {categoryBadge(tpl.category)}
                      {tpl.is_system && <Badge className="bg-amber-600/70 text-white text-xs">system</Badge>}
                    </div>
                    <p className="text-muted text-xs font-mono">{tpl.template_id}</p>
                  </div>
                </div>
                <p className="text-secondary text-sm mb-3">{tpl.description}</p>
                <div className="bg-black/40 p-2 rounded border border-slate-700 mb-3">
                  <p className="text-xs text-muted uppercase mb-1">{t('subjectLabel')}</p>
                  <p className="text-slate-200 text-sm">{tpl.subject}</p>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(() => {
                    const userVars = (tpl.variables || []).filter(v => !AUTO_FILLED_VARS.has(v.name));
                    if (userVars.length === 0) return <span className="text-xs text-muted">No custom fields — just pick an audience and send.</span>;
                    return (
                      <>
                        <span className="text-xs text-slate-400 mr-1">Personalizable fields:</span>
                        {userVars.slice(0, 6).map(v => (
                          <span key={v.name} className="bg-slate-800/80 text-slate-200 text-xs px-2 py-1 rounded border border-tc-gold/40">{getVarLabel(v.name, v.description)}</span>
                        ))}
                        {userVars.length > 6 && <span className="text-xs text-muted">+{userVars.length - 6} more</span>}
                      </>
                    );
                  })()}
                </div>
                <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-700">
                  <Button size="sm" variant="outline" onClick={() => openEdit(tpl)} className="tc-button-secondary text-xs" data-testid={`edit-template-${tpl.template_id}`}>
                    <Edit className="w-3 h-3 mr-1" /> {t('editBtn')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openPreview(tpl)} className="tc-button-secondary text-xs" data-testid={`preview-template-${tpl.template_id}`}>
                    <Eye className="w-3 h-3 mr-1" /> {t('previewBtn')}
                  </Button>
                  <Button size="sm" onClick={() => openBroadcast(tpl)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid={`broadcast-template-${tpl.template_id}`}>
                    <Send className="w-3 h-3 mr-1" /> {t('broadcastBtn')}
                  </Button>
                  {tpl.is_system ? (
                    <Button size="sm" variant="outline" onClick={() => resetSystemTemplate(tpl)} className="tc-button-secondary text-xs">
                      <RotateCcw className="w-3 h-3 mr-1" /> {t('resetBtn')}
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => deleteTemplate(tpl)} className="text-red-400 border-red-500/50 text-xs">
                      <Trash className="w-3 h-3 mr-1" /> {t('deleteBtn')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ))}

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={(o) => { if (!o) setShowEdit(false); }}>
        <DialogContent className="tc-dialog max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="edit-template-dialog">
          <DialogHeader>
            <DialogTitle className="tc-dialog-title flex items-center gap-2">
              <Edit className="w-5 h-5 text-tc-gold" /> {t('editTemplateTitle')}: {selected?.name}
            </DialogTitle>
            <DialogDescription className="tc-dialog-description">
              {t('editTemplateSubtitle')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="tc-label">{t('nameLabel')}</label>
                <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} className="tc-input" />
              </div>
              <div>
                <label className="tc-label">{t('categoryLabel')}</label>
                <select value={editForm.category} onChange={e => setEditForm({...editForm, category: e.target.value})} className="tc-select">
                  <option value="system">system</option>
                  <option value="transactional">transactional</option>
                  <option value="marketing">marketing</option>
                  <option value="custom">custom</option>
                </select>
              </div>
            </div>
            <div>
              <label className="tc-label">{t('descriptionLabel')}</label>
              <Input value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} className="tc-input" />
            </div>
            <div>
              <label className="tc-label">{t('subjectLabel')}</label>
              <Input value={editForm.subject} onChange={e => setEditForm({...editForm, subject: e.target.value})} className="tc-input font-mono text-sm" data-testid="edit-subject" />
              <p className="text-muted text-xs mt-1">Tip: you can leave placeholders like <code className="text-tc-gold">{'{{first_name}}'}</code> — they auto-fill per recipient.</p>
            </div>
            <div>
              <label className="tc-label">{t('htmlBodyLabel')}</label>
              <div className="tc-quill-wrapper bg-white rounded" data-testid="edit-html-body">
                <ReactQuill
                  theme="snow"
                  value={editForm.html_body}
                  onChange={(html) => setEditForm({...editForm, html_body: html})}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ height: 320, marginBottom: 40 }}
                />
              </div>
              <p className="text-muted text-xs mt-1">{t('templateVarsHelper')}</p>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-slate-700">
              <Button variant="outline" onClick={() => setShowEdit(false)} className="tc-button-secondary">{t('cancel')}</Button>
              <Button variant="outline" onClick={() => openPreview(selected, {})} className="tc-button-secondary">
                <Eye className="w-4 h-4 mr-1" /> {t('previewBtn')}
              </Button>
              <Button onClick={saveEdit} className="tc-button-primary" data-testid="save-template-btn">{t('saveBtn')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={(o) => { if (!o) setShowPreview(false); }}>
        <DialogContent className="tc-dialog max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="tc-dialog-title flex items-center gap-2">
              <Eye className="w-5 h-5 text-tc-gold" /> {t('previewTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-black/40 p-3 rounded border border-slate-700">
              <p className="text-xs text-muted uppercase mb-1">{t('subjectLabel')}</p>
              <p className="text-primary font-medium">{previewSubject}</p>
            </div>
            <div>
              <label className="tc-label">{t('previewVarsLabel')} (JSON)</label>
              <textarea
                rows={3}
                value={previewVarsText}
                onChange={e => setPreviewVarsText(e.target.value)}
                className="tc-input w-full font-mono text-xs"
                placeholder='{"first_name":"Alex"}'
              />
              <Button onClick={() => openPreview(selected)} className="tc-button-secondary text-xs mt-1">{t('rerenderBtn')}</Button>
            </div>
            <div className="border border-slate-700 rounded bg-white" style={{ minHeight: 300 }}>
              <iframe title="preview" srcDoc={previewHtml} style={{ width: '100%', height: 500, border: 0, borderRadius: 4 }} data-testid="preview-iframe" />
            </div>
            <div className="pt-3 border-t border-slate-700">
              <label className="tc-label">{t('sendTestToLabel')}</label>
              <div className="flex gap-2">
                <Input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="you@example.com" className="tc-input flex-1" data-testid="test-email-input" />
                <Button onClick={sendTest} disabled={testSending} className="tc-button-primary" data-testid="send-test-btn">
                  {testSending ? t('sending') : t('sendTestBtn')}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Broadcast Dialog */}
      <Dialog open={showBroadcast} onOpenChange={(o) => { if (!o) setShowBroadcast(false); }}>
        <DialogContent className="tc-dialog max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="tc-dialog-title flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-400" /> {t('broadcastTitle')}: {selected?.name}
            </DialogTitle>
            <DialogDescription className="tc-dialog-description">{t('broadcastSubtitle')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="tc-label">{t('audienceLabel')}</label>
              <select value={broadcastAudience} onChange={e => setBroadcastAudience(e.target.value)} className="tc-select" data-testid="broadcast-audience-select">
                <option value="all_admins">{t('audAllAdmins')}</option>
                <option value="all_dealers">{t('audAllDealers')}</option>
                <option value="installation_center">{t('audInstallationCenters')}</option>
                <option value="elite_partner">{t('audElitePartners')}</option>
                <option value="sales_rep">{t('audSalesReps')}</option>
                <option value="all_partners">{t('audAllPartners')}</option>
                <option value="custom">{t('audCustom')}</option>
              </select>
              <p className="text-xs text-emerald-400 mt-1" data-testid="audience-count">
                {broadcastAudience === 'custom' ? t('customEmailsHelper') : `${broadcastCount.count} ${t('recipients')} · ${(broadcastCount.preview || []).join(', ')}${broadcastCount.count > 5 ? '…' : ''}`}
              </p>
            </div>
            {broadcastAudience === 'custom' && (
              <div>
                <label className="tc-label">{t('customEmailsLabel')}</label>
                <textarea rows={2} value={broadcastCustomEmails} onChange={e => setBroadcastCustomEmails(e.target.value)}
                  placeholder="alice@example.com, bob@example.com" className="tc-input w-full" data-testid="custom-emails-input" />
              </div>
            )}
            <div className="p-3 bg-tc-gold/5 border border-tc-gold/30 rounded text-xs text-slate-300">
              <span className="text-tc-gold font-semibold">Tip:</span> Just fill in the fields below like a normal email — no codes to worry about. Each recipient sees their own name auto-filled.
            </div>
            <div>
              <label className="tc-label">{t('subjectOverrideLabel')}</label>
              <Input value={broadcastSubjectOverride} onChange={e => setBroadcastSubjectOverride(e.target.value)}
                placeholder={selected?.subject || 'Leave blank to use template default'} className="tc-input" />
              <p className="text-muted text-xs mt-1">Optional. Leave blank to use the template's default subject.</p>
            </div>
            {selected?.variables?.filter(v => !AUTO_FILLED_VARS.has(v.name)).map(v => (
              <div key={v.name}>
                <label className="tc-label">{getVarLabel(v.name, v.description)}</label>
                {isHtmlVar(v.name) ? (
                  <div className="tc-quill-wrapper bg-white rounded">
                    <ReactQuill
                      theme="snow"
                      value={broadcastVars[v.name] || ''}
                      onChange={(html) => setBroadcastVars({...broadcastVars, [v.name]: html})}
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ height: 180, marginBottom: 40 }}
                      placeholder={getVarPlaceholder(v.name, v.description)}
                    />
                  </div>
                ) : (
                  <Input value={broadcastVars[v.name] || ''} onChange={e => setBroadcastVars({...broadcastVars, [v.name]: e.target.value})}
                    className="tc-input" placeholder={getVarPlaceholder(v.name, v.description)} data-testid={`broadcast-var-${v.name}`} />
                )}
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-3 border-t border-slate-700">
              <Button variant="outline" onClick={() => setShowBroadcast(false)} className="tc-button-secondary">{t('cancel')}</Button>
              <Button onClick={sendBroadcast} disabled={broadcastSending || (broadcastAudience !== 'custom' && broadcastCount.count === 0)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-broadcast-btn">
                {broadcastSending ? t('sending') : (
                  <><Send className="w-4 h-4 mr-1" /> {t('sendBroadcastBtn')} ({broadcastAudience === 'custom' ? '?' : broadcastCount.count})</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { if (!o) setShowCreate(false); }}>
        <DialogContent className="tc-dialog max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="tc-dialog-title flex items-center gap-2">
              <Plus className="w-5 h-5 text-tc-gold" /> {t('createTemplateTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="tc-label">{t('templateIdLabel')} *</label>
                <Input value={createForm.template_id}
                  onChange={e => setCreateForm({...createForm, template_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')})}
                  className="tc-input font-mono" placeholder="my_custom_email" />
              </div>
              <div>
                <label className="tc-label">{t('nameLabel')} *</label>
                <Input value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} className="tc-input" />
              </div>
            </div>
            <div>
              <label className="tc-label">{t('categoryLabel')}</label>
              <select value={createForm.category} onChange={e => setCreateForm({...createForm, category: e.target.value})} className="tc-select">
                <option value="custom">custom</option>
                <option value="marketing">marketing</option>
                <option value="transactional">transactional</option>
                <option value="system">system</option>
              </select>
            </div>
            <div>
              <label className="tc-label">{t('descriptionLabel')}</label>
              <Input value={createForm.description} onChange={e => setCreateForm({...createForm, description: e.target.value})} className="tc-input" />
            </div>
            <div>
              <label className="tc-label">{t('subjectLabel')} *</label>
              <Input value={createForm.subject} onChange={e => setCreateForm({...createForm, subject: e.target.value})} className="tc-input font-mono" />
            </div>
            <div>
              <label className="tc-label">{t('htmlBodyLabel')} * ({t('templateVarsHelper')})</label>
              <div className="tc-quill-wrapper bg-white rounded">
                <ReactQuill
                  theme="snow"
                  value={createForm.html_body}
                  onChange={(html) => setCreateForm({...createForm, html_body: html})}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ height: 280, marginBottom: 40 }}
                  placeholder='<h1>Hello {{first_name}}</h1><p>Your body here</p>'
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-slate-700">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="tc-button-secondary">{t('cancel')}</Button>
              <Button onClick={createTemplate} className="tc-button-primary">{t('createBtn')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Log Dialog */}
      <Dialog open={showLog} onOpenChange={(o) => { if (!o) setShowLog(false); }}>
        <DialogContent className="tc-dialog max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="tc-dialog-title flex items-center gap-2">
              <Eye className="w-5 h-5 text-tc-gold" /> {t('emailLogTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {emailLog.length === 0 ? (
              <p className="text-muted text-center py-6">{t('noEmailLogYet')}</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-800 text-left text-xs">
                  <tr><th className="p-2">Date</th><th className="p-2">Kind</th><th className="p-2">Template</th><th className="p-2">Audience/To</th><th className="p-2 text-right">Sent/Failed</th></tr>
                </thead>
                <tbody>
                  {emailLog.map(l => (
                    <tr key={l.log_id} className="border-t border-slate-700">
                      <td className="p-2 text-slate-300">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="p-2"><Badge className="bg-slate-600 text-white text-xs">{l.kind}</Badge></td>
                      <td className="p-2 text-slate-200 font-mono text-xs">{l.template_id || (l.subject_override ? `“${l.subject_override}”` : '—')}</td>
                      <td className="p-2 text-slate-300 text-xs">{l.audience || l.to_email}</td>
                      <td className="p-2 text-right"><span className="text-emerald-400">{l.sent_count ?? (l.result?.ok ? 1 : 0)}</span>/<span className="text-red-400">{l.failed_count ?? (l.result?.ok ? 0 : 1)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


const PricingSection = () => {
  const { t } = useLanguage();
  const [pricingTiers, setPricingTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateTier, setShowCreateTier] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [newTier, setNewTier] = useState({
    role: '',
    discount_percentage: '',
    display_name: ''
  });

  useEffect(() => {
    fetchPricingTiers();
  }, []);

  const fetchPricingTiers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/pricing`);
      setPricingTiers(response.data);
    } catch (error) {
      toast.error('Error al cargar niveles de precios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTier = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/api/pricing`, {
        ...newTier,
        discount_percentage: parseFloat(newTier.discount_percentage)
      });
      toast.success('Nivel de precios creado exitosamente');
      setShowCreateTier(false);
      setNewTier({ role: '', discount_percentage: '', display_name: '' });
      fetchPricingTiers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear nivel de precios');
    }
  };

  const handleUpdateTier = async (role, updatedData) => {
    try {
      await axios.put(`${API_BASE_URL}/api/pricing/${role}`, {
        discount_percentage: parseFloat(updatedData.discount_percentage),
        display_name: updatedData.display_name
      });
      toast.success('Nivel de precios actualizado exitosamente');
      setEditingTier(null);
      fetchPricingTiers();
    } catch (error) {
      toast.error('Error al actualizar nivel de precios');
    }
  };

  const deleteTier = async (role, displayName) => {
    if (window.confirm(`¿Está seguro que desea eliminar el nivel "${displayName}"? Los usuarios con este rol verán precios sin descuento.`)) {
      try {
        await axios.delete(`${API_BASE_URL}/api/pricing/${role}`);
        toast.success('Nivel de precios eliminado exitosamente');
        fetchPricingTiers();
      } catch (error) {
        toast.error('Error al eliminar nivel de precios');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="tc-spinner"></div>
        <span className="ml-4 text-primary">Cargando niveles de precios...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-primary mb-2">
            Gestión de Precios
          </h2>
          <p className="text-secondary">Configura descuentos por tipo de cuenta</p>
        </div>
        <Dialog open={showCreateTier} onOpenChange={setShowCreateTier}>
          <DialogTrigger asChild>
            <Button className="tc-button-primary hover-lift">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Nivel
            </Button>
          </DialogTrigger>
          <DialogContent className="tc-dialog-content">
            <div className="tc-dialog-header">
              <DialogTitle className="tc-dialog-title">Crear Nivel de Precios</DialogTitle>
              <DialogDescription className="tc-dialog-description">
                Define un nuevo tipo de cuenta con su descuento correspondiente
              </DialogDescription>
            </div>
            <div className="tc-dialog-body">
              <form onSubmit={handleCreateTier} className="space-y-6">
                <div>
                  <label className="tc-label">Código del Rol</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={newTier.role}
                    onChange={(e) => setNewTier({...newTier, role: e.target.value})}
                    placeholder="Ej: premium_dealer"
                    required
                  />
                </div>
                
                <div>
                  <label className="tc-label">Nombre para Mostrar</label>
                  <input
                    className="tc-input tc-focus-ring"
                    value={newTier.display_name}
                    onChange={(e) => setNewTier({...newTier, display_name: e.target.value})}
                    placeholder="Ej: Distribuidor Premium"
                    required
                  />
                </div>
                
                <div>
                  <label className="tc-label">Porcentaje de Descuento (%)</label>
                  <input
                    className="tc-input tc-focus-ring"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={newTier.discount_percentage}
                    onChange={(e) => setNewTier({...newTier, discount_percentage: e.target.value})}
                    placeholder="20.0"
                    required
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <Button 
                    type="button" 
                    onClick={() => setShowCreateTier(false)}
                    className="tc-button-secondary flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" className="tc-button-primary flex-1">
                    Crear Nivel
                  </Button>
                </div>
              </form>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="tc-card">
        <div className="tc-card-header">
          <h3 className="text-xl font-semibold text-primary mb-1">Niveles de Precios Configurados</h3>
          <p className="text-secondary text-sm">Descuentos aplicados automáticamente según el tipo de cuenta</p>
        </div>
        <CardContent className="tc-card-content p-0">
          <div className="tc-table">
            <table className="w-full">
              <thead className="tc-table-header">
                <tr>
                  <th className="tc-table-header th">Tipo de Cuenta</th>
                  <th className="tc-table-header th">Código</th>
                  <th className="tc-table-header th">Descuento</th>
                  <th className="tc-table-header th">Ejemplo (Precio $100)</th>
                  <th className="tc-table-header th">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pricingTiers.map((tier) => (
                  <tr key={tier.role} className="tc-table-row">
                    <td className="tc-table-cell">
                      <div>
                        <p className="text-primary font-medium text-sm">{tier.display_name}</p>
                        <p className="text-muted text-xs">Rol: {tier.role}</p>
                      </div>
                    </td>
                    <td className="tc-table-cell">
                      <Badge className="tc-badge tc-badge-info text-xs">
                        {tier.role}
                      </Badge>
                    </td>
                    <td className="tc-table-cell">
                      {editingTier?.role === tier.role ? (
                        <input
                          className="tc-input w-20 text-center"
                          type="number"
                          step="0.1"
                          value={editingTier.discount_percentage}
                          onChange={(e) => setEditingTier({...editingTier, discount_percentage: e.target.value})}
                        />
                      ) : (
                        <span className="text-2xl font-bold text-gold">{tier.discount_percentage}%</span>
                      )}
                    </td>
                    <td className="tc-table-cell">
                      <div className="text-center">
                        <span className="text-sm text-muted line-through">$100.00</span>
                        <p className="text-lg font-bold text-green-400">
                          ${(100 * (1 - tier.discount_percentage / 100)).toFixed(2)}
                        </p>
                      </div>
                    </td>
                    <td className="tc-table-cell">
                      <div className="flex gap-2">
                        {editingTier?.role === tier.role ? (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleUpdateTier(tier.role, editingTier)}
                              className="tc-button-primary text-xs px-2 py-1"
                            >
                              Guardar
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => setEditingTier(null)}
                              className="tc-button-secondary text-xs px-2 py-1"
                            >
                              Cancelar
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              onClick={() => setEditingTier({...tier, discount_percentage: tier.discount_percentage.toString()})}
                              className="tc-button-secondary text-xs px-2 py-1"
                            >
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => deleteTier(tier.role, tier.display_name)}
                              className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white border-red-600"
                            >
                              Eliminar
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {pricingTiers.length === 0 && (
            <div className="text-center py-12">
              <DollarSign className="w-16 h-16 text-muted mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-primary mb-2">No hay niveles de precios configurados</h3>
              <p className="text-secondary mb-6">Crea niveles de precios para diferentes tipos de cuenta</p>
              <Button 
                onClick={() => setShowCreateTier(true)}
                className="tc-button-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear Primer Nivel
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// =============================================================================
// PRODUCTION BARCODE MANAGEMENT — factory CODE 128 barcode generation & export
// =============================================================================
const ProductionBarcodesSection = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', production_date: '', batch: '', product: '', factory: '', po_number: '' });

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ production_date: '', batch_number: '', quantity: '', product_type: '', factory: '', po_number: '', production_reference: '', notes: '' });

  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [barcodeSearch, setBarcodeSearch] = useState('');

  const [lookupValue, setLookupValue] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');

  const token = localStorage.getItem('token');

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/barcodes/stats`);
      setStats(res.data);
    } catch (e) { /* noop */ }
  };

  const loadBatches = async (overrideFilters) => {
    const f = overrideFilters || filters;
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API_BASE_URL}/api/admin/barcodes/batches`, { params });
      setBatches(res.data.batches || []);
    } catch (e) {
      toast.error('Failed to load production batches');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadStats(); loadBatches(); /* eslint-disable-next-line */ }, []);

  const handleCreate = async () => {
    if (!form.production_date || !form.batch_number || !form.quantity) {
      toast.error('Production Date, Batch Number and Quantity are required');
      return;
    }
    const batchNum = parseInt(form.batch_number, 10);
    const qty = parseInt(form.quantity, 10);
    if (isNaN(batchNum) || batchNum < 1 || batchNum > 99) { toast.error('Batch number must be between 1 and 99'); return; }
    if (isNaN(qty) || qty < 1 || qty > 99999) { toast.error('Quantity must be between 1 and 99,999'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/admin/barcodes/batches`, {
        production_date: form.production_date,
        batch_number: batchNum,
        quantity: qty,
        product_type: form.product_type || null,
        factory: form.factory || null,
        po_number: form.po_number || null,
        production_reference: form.production_reference || null,
        notes: form.notes || null,
      });
      toast.success(res.data.message);
      setShowCreate(false);
      setForm({ production_date: '', batch_number: '', quantity: '', product_type: '', factory: '', po_number: '', production_reference: '', notes: '' });
      loadStats(); loadBatches();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create batch');
    } finally { setCreating(false); }
  };

  const openBatch = async (batch, search = '') => {
    setSelectedBatch(batch);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/barcodes/batches/${batch.batch_id}`, { params: { search: search || undefined, limit: 100 } });
      setBatchDetail(res.data);
    } catch (e) {
      toast.error('Failed to load batch details');
    } finally { setDetailLoading(false); }
  };

  const handleLookup = async () => {
    const val = lookupValue.trim();
    if (!val) return;
    setLookupError(''); setLookupResult(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/barcodes/lookup`, { params: { barcode_id: val } });
      setLookupResult(res.data);
    } catch (e) {
      setLookupError(e.response?.data?.detail || 'Barcode not found');
    }
  };

  const downloadExport = (batchId, kind) => {
    const url = `${API_BASE_URL}/api/admin/barcodes/batches/${batchId}/export/${kind}?token=${encodeURIComponent(token)}`;
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 1000);
  };

  const fmtDate = (iso) => { try { return new Date(iso + 'T00:00:00').toLocaleDateString(); } catch { return iso; } };
  const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  // ---------------- BATCH DETAIL VIEW ----------------
  if (selectedBatch) {
    const b = batchDetail?.batch || selectedBatch;
    return (
      <div className="space-y-6" data-testid="barcode-batch-detail">
        <Button variant="outline" className="tc-button-secondary" onClick={() => { setSelectedBatch(null); setBatchDetail(null); setBarcodeSearch(''); }} data-testid="barcode-back-btn">
          ← Back to Batch History
        </Button>

        <Card className="tc-card">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-primary flex items-center gap-2">
                  <Boxes className="w-5 h-5 text-tc-gold" /> Batch {b.batch_code} — {b.barcode_prefix}
                </CardTitle>
                <p className="text-muted text-sm mt-1">{fmtDate(b.production_date)} · {b.quantity} barcodes · {b.status}</p>
              </div>
              <div className="flex gap-2">
                <Button className="tc-button-primary" onClick={() => downloadExport(b.batch_id, 'factory')} data-testid="export-factory-btn">
                  <Download className="w-4 h-4 mr-2" /> Export Factory Excel
                </Button>
                <Button variant="outline" className="tc-button-secondary" onClick={() => downloadExport(b.batch_id, 'ids')} data-testid="export-ids-btn">
                  <Download className="w-4 h-4 mr-2" /> Export Barcode IDs
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><span className="text-muted block text-xs">Product</span><span className="text-primary">{b.product_type || '—'}</span></div>
              <div><span className="text-muted block text-xs">Factory</span><span className="text-primary">{b.factory || '—'}</span></div>
              <div><span className="text-muted block text-xs">PO Number</span><span className="text-primary">{b.po_number || '—'}</span></div>
              <div><span className="text-muted block text-xs">Reference</span><span className="text-primary">{b.production_reference || '—'}</span></div>
              <div><span className="text-muted block text-xs">Created By</span><span className="text-primary">{b.created_by}</span></div>
              <div><span className="text-muted block text-xs">Created</span><span className="text-primary">{fmtDateTime(b.created_at)}</span></div>
              {b.notes && <div className="col-span-2"><span className="text-muted block text-xs">Notes</span><span className="text-primary">{b.notes}</span></div>}
            </div>
          </CardContent>
        </Card>

        <Card className="tc-card">
          <CardHeader>
            <CardTitle className="text-primary text-base">Individual Barcodes {batchDetail?.pagination ? `(${batchDetail.pagination.total})` : ''}</CardTitle>
            <div className="flex gap-2 mt-2">
              <Input className="tc-input tc-focus-ring" placeholder="Search a barcode within this batch..." value={barcodeSearch}
                onChange={(e) => setBarcodeSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && openBatch(selectedBatch, barcodeSearch)}
                data-testid="in-batch-search-input" />
              <Button className="tc-button-primary" onClick={() => openBatch(selectedBatch, barcodeSearch)} data-testid="in-batch-search-btn">Search</Button>
            </div>
          </CardHeader>
          <CardContent>
            {detailLoading ? <p className="text-muted">Loading...</p> : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(batchDetail?.barcodes || []).map((bc) => (
                  <div key={bc.barcode_id} className="border border-border rounded-lg p-3 bg-black/20 flex flex-col items-center" data-testid={`barcode-item-${bc.barcode_id}`}>
                    <img src={bc.barcode_image} alt={bc.barcode_id} className="bg-white rounded p-1 max-w-full" style={{ height: 70 }} />
                    <span className={`text-xs mt-2 ${bc.status === 'VOID' ? 'line-through text-red-400' : 'text-primary'}`}>{bc.status}</span>
                  </div>
                ))}
                {batchDetail?.pagination?.has_more && (
                  <p className="text-muted text-sm col-span-full">Showing first {batchDetail.pagination.limit}. Use search to find a specific barcode.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------------- MAIN VIEW ----------------
  return (
    <div className="space-y-6" data-testid="production-barcodes-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-primary flex items-center gap-2"><Barcode className="w-6 h-6 text-tc-gold" /> Production Barcodes</h2>
          <p className="text-muted text-sm">Generate, store and export unique CODE 128 barcodes for factory rolls.</p>
        </div>
        <Button className="tc-button-primary" onClick={() => setShowCreate(true)} data-testid="create-batch-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Production Batch
        </Button>
      </div>

      {/* Dashboard cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="tc-card" data-testid="card-total-barcodes"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-muted text-xs uppercase"><Barcode className="w-4 h-4" /> Total Barcodes</div>
          <div className="text-3xl font-bold text-tc-gold mt-1">{stats?.total_barcodes ?? '—'}</div>
        </CardContent></Card>
        <Card className="tc-card" data-testid="card-total-batches"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-muted text-xs uppercase"><Boxes className="w-4 h-4" /> Production Batches</div>
          <div className="text-3xl font-bold text-primary mt-1">{stats?.total_batches ?? '—'}</div>
        </CardContent></Card>
        <Card className="tc-card" data-testid="card-month-barcodes"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-muted text-xs uppercase"><Calendar className="w-4 h-4" /> Generated This Month</div>
          <div className="text-3xl font-bold text-primary mt-1">{stats?.barcodes_this_month ?? '—'}</div>
        </CardContent></Card>
        <Card className="tc-card" data-testid="card-latest-batch"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-muted text-xs uppercase"><Factory className="w-4 h-4" /> Latest Batch</div>
          <div className="text-lg font-bold text-primary mt-1">{stats?.latest_batch ? `${stats.latest_batch.barcode_prefix}` : '—'}</div>
          {stats?.latest_batch && <div className="text-xs text-muted">{fmtDate(stats.latest_batch.production_date)}</div>}
        </CardContent></Card>
      </div>

      {/* Individual barcode lookup */}
      <Card className="tc-card">
        <CardHeader><CardTitle className="text-primary text-base flex items-center gap-2"><ScanLine className="w-5 h-5 text-tc-gold" /> Barcode Lookup</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input className="tc-input tc-focus-ring" placeholder="Type or scan a barcode ID (e.g. TC2608280300125)" value={lookupValue}
              onChange={(e) => setLookupValue(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
              data-testid="barcode-lookup-input" />
            <Button className="tc-button-primary" onClick={handleLookup} data-testid="barcode-lookup-btn">Lookup</Button>
          </div>
          {lookupError && <p className="text-red-400 text-sm mt-3" data-testid="lookup-error">{lookupError}</p>}
          {lookupResult && (
            <div className="mt-4 border border-border rounded-lg p-4 bg-black/20" data-testid="lookup-result">
              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <img src={lookupResult.barcode_image} alt={lookupResult.barcode_id} className="bg-white rounded p-2" style={{ height: 90 }} />
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm flex-1">
                  <div><span className="text-muted text-xs block">Barcode ID</span><span className="text-tc-gold font-mono">{lookupResult.barcode_id}</span></div>
                  <div><span className="text-muted text-xs block">Status</span><span className="text-primary">{lookupResult.status}</span></div>
                  <div><span className="text-muted text-xs block">Production Date</span><span className="text-primary">{fmtDate(lookupResult.production_date)}</span></div>
                  <div><span className="text-muted text-xs block">Batch</span><span className="text-primary">{lookupResult.batch_code}</span></div>
                  <div><span className="text-muted text-xs block">Roll</span><span className="text-primary">{lookupResult.serial_code}</span></div>
                  <div><span className="text-muted text-xs block">Product</span><span className="text-primary">{lookupResult.product_type || '—'}</span></div>
                  <div><span className="text-muted text-xs block">Factory</span><span className="text-primary">{lookupResult.factory || '—'}</span></div>
                  <div><span className="text-muted text-xs block">PO</span><span className="text-primary">{lookupResult.po_number || '—'}</span></div>
                  <div><span className="text-muted text-xs block">Created By</span><span className="text-primary">{lookupResult.created_by}</span></div>
                  <div><span className="text-muted text-xs block">Created</span><span className="text-primary">{fmtDateTime(lookupResult.created_at)}</span></div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Batch history */}
      <Card className="tc-card">
        <CardHeader>
          <CardTitle className="text-primary text-base">Production Batch History</CardTitle>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-3">
            <Input className="tc-input tc-focus-ring" placeholder="Search..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="filter-search" />
            <Input className="tc-input tc-focus-ring" type="date" value={filters.production_date} onChange={(e) => setFilters({ ...filters, production_date: e.target.value })} data-testid="filter-date" />
            <Input className="tc-input tc-focus-ring" placeholder="Batch #" value={filters.batch} onChange={(e) => setFilters({ ...filters, batch: e.target.value })} data-testid="filter-batch" />
            <Input className="tc-input tc-focus-ring" placeholder="Product" value={filters.product} onChange={(e) => setFilters({ ...filters, product: e.target.value })} data-testid="filter-product" />
            <Input className="tc-input tc-focus-ring" placeholder="Factory" value={filters.factory} onChange={(e) => setFilters({ ...filters, factory: e.target.value })} data-testid="filter-factory" />
            <Input className="tc-input tc-focus-ring" placeholder="PO Number" value={filters.po_number} onChange={(e) => setFilters({ ...filters, po_number: e.target.value })} data-testid="filter-po" />
          </div>
          <div className="flex gap-2 mt-2">
            <Button className="tc-button-primary" size="sm" onClick={loadBatches} data-testid="apply-filters-btn">Apply</Button>
            <Button variant="outline" className="tc-button-secondary" size="sm" onClick={() => { const cleared = { search: '', production_date: '', batch: '', product: '', factory: '', po_number: '' }; setFilters(cleared); loadBatches(cleared); }}>Clear</Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-muted">Loading...</p> : batches.length === 0 ? (
            <p className="text-muted text-center py-8" data-testid="no-batches">No production batches yet. Create your first batch to generate barcodes.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="batch-history-table">
                <thead>
                  <tr className="text-left text-muted border-b border-border">
                    <th className="p-2">Production Date</th><th className="p-2">Batch</th><th className="p-2">Product</th>
                    <th className="p-2">Factory</th><th className="p-2">PO</th><th className="p-2">Qty</th>
                    <th className="p-2">Created By</th><th className="p-2">Created</th><th className="p-2">Status</th><th className="p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.batch_id} className="border-b border-border/50 hover:bg-white/5" data-testid={`batch-row-${b.batch_code}`}>
                      <td className="p-2 text-primary">{fmtDate(b.production_date)}</td>
                      <td className="p-2 text-primary font-mono">{b.batch_code}</td>
                      <td className="p-2 text-primary">{b.product_type || '—'}</td>
                      <td className="p-2 text-primary">{b.factory || '—'}</td>
                      <td className="p-2 text-primary">{b.po_number || '—'}</td>
                      <td className="p-2 text-primary">{b.quantity}</td>
                      <td className="p-2 text-muted">{b.created_by}</td>
                      <td className="p-2 text-muted">{fmtDateTime(b.created_at)}</td>
                      <td className="p-2"><Badge className="tc-badge tc-badge-success">{b.status}</Badge></td>
                      <td className="p-2"><Button size="sm" variant="outline" className="tc-button-secondary" onClick={() => openBatch(b)} data-testid={`view-batch-${b.batch_code}`}>View</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Batch dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="tc-dialog max-w-lg" data-testid="create-batch-dialog">
          <DialogHeader>
            <DialogTitle className="text-primary">Create Production Batch</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div><label className="tc-label">Production Date *</label>
                <Input className="tc-input tc-focus-ring" type="date" value={form.production_date} onChange={(e) => setForm({ ...form, production_date: e.target.value })} data-testid="form-production-date" /></div>
              <div><label className="tc-label">Batch # *</label>
                <Input className="tc-input tc-focus-ring" type="number" min="1" max="99" placeholder="03" value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} data-testid="form-batch-number" /></div>
              <div><label className="tc-label">Quantity *</label>
                <Input className="tc-input tc-focus-ring" type="number" min="1" max="99999" placeholder="1000" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="form-quantity" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="tc-label">Product / Film Type</label>
                <Input className="tc-input tc-focus-ring" value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} data-testid="form-product" /></div>
              <div><label className="tc-label">Factory</label>
                <Input className="tc-input tc-focus-ring" value={form.factory} onChange={(e) => setForm({ ...form, factory: e.target.value })} data-testid="form-factory" /></div>
              <div><label className="tc-label">PO Number</label>
                <Input className="tc-input tc-focus-ring" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} data-testid="form-po" /></div>
              <div><label className="tc-label">Production Reference</label>
                <Input className="tc-input tc-focus-ring" value={form.production_reference} onChange={(e) => setForm({ ...form, production_reference: e.target.value })} data-testid="form-reference" /></div>
            </div>
            <div><label className="tc-label">Notes</label>
              <Input className="tc-input tc-focus-ring" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" /></div>

            {form.production_date && form.batch_number && form.quantity && (
              <div className="border border-tc-gold/40 rounded-lg p-3 bg-black/20 text-sm" data-testid="batch-preview">
                <span className="text-muted text-xs block mb-1">Preview — will generate {form.quantity} barcodes:</span>
                <span className="text-tc-gold font-mono">
                  TC{(() => { try { return new Date(form.production_date + 'T00:00:00').toISOString().slice(2,10).replace(/-/g,''); } catch { return 'YYMMDD'; } })()}{String(parseInt(form.batch_number||'0',10)).padStart(2,'0')}00001
                  {' … '}
                  {String(parseInt(form.quantity||'0',10)).padStart(5,'0')}
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="tc-button-secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="tc-button-primary" onClick={handleCreate} disabled={creating} data-testid="confirm-create-batch-btn">
              {creating ? 'Generating...' : 'Generate Barcodes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Protected Route Component

// =============================================================================
// PUBLIC VERIFY PAGE — customers check installation certificates with serial + name
// =============================================================================
const VerifyPage = () => {
  const [serial, setSerial] = useState('');
  const [clientName, setClientName] = useState('');
  const [loading, setLoading] = useState(false);
  const [certificate, setCertificate] = useState(null);
  const [possibleClients, setPossibleClients] = useState(null);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const location = useLocation();

  // Auto-fill and verify when arriving from a QR code link (?serial=...)
  useEffect(() => {
    const qsSerial = new URLSearchParams(location.search).get('serial');
    if (qsSerial && qsSerial.trim().length >= 3) {
      setSerial(qsSerial.trim());
      handleVerify(null, qsSerial.trim());
    }
  }, [location.search]);

  const handleVerify = async (e, serialOverride) => {
    e?.preventDefault();
    const serialToUse = (serialOverride ?? serial).trim();
    if (!serialToUse) return;
    setLoading(true); setError(''); setCertificate(null); setPossibleClients(null);
    try {
      const params = new URLSearchParams({ serial: serialToUse });
      if (clientName.trim()) params.append('client_name', clientName.trim());
      const res = await axios.get(`${API_BASE_URL}/api/verify?${params.toString()}`);
      if (res.data.single) {
        setCertificate(res.data.certificate);
      } else if (res.data.requires_disambiguation) {
        setPossibleClients(res.data.possible_clients || []);
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed. Please check the serial and try again.');
    } finally { setLoading(false); }
  };

  const downloadPdf = async () => {
    if (!certificate) return;
    setPdfLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/verify/${certificate.warranty_id}/certificate`);
      const b64 = res.data.certificate_pdf;
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TC-Certificate-${certificate.client_name.replace(/\s+/g,'_')}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Could not download certificate');
    } finally { setPdfLoading(false); }
  };

  const reset = () => {
    setSerial(''); setClientName(''); setCertificate(null); setPossibleClients(null); setError('');
  };

  const daysLeft = () => {
    if (!certificate?.warranty_end_date) return null;
    const end = new Date(certificate.warranty_end_date);
    const now = new Date();
    return Math.max(0, Math.floor((end - now) / 86400000));
  };
  const isExpired = () => {
    if (!certificate?.warranty_end_date) return false;
    return new Date(certificate.warranty_end_date) < new Date();
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
  const locationLabel = (loc) => ({
    'full_body': 'Full Body',
    'partial_front': 'Partial Front',
    'front_bumper': 'Front Bumper',
    'hood': 'Hood',
    'fenders': 'Fenders',
    'protection_kit': 'Protection Kit',
    'interior': 'Interior',
    'windshield': 'Windshield',
    'change_finish': 'Change of Finish',
    'full_wrap': 'Full Wrap',
  })[loc] || (loc || '—').replace(/_/g,' ');

  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(180deg,#0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)', color:'#fff', fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }} data-testid="verify-page">
      {/* Header */}
      <div style={{ borderBottom:'1px solid rgba(212,175,55,0.2)', padding:'20px 32px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <img src={`${API_BASE_URL}/api/assets/tc_logo.png`} alt="TC Protection Film" style={{ height:44 }} onError={(e)=>{e.currentTarget.style.display='none';}} />
          <div>
            <div style={{ color:'#D4AF37', fontWeight:800, letterSpacing:2, fontSize:15 }}>TC PROTECTION FILM</div>
            <div style={{ color:'#9a9a9a', fontSize:11, letterSpacing:2, textTransform:'uppercase' }}>Certificate Verification</div>
          </div>
        </div>
        <a href="/login" style={{ color:'#9a9a9a', textDecoration:'none', fontSize:12, letterSpacing:1 }}>PARTNER LOGIN →</a>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '48px 24px' }}>
        {!certificate && (
          <div>
            <div style={{ textAlign:'center', marginBottom:40 }}>
              <div style={{ color:'#D4AF37', fontSize:11, letterSpacing:4, textTransform:'uppercase', fontWeight:700, marginBottom:10 }}>Authentic Installation</div>
              <h1 style={{ fontSize:'clamp(32px,5vw,44px)', fontWeight:800, margin:0, letterSpacing:'-1px', lineHeight:1.15 }}>Verify Your TC Certificate</h1>
              <p style={{ color:'#9a9a9a', fontSize:16, marginTop:14, maxWidth:560, marginLeft:'auto', marginRight:'auto' }}>Enter the certificate number or serial number printed on your certificate to confirm your paint protection film installation and coverage.</p>
            </div>

            <form onSubmit={handleVerify} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(212,175,55,0.25)', backdropFilter:'blur(20px)', borderRadius:20, padding:32, maxWidth:560, margin:'0 auto' }}>
              <label style={{ display:'block', color:'#D4AF37', fontSize:11, letterSpacing:2, textTransform:'uppercase', fontWeight:700, marginBottom:8 }}>Certificate or Serial Number *</label>
              <input type="text" value={serial} onChange={(e)=>setSerial(e.target.value)} required minLength={3}
                data-testid="verify-serial-input"
                placeholder="Certificate No. or Serial — e.g. TC-PPF-1234567890"
                style={{ width:'100%', padding:'14px 16px', background:'#000', border:'1px solid #333', borderRadius:10, color:'#fff', fontSize:16, fontFamily:'inherit', outline:'none', letterSpacing:0.5, boxSizing:'border-box' }} />

              <label style={{ display:'block', color:'#D4AF37', fontSize:11, letterSpacing:2, textTransform:'uppercase', fontWeight:700, margin:'20px 0 8px' }}>Client Name (optional)</label>
              <input type="text" value={clientName} onChange={(e)=>setClientName(e.target.value)}
                data-testid="verify-client-input"
                placeholder="Required if the warranty number matches multiple installations"
                style={{ width:'100%', padding:'14px 16px', background:'#000', border:'1px solid #333', borderRadius:10, color:'#fff', fontSize:16, fontFamily:'inherit', outline:'none', boxSizing:'border-box' }} />

              <button type="submit" disabled={loading} data-testid="verify-submit-btn"
                style={{ marginTop:24, width:'100%', padding:'16px', background:'#D4AF37', color:'#0a0a0a', border:'none', borderRadius:10, fontSize:15, fontWeight:800, letterSpacing:2, textTransform:'uppercase', cursor:loading?'wait':'pointer', boxShadow:'0 10px 30px rgba(212,175,55,0.35)' }}>
                {loading ? 'Verifying...' : 'Verify Certificate  →'}
              </button>

              {error && !possibleClients && (
                <div data-testid="verify-error" style={{ marginTop:20, padding:'14px 16px', background:'rgba(220,38,38,0.1)', border:'1px solid rgba(220,38,38,0.35)', borderRadius:10, color:'#fca5a5', fontSize:14, textAlign:'center' }}>
                  ⚠ {error}
                </div>
              )}
              {possibleClients && possibleClients.length > 0 && (
                <div data-testid="verify-disambig" style={{ marginTop:20, padding:'14px 16px', background:'rgba(212,175,55,0.1)', border:'1px solid rgba(212,175,55,0.35)', borderRadius:10, color:'#fef3c7', fontSize:13 }}>
                  <div style={{ marginBottom:6 }}>{error}</div>
                  <div style={{ color:'#9a9a9a', fontSize:12, marginTop:8 }}>Possible clients on this serial:</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:6 }}>
                    {possibleClients.map(n => (
                      <button key={n} type="button" onClick={()=>setClientName(n)} style={{ background:'rgba(212,175,55,0.15)', border:'1px solid rgba(212,175,55,0.35)', color:'#fef3c7', padding:'4px 10px', borderRadius:100, fontSize:12, cursor:'pointer' }}>{n}</button>
                    ))}
                  </div>
                </div>
              )}
            </form>
          </div>
        )}

        {certificate && (
          <div data-testid="verify-certificate">
            {/* Certificate Card */}
            <div style={{ background:'#fff', color:'#111', borderRadius:24, overflow:'hidden', boxShadow:'0 30px 80px rgba(0,0,0,0.5)' }}>
              {/* Hero */}
              <div style={{ background:'linear-gradient(135deg,#0a0a0a 0%,#1a1a1a 50%,#0a0a0a 100%)', padding:'40px 40px 32px', textAlign:'center' }}>
                <img src={`${API_BASE_URL}/api/assets/tc_logo.png`} alt="TC" style={{ height:60, marginBottom:12 }} onError={(e)=>{e.currentTarget.style.display='none';}} />
                <div style={{ width:56, height:3, background:'#D4AF37', margin:'12px auto', borderRadius:2 }}></div>
                <div style={{ color:'#D4AF37', fontSize:11, letterSpacing:4, textTransform:'uppercase', fontWeight:700 }}>Certificate of Installation</div>
                <div style={{ color:'#e5e5e5', fontSize:20, fontWeight:700, marginTop:10 }}>Authenticity Verified ✓</div>
              </div>

              {/* Status Ribbon */}
              <div style={{ background: isExpired() ? '#fef2f2' : '#ecfdf5', borderBottom:'1px solid #e5e7eb', padding:'16px 40px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:10, height:10, borderRadius:'50%', background: isExpired()?'#dc2626':'#10b981' }}></div>
                  <span style={{ color: isExpired()?'#7f1d1d':'#065f46', fontWeight:700, fontSize:14 }}>
                    {isExpired() ? 'WARRANTY EXPIRED' : 'ACTIVE WARRANTY'}
                  </span>
                </div>
                {!isExpired() && (
                  <div style={{ color:'#065f46', fontSize:13 }}>
                    <strong>{daysLeft()}</strong> days remaining
                  </div>
                )}
              </div>

              {/* Body */}
              <div style={{ padding:'36px 40px' }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'24px 32px', marginBottom:32 }}>
                  <div>
                    <div style={{ color:'#9ca3af', fontSize:11, letterSpacing:2, textTransform:'uppercase', fontWeight:700 }}>Client</div>
                    <div style={{ color:'#0a0a0a', fontSize:20, fontWeight:800, marginTop:4 }}>{certificate.client_name}</div>
                  </div>
                  <div>
                    <div style={{ color:'#9ca3af', fontSize:11, letterSpacing:2, textTransform:'uppercase', fontWeight:700 }}>Installed By</div>
                    <div style={{ color:'#0a0a0a', fontSize:16, fontWeight:600, marginTop:4 }}>{certificate.user_company || 'TC Certified Installer'}</div>
                  </div>
                  <div>
                    <div style={{ color:'#9ca3af', fontSize:11, letterSpacing:2, textTransform:'uppercase', fontWeight:700 }}>Vehicle</div>
                    <div style={{ color:'#0a0a0a', fontSize:16, fontWeight:600, marginTop:4 }}>{certificate.vehicle_year} {certificate.vehicle_make} {certificate.vehicle_model}</div>
                  </div>
                  <div>
                    <div style={{ color:'#9ca3af', fontSize:11, letterSpacing:2, textTransform:'uppercase', fontWeight:700 }}>VIN</div>
                    <div style={{ color:'#0a0a0a', fontSize:14, fontFamily:'SF Mono, Menlo, monospace', marginTop:4 }}>{certificate.vin_number || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color:'#9ca3af', fontSize:11, letterSpacing:2, textTransform:'uppercase', fontWeight:700 }}>Coverage Area</div>
                    <div style={{ color:'#0a0a0a', fontSize:16, fontWeight:600, marginTop:4 }}>{locationLabel(certificate.installation_location)}</div>
                  </div>
                  <div>
                    <div style={{ color:'#9ca3af', fontSize:11, letterSpacing:2, textTransform:'uppercase', fontWeight:700 }}>Installation Date</div>
                    <div style={{ color:'#0a0a0a', fontSize:16, fontWeight:600, marginTop:4 }}>{fmt(certificate.installation_date)}</div>
                  </div>
                </div>

                <div style={{ background:'linear-gradient(135deg,#0a0a0a 0%,#1a1a1a 100%)', borderRadius:16, padding:24, color:'#fff', marginBottom:24 }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
                    <div>
                      <div style={{ color:'#9a9a9a', fontSize:10, letterSpacing:3, textTransform:'uppercase', fontWeight:700 }}>Warranty Number</div>
                      <div style={{ color:'#D4AF37', fontSize:16, fontFamily:'SF Mono, Menlo, monospace', marginTop:4, fontWeight:700, wordBreak:'break-all' }}>{certificate.serial_number}</div>
                    </div>
                    <div>
                      <div style={{ color:'#9a9a9a', fontSize:10, letterSpacing:3, textTransform:'uppercase', fontWeight:700 }}>Warranty ID</div>
                      <div style={{ color:'#e5e7eb', fontSize:13, fontFamily:'SF Mono, Menlo, monospace', marginTop:4, wordBreak:'break-all' }}>{certificate.warranty_id}</div>
                    </div>
                  </div>
                  <div style={{ height:1, background:'rgba(212,175,55,0.2)', margin:'20px 0' }}></div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
                    <div>
                      <div style={{ color:'#9a9a9a', fontSize:10, letterSpacing:3, textTransform:'uppercase', fontWeight:700 }}>Coverage Start</div>
                      <div style={{ color:'#e5e7eb', fontSize:14, marginTop:4 }}>{fmt(certificate.warranty_start_date)}</div>
                    </div>
                    <div>
                      <div style={{ color:'#9a9a9a', fontSize:10, letterSpacing:3, textTransform:'uppercase', fontWeight:700 }}>Coverage End</div>
                      <div style={{ color:'#e5e7eb', fontSize:14, marginTop:4 }}>{fmt(certificate.warranty_end_date)}</div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                  <button onClick={downloadPdf} disabled={pdfLoading} data-testid="verify-download-pdf"
                    style={{ flex:1, minWidth:200, padding:'14px 24px', background:'#D4AF37', color:'#0a0a0a', border:'none', borderRadius:100, fontSize:13, fontWeight:800, letterSpacing:2, textTransform:'uppercase', cursor:pdfLoading?'wait':'pointer', boxShadow:'0 10px 25px rgba(212,175,55,0.35)' }}>
                    {pdfLoading ? 'Preparing...' : 'Download PDF Certificate'}
                  </button>
                  <button onClick={reset} data-testid="verify-check-another"
                    style={{ padding:'14px 24px', background:'#fff', color:'#0a0a0a', border:'1px solid #d1d5db', borderRadius:100, fontSize:13, fontWeight:700, letterSpacing:1, textTransform:'uppercase', cursor:'pointer' }}>
                    Check Another
                  </button>
                </div>
              </div>
            </div>

            <p style={{ color:'#6b7280', fontSize:12, textAlign:'center', marginTop:24 }}>
              This certificate has been electronically verified against TC Protection Film records.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};


const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">{t("loading")}</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
};
// Main App Component
const App = () => {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify" element={<VerifyPage />} />
            <Route path="/activate" element={<ActivatePage />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <Toaster 
            position="top-right"
            expand={false}
            richColors
            closeButton
          />
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App;